import React, { useRef, useEffect, useState, useMemo, useCallback } from 'react';
import { Canvas, ThreeEvent, useThree } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import * as THREE from 'three';
import { App, Tooltip } from 'antd';
import { KeyOutlined } from '@ant-design/icons';
import { TEX_RESOLUTIONS, BRUSH_MIN, BRUSH_MAX } from '../paintingConstants';
import { viewportActions } from '../viewportActions';
import { useAppStore, Layer, LayerKeyframes, interpolateLayers, RenderSettings, DEFAULT_RENDER_SETTINGS, FRAME_OFFSET, ALL_PROPS } from '../store/useAppStore';
import { maybeAutoKeyBatch } from '../utils/autoKeyUtils';
import { buildLayerTransform, buildEffectiveLayerTransform, buildLayerEffectCache, isLayerEffectivelyVisible, isEffectivelyLocked, getEffectiveOpacity, getEffectiveTranslateY, catmullRomPoint, getSplinePoints, resampleStrokeSpine } from '../utils/viewportMathUtils';
import ProjectModal, { storeLastHandle, getLastHandle } from './ProjectModal';
import RenderModal from './RenderModal';
import AxisGizmo from './AxisGizmo';
import { useCameraControl } from '../hooks/useCameraControl';
import { useViewportKeyboard } from '../hooks/useViewportKeyboard';
import { useOpacityScrub } from '../hooks/useOpacityScrub';
import { useLayerHistory, LayerHistoryEntry, disposeLayerHistoryEntry } from '../hooks/useLayerHistory';
import { useProjectIO } from '../hooks/useProjectIO';
import { useAutosave } from '../hooks/useAutosave';
import { useFpsCounter } from '../hooks/useFpsCounter';
import { useScrollDrag } from '../hooks/useScrollDrag';
import { HERO_POSITIONS } from '../heroConstants';
import { brushVertexShader, brushFragmentShader } from '../shaders/brushShader';
import { copyVertexShader, copyFragmentShader } from '../shaders/copyShader';
import { compositeVertexShader, compositeFragmentShader } from '../shaders/compositeShader';
import { makeEnvelopeShaders } from '../shaders/envelopeShader';
import { stampVertexShader, stampFragmentShader } from '../shaders/stampShader';
import { blurVertexShader, blurFragmentShader } from '../shaders/blurShader';
import { projectBackHitToNdc, worldPointToUV, uvToWorldPoint, uvAlignedTangentFrame, camFrustumHalfH, getDuplicationCenters, wrapStampDiam } from '../utils/viewportRenderUtils';
import { convertWebMToMP4 } from '../utils/ffmpegUtils';
import { floodFill, buildFillMask, flipBufferY, floodFillGradient, GradientStop } from '../utils/floodFill';
import { useStampPipeline } from '../hooks/useStampPipeline';
export type { HeroPosition } from '../heroConstants';
export { HERO_POSITIONS } from '../heroConstants';

// ---------------------------------------------------------------------------
// Global texture resolution.
// Change these two values to resize ALL render targets and canvases at once.
// Width should be 2× height to match a 2:1 equirectangular sphere UV layout.
// ---------------------------------------------------------------------------
export const TEXTURE_WIDTH = 2048;
export const TEXTURE_HEIGHT = 1024;

/** Reusable Matrix3 scratch object — avoids a ~48-byte allocation per visible layer per flatten. */
const _scratchMatrix3 = new THREE.Matrix3();
/** Permanent identity Matrix3 for uniforms that are never rotated. */
const _identityMatrix3 = new THREE.Matrix3();
/** Scratch objects for paintSphereBrush projection-mode dup copies — eliminates per-dot allocations. */
const _scratchM4a = new THREE.Matrix4();
const _scratchM4b = new THREE.Matrix4();
const _scratchQ   = new THREE.Quaternion();
// CPU-side counterpart of the GLSL applyInverseWarp function.
// Applies the same sphere-locked radial warp used by the brush/envelope shaders.
function cpuInverseWarp(ndcXY: THREE.Vector2, scNDC: THREE.Vector2, k: number, str: number): THREE.Vector2 {
  const sv = ndcXY.clone().sub(scNDC);
  const r = sv.length();
  const sd = r > 1e-6 ? sv.clone().divideScalar(r) : new THREE.Vector2(1, 0);
  const rc = Math.min(r, 1.0);
  const inRw = 1 - Math.pow(Math.max(1 - rc, 0), k);
  const outRw = Math.pow(rc, k);
  const em = THREE.MathUtils.smoothstep(rc, 0.2, 0.95);
  const targetR = inRw + (outRw - inRw) * em;
  const wr = Math.max(0, Math.min(1.5, rc + (targetR - rc) * str));
  return scNDC.clone().addScaledVector(sd, wr);
}

// Computes NDC positions for a pair of envelope spine points in inverse projection mode.
// Traces each point's camera ray to the back-sphere exit, projects to NDC, then warps.
function computeInverseSegmentNdc(
  pAN: THREE.Vector3, pBN: THREE.Vector3,
  cam: THREE.Camera,
  scNDC: THREE.Vector2, wK: number, wStr: number,
): { ndcA: THREE.Vector3; ndcB: THREE.Vector3 } {
  const D2 = cam.position.dot(cam.position);
  const rayA = pAN.clone().sub(cam.position);
  const tA = (D2 - 1.0) / rayA.dot(rayA);
  const rawA = cam.position.clone().addScaledVector(rayA, tA).project(cam);
  const warpedA = cpuInverseWarp(new THREE.Vector2(rawA.x, rawA.y), scNDC, wK, wStr);
  const ndcA = new THREE.Vector3(warpedA.x, warpedA.y, rawA.z);
  const rayB = pBN.clone().sub(cam.position);
  const tB = (D2 - 1.0) / rayB.dot(rayB);
  const rawB = cam.position.clone().addScaledVector(rayB, tB).project(cam);
  const warpedB = cpuInverseWarp(new THREE.Vector2(rawB.x, rawB.y), scNDC, wK, wStr);
  const ndcB = new THREE.Vector3(warpedB.x, warpedB.y, rawB.z);
  return { ndcA, ndcB };
}

// ---------------------------------------------------------------------------
// Captures the R3F invalidate() function into a ref so imperative code outside
// the Canvas (brush rendering, layer flattening, camera animation) can trigger
// a scene redraw when running in frameloop="demand" mode.
// Must live inside <Canvas> to access useThree.
// ---------------------------------------------------------------------------
const InvalidateCapture: React.FC<{ target: React.MutableRefObject<() => void> }> = ({ target }) => {
  const { invalidate } = useThree();
  // Assign on every render — invalidate is a stable reference from R3F so this
  // is always a no-op after the first mount, but ensures correct wiring if the
  // Canvas ever re-mounts.
  target.current = invalidate;
  return null;
};

// ---------------------------------------------------------------------------
// Inner Canvas component — switches the R3F camera between perspective and ortho
// without unmounting the Canvas. Must live inside <Canvas> to access useThree.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// TabletOrbitSync — keeps OrbitControls camera behaviour in sync with tablet mode
// ---------------------------------------------------------------------------
const TabletOrbitSync: React.FC<{
  controlsRef: React.RefObject<any>;
}> = ({ controlsRef }) => {
  const tabletMode = useAppStore((s) => s.tabletMode);
  const tabletCameraMode = useAppStore((s) => s.tabletCameraMode);
  useEffect(() => {
    const c = controlsRef.current;
    if (!c) return;
    if (!tabletMode) {
      c.enableRotate = false;
      c.mouseButtons = { LEFT: THREE.MOUSE.ROTATE, MIDDLE: THREE.MOUSE.DOLLY, RIGHT: THREE.MOUSE.PAN };
      return;
    }
    if (tabletCameraMode === 'zoom') {
      c.enableRotate = false;
      c.mouseButtons = { LEFT: THREE.MOUSE.DOLLY, MIDDLE: THREE.MOUSE.DOLLY, RIGHT: THREE.MOUSE.PAN };
    } else if (tabletCameraMode === 'pan') {
      c.enableRotate = false;
      c.mouseButtons = { LEFT: THREE.MOUSE.PAN, MIDDLE: THREE.MOUSE.DOLLY, RIGHT: THREE.MOUSE.PAN };
    } else {
      c.enableRotate = true;
      c.mouseButtons = { LEFT: THREE.MOUSE.ROTATE, MIDDLE: THREE.MOUSE.DOLLY, RIGHT: THREE.MOUSE.PAN };
    }
  }, [tabletMode, tabletCameraMode, controlsRef]);
  return null;
};

const OrthoCamera: React.FC<{
  enabled: boolean;
  cameraRef: React.MutableRefObject<THREE.Camera | null>;
}> = ({ enabled, cameraRef }) => {
  const { camera, set, size } = useThree();
  const savedPerspRef = useRef<THREE.PerspectiveCamera | null>(null);
  const savedOrthoRef = useRef<THREE.OrthographicCamera | null>(null);

  useEffect(() => {
    if (enabled) {
      const pc = camera as THREE.PerspectiveCamera;
      savedPerspRef.current = pc;
      const aspect = size.width / size.height;
      const dist = pc.position.length();
      const worldHeight = 2 * Math.tan(THREE.MathUtils.degToRad(pc.fov) / 2) * dist;
      const oc = new THREE.OrthographicCamera(
        -(worldHeight * aspect) / 2,
        (worldHeight * aspect) / 2,
        worldHeight / 2,
        -(worldHeight / 2),
        0.01,
        2000,
      );
      oc.position.copy(pc.position);
      oc.quaternion.copy(pc.quaternion);
      oc.updateProjectionMatrix();
      savedOrthoRef.current = oc;
      cameraRef.current = oc;
      set({ camera: oc });
    } else {
      const pc = savedPerspRef.current;
      if (pc) {
        const oc = savedOrthoRef.current;
        if (oc) {
          pc.position.copy(oc.position);
          pc.quaternion.copy(oc.quaternion);
          pc.updateProjectionMatrix();
        }
        cameraRef.current = pc;
        set({ camera: pc });
        savedPerspRef.current = null;
        savedOrthoRef.current = null;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  return null;
};

// ── Architecture cap ─ black cap over the bottom ~1/3 of the sphere ──────────────────────────
// Uses a FULL sphere + ShaderMaterial that discards y > threshold.
// Partial sphere geometry had unreliable face-winding: from outside the sphere the outer normals
// on the bottom cap point away from the camera, making the cap see-through from the outer face.
// A full sphere + discard sidesteps all winding / DoubleSide ordering issues entirely.
const _ARCH_VERT = /* glsl */`
  varying float vY;
  void main() {
    vY = position.y;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;
const _ARCH_FRAG = /* glsl */`
  varying float vY;
  uniform float uCapTop;
  void main() {
    if (vY > uCapTop) discard;
    gl_FragColor = vec4(0.0, 0.0, 0.0, 1.0);
  }
`;

const ArchitectureCap: React.FC = React.memo(() => {
  const mesh = useMemo(() => {
    const geo = new THREE.SphereGeometry(1.003, 64, 32);
    const mat = new THREE.ShaderMaterial({
      vertexShader:   _ARCH_VERT,
      fragmentShader: _ARCH_FRAG,
      uniforms: { uCapTop: { value: -0.5 } },  // bottom of sphere is y=-1; cap top at y=-0.5
      // transparent:true is critical: Three.js renders opaque objects first, transparent second.
      // Without it the cap lands in the opaque pass and the transparent sphere meshes (renderOrder
      // 1 & 2) overwrite it. With transparent:true, renderOrder=4 correctly places it last.
      // depthTest:true — the cap sphere is at R=1.003, always fractionally closer to an outside
      // camera than the painted sphere at R=1.0, so it passes the depth test when facing the
      // camera, and is correctly occluded by the sphere body when the camera rotates away.
      transparent: true,
      depthTest:   true,
      depthWrite:  false,
      side: THREE.DoubleSide,
    });
    const m = new THREE.Mesh(geo, mat);
    m.renderOrder = 4;
    return m;
  }, []);
  useEffect(() => () => { mesh.geometry.dispose(); (mesh.material as THREE.Material).dispose(); }, [mesh]);
  return <primitive object={mesh} />;
});

// ── Safety reticles ─ three rounded-rectangle tubes per sector, on the sphere surface ─────────
// LineBasicMaterial.linewidth > 1 is ignored by WebGL; TubeGeometry + MeshBasicMaterial gives
// true thickness.
//
// Three rounded rectangles per sector:
//   blue  = same footprint as the old blue circle (square-ish)
//   red   = same width as the old red circle, height just a bit taller than blue
//   green = midpoint between blue and red
// All sector centres are tilted 15° up toward +Y before the shapes are built.

const SAFETY_RADIUS    = 1.0;                              // sits above stroke sphere (1.001)
const SAFETY_TUBE_R    = 0.004;                              // tube cross-section radius (thickness)
const SAFETY_TUBE_SEGS = 120;                                // longitudinal smoothness
const _SAFETY_BASE_R   = Math.PI / 6;                       // 30° reference
const _SAFETY_BLUE_R   = _SAFETY_BASE_R * 0.75;              // 22.5° — base half-extent
const _SAFETY_RED_R    = _SAFETY_BASE_R * 1.75;               // 52.5° — largest width
// Rounded-rectangle half-extents (angular, radians) and shared corner radius
const _RR_BLUE_W  = _SAFETY_BLUE_R;                              // half-width  (same as old blue circle)
const _RR_BLUE_H  = _SAFETY_BLUE_R;                              // half-height (same as old blue circle)
const _RR_RED_W   = _SAFETY_RED_R;                               // half-width  (same as old red circle)
const _RR_RED_H   = _SAFETY_BLUE_R * 1.1;                        // half-height (just a bit taller than blue)
const _RR_GREEN_W = (_RR_BLUE_W + _RR_RED_W) / 2;               // half-width  (midpoint)
const _RR_GREEN_H = (_RR_BLUE_H + _RR_RED_H) / 2;               // half-height (midpoint)
const _RR_CORNER  = _SAFETY_BLUE_R * 0.4;                        // shared corner radius

// Lift a sector centre to latitude +10° while keeping its azimuth direction.
// Rotating around the world X axis would only work correctly for the ±Z sectors;
// for other azimuths it pushes centres sideways/down. Instead we decompose into
// (azimuthal XZ direction) + fixed elevation sin(10°):
//   result = cos(10°)·azimuthDir + sin(10°)·Y
const _SAFETY_ELEV     = THREE.MathUtils.degToRad(15);
const _SAFETY_ELEV_COS = Math.cos(_SAFETY_ELEV);   // ~0.985
const _SAFETY_ELEV_SIN = Math.sin(_SAFETY_ELEV);   // ~0.174
function _tiltUp(cx: number, cy: number, cz: number): THREE.Vector3 {
  // Project out the Y component to get the pure azimuth direction, then re-elevate.
  const azimuth = new THREE.Vector3(cx, 0, cz).normalize();
  return new THREE.Vector3(
    _SAFETY_ELEV_COS * azimuth.x,
    _SAFETY_ELEV_SIN,
    _SAFETY_ELEV_COS * azimuth.z,
  );
}

// Parametric Curve tracing a rounded rectangle on the unit sphere surface.
// halfW / halfH / cornerR are all angular extents in radians.
// The rectangle lies in the tangent plane at `center`; points are projected
// back to the sphere via the exponential map.
class _SphereRRectCurve extends THREE.Curve<THREE.Vector3> {
  private _cen: THREE.Vector3;
  private _u:   THREE.Vector3;  // horizontal tangent
  private _v:   THREE.Vector3;  // vertical   tangent
  private _w:   number;         // half-width
  private _h:   number;         // half-height
  private _r:   number;         // corner radius
  constructor(center: THREE.Vector3, halfW: number, halfH: number, cornerR: number) {
    super();
    this._cen = center.clone().normalize();
    // _v = world +Y projected onto the tangent plane → vertical axis of the rectangle
    const worldUp = new THREE.Vector3(0, 1, 0);
    this._v = worldUp.clone().addScaledVector(this._cen, -worldUp.dot(this._cen)).normalize();
    // _u = right-hand horizontal perpendicular (cross(v, cen) points screen-right)
    this._u = new THREE.Vector3().crossVectors(this._v, this._cen).normalize();
    this._w = halfW;
    this._h = halfH;
    this._r = cornerR;
  }
  getPoint(t: number): THREE.Vector3 {
    const W = this._w, H = this._h, R = this._r;
    const cw  = W - R;                         // corner centre x-offset
    const ch  = H - R;                         // corner centre y-offset
    const arc = (Math.PI / 2) * R;             // arc length of one 90° corner
    const sX  = 2 * cw;                        // straight horizontal side length
    const sY  = 2 * ch;                        // straight vertical   side length
    const total = 2 * sX + 2 * sY + 4 * arc;
    // Trace clockwise from top-left:
    //   top → TR corner → right → BR corner → bottom → BL corner → left → TL corner
    let rem = ((t % 1) * total + total) % total;
    let u = 0, v = 0;
    if (rem < sX) {
      u = -cw + (rem / sX) * 2 * cw; v = H;                              // top side
    } else if ((rem -= sX) < arc) {
      const a = Math.PI / 2 - (rem / arc) * Math.PI / 2;                 // TR corner
      u = cw + R * Math.cos(a); v = ch + R * Math.sin(a);
    } else if ((rem -= arc) < sY) {
      u = W; v = ch - (rem / sY) * 2 * ch;                               // right side
    } else if ((rem -= sY) < arc) {
      const a = -(rem / arc) * Math.PI / 2;                              // BR corner
      u = cw + R * Math.cos(a); v = -ch + R * Math.sin(a);
    } else if ((rem -= arc) < sX) {
      u = cw - (rem / sX) * 2 * cw; v = -H;                             // bottom side
    } else if ((rem -= sX) < arc) {
      const a = -Math.PI / 2 - (rem / arc) * Math.PI / 2;               // BL corner
      u = -cw + R * Math.cos(a); v = -ch + R * Math.sin(a);
    } else if ((rem -= arc) < sY) {
      u = -W; v = -ch + (rem / sY) * 2 * ch;                            // left side
    } else {
      rem -= sY;
      const a = Math.PI - (rem / arc) * Math.PI / 2;                    // TL corner
      u = -cw + R * Math.cos(a); v = ch + R * Math.sin(a);
    }
    // Exponential map: angular offset (u, v) → sphere point
    const r = Math.sqrt(u * u + v * v);
    if (r < 1e-10) return this._cen.clone().multiplyScalar(SAFETY_RADIUS);
    const sinr_r = Math.sin(r) / r;
    return new THREE.Vector3()
      .addScaledVector(this._cen, Math.cos(r))
      .addScaledVector(this._u, sinr_r * u)
      .addScaledVector(this._v, sinr_r * v)
      .normalize()
      .multiplyScalar(SAFETY_RADIUS);
  }
}

function _buildSafetyRRect(center: THREE.Vector3, halfW: number, halfH: number, color: string): THREE.Mesh {
  const geo  = new THREE.TubeGeometry(new _SphereRRectCurve(center, halfW, halfH, _RR_CORNER), SAFETY_TUBE_SEGS, SAFETY_TUBE_R, 6, true);
  const mat  = new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.9, depthTest: true });
  const mesh = new THREE.Mesh(geo, mat);
  mesh.renderOrder = 5;
  return mesh;
}

function _getSafetyCenters(dupMode: string): THREE.Vector3[] {
  // Azimuth offset so the hero reticle faces the XOLiveStream camera at (3.747, 0.045, 3.709).
  // atan2(x, z) = atan2(3.747, 3.709) ≈ 45.3°.  Adjust SAFETY_AZIMUTH_DEG to fine-tune.
  const SAFETY_AZIMUTH_DEG = 45.3;
  const az = THREE.MathUtils.degToRad(SAFETY_AZIMUTH_DEG);
  const cosAz = Math.cos(az), sinAz = Math.sin(az);
  // Rotate each raw centre around Y by the azimuth offset before elevating.
  const rotY = (cx: number, cz: number): [number, number, number] => [
    cosAz * cx + sinAz * cz,
    0,
    -sinAz * cx + cosAz * cz,
  ];

  let raw: [number, number, number][];
  if (dupMode === 'diptych')
    raw = [[0, 0, 1], [0, 0, -1]].map(([cx, cy, cz]) => rotY(cx, cz));
  else if (dupMode === 'triptych')
    raw = [0, 120, 240].map((d) => { const a = THREE.MathUtils.degToRad(d); return rotY(Math.sin(a), Math.cos(a)); });
  else if (dupMode === 'quadriptych')
    raw = [0, 90, 180, 270].map((d) => { const a = THREE.MathUtils.degToRad(d); return rotY(Math.sin(a), Math.cos(a)); });
  else
    raw = [rotY(0, 1)]; // hero
  return raw.map(([cx, cy, cz]) => _tiltUp(cx, cy, cz));
}

const SafetyReticles: React.FC<{ dupMode: 'hero' | 'diptych' | 'triptych' | 'quadriptych' }> = React.memo(({ dupMode }) => {
  const meshes = useMemo(() => {
    const rings: THREE.Mesh[] = [];
    for (const center of _getSafetyCenters(dupMode)) {
      rings.push(_buildSafetyRRect(center, _RR_BLUE_W,  _RR_BLUE_H,  '#4488ff')); // smallest — blue
      rings.push(_buildSafetyRRect(center, _RR_GREEN_W, _RR_GREEN_H, '#44cc44')); // middle   — green
      rings.push(_buildSafetyRRect(center, _RR_RED_W,   _RR_RED_H,   '#ff4444')); // largest  — red
    }
    return rings;
  }, [dupMode]);
  useEffect(
    () => () => { meshes.forEach((m) => { m.geometry.dispose(); (m.material as THREE.Material).dispose(); }); },
    [meshes],
  );
  return <>{meshes.map((m, i) => <primitive key={i} object={m} />)}</>;
});

// ---------------------------------------------------------------------------
// TabletCameraButtons — horizontal row of camera-mode buttons, rendered below
// the camera info panel (position: absolute, right-aligned with the panel).
// Controls what dragging OUTSIDE the sphere does.
// ---------------------------------------------------------------------------
const TABLET_BTN: React.CSSProperties = {
  width: 36, height: 36, borderRadius: 6, border: 'none', cursor: 'pointer',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  fontSize: 18, background: 'transparent', color: '#888',
};

const TabletCameraButtons: React.FC = () => {
  const tabletMode = useAppStore((s) => s.tabletMode);
  const mode = useAppStore((s) => s.tabletCameraMode);
  const setMode = useAppStore((s) => s.setTabletCameraMode);
  if (!tabletMode) return null;
  return (
    <div style={{
      display: 'flex', flexDirection: 'row', gap: 2, marginTop: 6,
      borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: 5,
    }}>
      {([
        { m: 'orbit' as const, label: '⤾', tip: 'Orbit (drag background)' },
        { m: 'zoom'  as const, label: '⊕', tip: 'Zoom (drag background)' },
        { m: 'pan'   as const, label: '✛', tip: 'Pan (drag background)' },
      ]).map(({ m, label, tip }) => (
        <Tooltip key={m} title={tip} placement="bottom">
          <button onClick={() => setMode(m)} style={{
            ...TABLET_BTN,
            flex: 1,
            background: mode === m ? 'rgba(88,166,255,0.2)' : 'rgba(255,255,255,0.06)',
            color: mode === m ? '#58a6ff' : 'rgba(255,255,255,0.4)',
            outline: mode === m ? '1px solid #58a6ff' : 'none',
            borderRadius: 5,
          }}>{label}</button>
        </Tooltip>
      ))}
    </div>
  );
};

// ---------------------------------------------------------------------------
// TabletSphereButtons — horizontal row of sphere-interaction buttons, floating
// bottom-right. Controls what dragging ON the sphere does.
// ---------------------------------------------------------------------------
const TabletSphereButtons: React.FC = () => {
  const tabletMode = useAppStore((s) => s.tabletMode);
  const mode = useAppStore((s) => s.tabletSphereMode);
  const setMode = useAppStore((s) => s.setTabletSphereMode);
  if (!tabletMode) return null;
  return (
    <div style={{
      position: 'absolute', right: 12, bottom: 36,
      display: 'flex', flexDirection: 'row', gap: 2,
      background: 'rgba(0,0,0,0.6)', borderRadius: 8,
      padding: '3px 6px',
      userSelect: 'none',
    }}>
      {([
        { m: 'draw'           as const, label: '✏', tip: 'Use selected tool on sphere' },
        { m: 'rotateLayer'    as const, label: '↻', tip: 'Rotate layer (drag on sphere)' },
        { m: 'translateLayer' as const, label: '✥', tip: 'Translate layer (drag on sphere)' },
      ]).map(({ m, label, tip }) => (
        <Tooltip key={m} title={tip} placement="top">
          <button onClick={() => setMode(m)} style={{
            ...TABLET_BTN,
            background: mode === m ? 'rgba(88,166,255,0.25)' : 'transparent',
            color: mode === m ? '#58a6ff' : '#888',
            outline: mode === m ? '2px solid #58a6ff' : 'none',
          }}>{label}</button>
        </Tooltip>
      ))}
    </div>
  );
};

const Viewport3D: React.FC = () => {
  const { notification } = App.useApp();
  const sphereRef = useRef<THREE.Mesh>(null!);
  const materialRef = useRef<THREE.MeshBasicMaterial | null>(null);
  const cameraRef = useRef<THREE.Camera | null>(null);
  const glRef = useRef<any>(null);
  const controlsRef = useRef<any>(null);
  const strokeSphereRef = useRef<THREE.Mesh>(null!);
  const strokeMaterialRef = useRef<THREE.MeshBasicMaterial | null>(null);
  const raycaster = useRef(new THREE.Raycaster()).current;
  // Captured from R3F useThree() by InvalidateCapture; used to request scene
  // redraws in frameloop="demand" mode from outside the Canvas context.
  const invalidateRef = useRef<() => void>(() => {});

  const textureCanvas = useRef<HTMLCanvasElement | null>(null);
  const textureRef = useRef<THREE.CanvasTexture | null>(null);
  // Render target for compositing a single layer with its rotation/opacity for preview.
  const singleLayerDisplayRT = useRef<THREE.WebGLRenderTarget | null>(null);
  // A permanently-transparent 1×1 RT used as the base when compositing a single layer.
  const transparentBaseRT = useRef<THREE.WebGLRenderTarget | null>(null);
  const setTextureCanvas = useAppStore((s) => s.setTextureCanvas);
  const bumpTexture = useAppStore((s) => s.bumpTextureVersion);
  const setLayerThumbnail = useAppStore((s) => s.setLayerThumbnail);
  const setUvFullCanvas = useAppStore((s) => s.setUvFullCanvas);
  const setIsDrawingActive = useAppStore((s) => s.setIsDrawingActive);
  const layers = useAppStore((s) => s.layers);
  const selectedLayerId = useAppStore((s) => s.selectedLayerId);
  const updateLayer = useAppStore((s) => s.updateLayer);
  const removeLayer = useAppStore((s) => s.removeLayer);
  const selectLayer = useAppStore((s) => s.selectLayer);
  const pendingMergeDown = useAppStore((s) => s.pendingMergeDown);
  const clearMergeRequest = useAppStore((s) => s.clearMergeRequest);
  const stampImage = useAppStore((s) => s.stampImage);
  const autoKey        = useAppStore((s) => s.autoKey);
  const setKeyframe    = useAppStore((s) => s.setKeyframe);
  const currentFrame   = useAppStore((s) => s.currentFrame);
  const layerKeyframes = useAppStore((s) => s.layerKeyframes);


  // Stamp shader refs
  const stampMaterial = useRef<THREE.ShaderMaterial | null>(null);
  const stampScene = useRef<THREE.Scene | null>(null);
  const stampTexture = useRef<THREE.CanvasTexture | null>(null);
  const clipboardPasteTextureRef = useRef<THREE.CanvasTexture | null>(null);
  // True when the pending clipboard paste should be committed into a brand-new layer
  // rather than the currently selected one.
  const clipboardPasteNewLayerRef = useRef(false);

  // Blur shader refs
  const blurMaterial = useRef<THREE.ShaderMaterial | null>(null);
  const blurScene = useRef<THREE.Scene | null>(null);
  const blurTempRT = useRef<THREE.WebGLRenderTarget | null>(null);
  const blurRadiusRef = useRef(0.3);

  const renderTargetA = useRef<THREE.WebGLRenderTarget | null>(null);
  const renderTargetB = useRef<THREE.WebGLRenderTarget | null>(null);
  const strokeRenderTargetA = useRef<THREE.WebGLRenderTarget | null>(null);
  const strokeRenderTargetB = useRef<THREE.WebGLRenderTarget | null>(null);
  const activeStrokeRenderTarget = useRef<THREE.WebGLRenderTarget | null>(null);
  const lastStrokeRenderTarget = useRef<THREE.WebGLRenderTarget | null>(null);
  const activeRenderTarget = useRef<THREE.WebGLRenderTarget | null>(null);
  const quadScene = useRef<THREE.Scene | null>(null);
  const copyScene = useRef<THREE.Scene | null>(null);
  const orthoCamera = useRef<THREE.OrthographicCamera | null>(null);
  const brushMaterial = useRef<THREE.ShaderMaterial | null>(null);
  const compositeMaterial = useRef<THREE.ShaderMaterial | null>(null);
  const compositeScene = useRef<THREE.Scene | null>(null);
  const compositeMesh = useRef<THREE.Mesh | null>(null);
  const copyMaterial = useRef<THREE.ShaderMaterial | null>(null);
  const envelopeMaterialRef = useRef<THREE.ShaderMaterial | null>(null);
  const envelopeSceneRef = useRef<THREE.Scene | null>(null);
  const pressureRef = useRef(1.0);
  const usePressureRef = useRef(false);
  const simPressureRef = useRef(false);
  const simPressureRandomnessRef = useRef(0.2);
  const prevHitPoint = useRef<THREE.Vector3 | null>(null);
  // EMA-smoothed position of the last raw hit point — used to reduce input jitter.
  const smoothedHitPoint = useRef<THREE.Vector3 | null>(null);
  const smoothnessRef = useRef(0.5);
  // Last hit point while hovering in stamp/text mode — used to refresh the
  // ghost preview when tool settings change without moving the mouse.
  const lastStampHitPoint = useRef<THREE.Vector3 | null>(null);
  const savedFileHandleRef = useRef<any>(null);          // FileSystemFileHandle from last showSaveFilePicker
  const saveCurrentProjectRef = useRef<() => Promise<void>>(() => Promise.resolve());
  const openProjectRef = useRef<() => void | Promise<void>>(() => {});
  const brushRadius = useRef(0.08);
  const brushOpacityRef = useRef(1);
  const brushColorRef = useRef('#108ee9');
  const pickerValueRef = useRef<any>(null);
  const ctrlHeldRef = useRef(false);
  const toolModeRef = useRef<'draw' | 'fill' | 'erase' | 'stamp' | 'text' | 'blur'>('draw');
  const dupModeRef = useRef<'hero' | 'diptych' | 'triptych' | 'quadriptych'>('hero');
  // Per-layer GPU render-target pairs.  Key = layer id.
  const layerRTs = useRef<Map<string, { a: THREE.WebGLRenderTarget; b: THREE.WebGLRenderTarget; active: 'a' | 'b' }>>(new Map());
  const layersRef = useRef<Layer[]>([]);
  const selectedLayerIdRef = useRef<string | null>(null);
  // Small render target used for generating layer thumbnail images.
  const thumbnailRT = useRef<THREE.WebGLRenderTarget | null>(null);
  const thumbnailCanvas = useRef<HTMLCanvasElement | null>(null);

  // Unified undo/redo history — every snapshot captures the full world state
  // (layers, keyframes, camera keyframes, and per-layer GPU pixel data).
  const undoStack = useRef<LayerHistoryEntry[]>([]);
  const redoStack = useRef<LayerHistoryEntry[]>([]);
  const MAX_HISTORY = 20;
  // Called by useProjectIO to wipe history on load/new.
  const clearHistoryRef = useRef<() => void>(() => {});

  const pendingBump = useRef(false);
  const pendingBumpAfter = useRef<(() => void) | null>(null);
  /** Persistent pixel readback buffers — avoid ~8 MB allocations per stroke/save. */
  const readPixelBufferRef = useRef<Uint8Array | null>(null);
  /** Refs for flood-fill tool. */
  const fillFloodModeRef = useRef<'island' | 'noncontiguous' | 'global'>('island');
  const fillToleranceRef = useRef(15);
  const floodFillAtPointRef = useRef<(worldPoint: THREE.Vector3) => void>(() => {});
  /** Drag-start world point for fill-mode gradient drags. null when not dragging. */
  const fillDragStartRef = useRef<THREE.Vector3 | null>(null);
  /** Fill hover preview refs. */
  const lastFillHoverPointRef = useRef<THREE.Vector3 | null>(null);
  const fillPreviewPixelBufRef = useRef<Uint8Array | null>(null);
  const fillPreviewCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const fillPreviewTextureRef = useRef<THREE.CanvasTexture | null>(null);
  const fillPreviewMaterialRef = useRef<THREE.MeshBasicMaterial | null>(null);
  const fillPreviewThrottleRef = useRef(0);
  const fillPreviewUpdateRef = useRef<() => void>(() => {});
  /** Reusable ImageData objects for the full-res Y-flip — avoids an 8 MB alloc per stroke. */
  const fullImageDataRef = useRef<ImageData | null>(null);
  const strokeImageDataRef = useRef<ImageData | null>(null);
  /** Blob URLs currently assigned as layer thumbnails; tracked so they can be revoked. */
  const thumbnailBlobUrlsRef = useRef<Map<string, string>>(new Map());
  /** Web Worker that handles Y-flip + WebP encoding off the main thread. */
  const imageWorkerRef = useRef<Worker | null>(null);
  /** Pending worker response callbacks, keyed by request ID. */
  const workerCallbacksRef = useRef<Map<string, (msg: Record<string, any>) => void>>(new Map());

  // ── Background layer-texture cache ───────────────────────────────────────
  // Pre-encoded WebP data URLs keyed by layer id. Populated lazily by the idle
  // encoder loop so buildProjectJson (autosave) never needs GPU readbacks.
  const layerTextureCacheRef = useRef<Map<string, string>>(new Map());
  /** Layer ids whose RT has changed since the last idle-encode pass. */
  const layerCacheDirtyRef = useRef<Set<string>>(new Set());
  /** requestIdleCallback handle for the encoder loop, or null when idle. */
  const encoderIdleHandleRef = useRef<number | null>(null);
  /** Shared pixel buffer for the encoder loop — avoids per-layer allocations. */
  const encoderPixelBufRef = useRef<Uint8Array | null>(null);

  /** Live stroke canvas updated each rAF during UV drawing for UVPanel overlay. */
  const strokeLivePixelBufRef = useRef<Uint8Array | null>(null);
  const strokeLiveCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const strokeLiveRafPendingRef = useRef(false);
  const readRTPixelBufferRef = useRef<Uint8Array | null>(null);
  const scheduleBump = (after?: () => void) => {
    if (pendingBump.current) {
      // A bump rAF is already queued.  Chain any new callback onto the existing
      // one so no pushHistory call is ever silently dropped during rapid operations.
      if (after) {
        const existing = pendingBumpAfter.current;
        pendingBumpAfter.current = existing
          ? () => { existing(); after(); }
          : after;
      }
      return;
    }
    pendingBump.current = true;
    pendingBumpAfter.current = after ?? null;
    requestAnimationFrame(() => {
      bumpTexture();
      const cb = pendingBumpAfter.current;
      pendingBump.current = false;
      pendingBumpAfter.current = null;
      if (cb) cb();
    });
  };

  // ── Layer texture cache helpers ──────────────────────────────────────────

  /** Mark layers dirty and schedule the idle encoder if not already running. */
  const markLayersDirty = (...ids: string[]) => {
    for (const id of ids) {
      layerCacheDirtyRef.current.add(id);
      layerTextureCacheRef.current.delete(id);
    }
    scheduleEncoder();
  };

  /** Cancel the queued encoder rIC (called on pointer-down). */
  const cancelEncoder = () => {
    if (encoderIdleHandleRef.current !== null && typeof cancelIdleCallback === 'function') {
      cancelIdleCallback(encoderIdleHandleRef.current);
      encoderIdleHandleRef.current = null;
    }
  };

  const scheduleEncoder = () => {
    if (encoderIdleHandleRef.current !== null) return; // already queued
    if (layerCacheDirtyRef.current.size === 0) return;
    if (typeof requestIdleCallback !== 'function') {
      setTimeout(() => runEncoderStep(), 0);
      return;
    }
    encoderIdleHandleRef.current = requestIdleCallback(() => {
      encoderIdleHandleRef.current = null;
      runEncoderStep();
    }, { timeout: 5000 });
  };

  const runEncoderStep = () => {
    const gl = glRef.current;
    if (!gl) return;
    const dirty = layerCacheDirtyRef.current;
    if (dirty.size === 0) return;
    // Encode one layer per tick so we never block > ~16 ms.
    const layerId = dirty.values().next().value as string;
    dirty.delete(layerId);
    const lRTData = layerRTs.current.get(layerId);
    if (lRTData) {
      const activeRT = lRTData.active === 'a' ? lRTData.a : lRTData.b;
      const { width: w, height: h } = activeRT;
      const needed = w * h * 4;
      if (!encoderPixelBufRef.current || encoderPixelBufRef.current.length !== needed)
        encoderPixelBufRef.current = new Uint8Array(needed);
      try {
        gl.readRenderTargetPixels(activeRT, 0, 0, w, h, encoderPixelBufRef.current);
        const worker = imageWorkerRef.current;
        if (!worker) { dirty.add(layerId); scheduleEncoder(); return; }
        // Transfer a copy of the pixel data to the worker (zero-copy transfer;
        // the shared ref remains immediately usable for the next layer).
        const buf = new Uint8ClampedArray(encoderPixelBufRef.current).buffer;
        const id = crypto.randomUUID();
        workerCallbacksRef.current.set(id, (msg) => {
          if (msg.error) { dirty.add(layerId); scheduleEncoder(); return; }
          if (typeof msg.dataUrl === 'string')
            layerTextureCacheRef.current.set(layerId, msg.dataUrl);
        });
        worker.postMessage({ id, kind: 'encode', layerId, buffer: buf, width: w, height: h }, [buf]);
      } catch { dirty.add(layerId); } // GPU not ready; retry next cycle
    }
    if (dirty.size > 0) scheduleEncoder();
  };

  // ---------------------------------------------------------------------------
  // Layer render-target helpers
  // ---------------------------------------------------------------------------

  /** Creates ping-pong RT pairs for any layer that doesn't have them yet. */
  const ensureLayerRTs = (currentLayers: Layer[]) => {
    const gl = glRef.current;
    if (!gl) return;
    const makeLayerRT = () => {
      const rt = new THREE.WebGLRenderTarget(texResRef.current.w, texResRef.current.h, {
        type: THREE.UnsignedByteType,
        minFilter: THREE.LinearFilter,
        magFilter: THREE.LinearFilter,
        wrapS: THREE.RepeatWrapping,
        wrapT: THREE.RepeatWrapping,
      });
      rt.texture.colorSpace = THREE.SRGBColorSpace;
      return rt;
    };
    for (const layer of currentLayers) {
      if (!layerRTs.current.has(layer.id)) {
        const a = makeLayerRT();
        const b = makeLayerRT();
        gl.setRenderTarget(a);
        gl.clearColor(0, 0, 0, 0);
        gl.clear(gl.COLOR_BUFFER_BIT);
        gl.setRenderTarget(b);
        gl.clearColor(0, 0, 0, 0);
        gl.clear(gl.COLOR_BUFFER_BIT);
        gl.setRenderTarget(null);
        layerRTs.current.set(layer.id, { a, b, active: 'a' });
      }
    }
  };

  /**
   * Composites all visible layers (bottom → top) into renderTargetA/B using
   * the existing composite shader, then updates activeRenderTarget and the
   * main sphere material to show the result.
   */

  const flattenLayers = useCallback((frame?: number) => {
    const gl = glRef.current;
    const rtA = renderTargetA.current;
    const rtB = renderTargetB.current;
    if (!gl || !rtA || !rtB || !compositeMaterial.current || !compositeScene.current || !orthoCamera.current) return;

    const currentLayers = layersRef.current;

    // Start accumulation with a fully-transparent base in rtA.
    gl.setRenderTarget(rtA);
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.setRenderTarget(null);

    let readRT: THREE.WebGLRenderTarget = rtA;
    let writeRT: THREE.WebGLRenderTarget = rtB;

    // Layers are stored newest-first; reverse to composite bottom → top.
    const orderedLayers = [...currentLayers].reverse();
    const { currentFrame: _cf, totalFrames: _tf } = useAppStore.getState();
    const _roundedFrame = Math.round(frame ?? _cf);
    // Build effective transform/visibility data for all layers in one O(N) pass.
    const _effectCache = buildLayerEffectCache(currentLayers);
    for (const layer of orderedLayers) {
      const _eff = _effectCache.get(layer.id);
      if (!_eff?.visible) continue;
      // Skip layers whose timing bar does not cover the current frame.
      const _layerStart = layer.frameStart ?? 0;
      const _layerEnd   = layer.frameEnd   ?? (_tf - 1);
      if (_roundedFrame < _layerStart || _roundedFrame > _layerEnd) continue;
      const lRTData = layerRTs.current.get(layer.id);
      if (!lRTData) continue;
      const layerRT = lRTData.active === 'a' ? lRTData.a : lRTData.b;

      compositeMaterial.current.uniforms.uBase.value = readRT.texture;
      compositeMaterial.current.uniforms.uStroke.value = layerRT.texture;
      compositeMaterial.current.uniforms.uOpacity.value = _eff.opacity;
      compositeMaterial.current.uniforms.uMode.value = 0; // always draw for flatten

      // Build the combined inverse rotation matrix (translate → rotate) and send
      // its transpose (= inverse for orthogonal matrices) to the shader.
      // Uses effective transform so parent rotation/translateX offsets are inherited.
      compositeMaterial.current.uniforms.uRotInv.value =
        _scratchMatrix3.setFromMatrix4(_eff.matrix).transpose();
      compositeMaterial.current.uniforms.uTranslateY.value = _eff.translateY;
      compositeMaterial.current.uniforms.uBakeMode.value   = 0;

      gl.setRenderTarget(writeRT);
      gl.render(compositeScene.current, orthoCamera.current);
      gl.setRenderTarget(null);

      [readRT, writeRT] = [writeRT, readRT];
    }

    // readRT holds the final flattened result.
    activeRenderTarget.current = readRT;
    if (materialRef.current) {
      materialRef.current.map = readRT.texture;
      materialRef.current.needsUpdate = true;
    }
    // In demand mode, schedule a scene redraw so the updated texture appears.
    invalidateRef.current();
  }, []);

  /**
   * Composites a single layer (with its rotation and opacity applied) into
   * singleLayerDisplayRT using the composite shader, exactly as flattenLayers
   * would — but with a transparent base and only this one layer.
   * Returns singleLayerDisplayRT on success, or null if prerequisites are missing.
   */
  const buildSingleLayerDisplay = useCallback((layerId: string, frame?: number): THREE.WebGLRenderTarget | null => {
    const gl = glRef.current;
    const lRTData = layerRTs.current.get(layerId);
    const sldRT = singleLayerDisplayRT.current;
    const tbRT  = transparentBaseRT.current;
    if (!gl || !lRTData || !sldRT || !tbRT ||
        !compositeMaterial.current || !compositeScene.current || !orthoCamera.current) return null;

    const layer = layersRef.current.find((l) => l.id === layerId);
    if (!layer) return null;

    const { totalFrames: _tf } = useAppStore.getState();
    const _roundedFrame = Math.round(frame ?? useAppStore.getState().currentFrame);
    const _layerStart = layer.frameStart ?? 0;
    const _layerEnd   = layer.frameEnd   ?? (_tf - 1);
    if (_roundedFrame < _layerStart || _roundedFrame > _layerEnd) {
      gl.setRenderTarget(sldRT);
      gl.clearColor(0, 0, 0, 0);
      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.setRenderTarget(null);
      return sldRT;
    }

    const _eff = buildLayerEffectCache(layersRef.current).get(layer.id);
    if (!_eff?.visible) {
      gl.setRenderTarget(sldRT);
      gl.clearColor(0, 0, 0, 0);
      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.setRenderTarget(null);
      return sldRT;
    }

    const layerRT = lRTData.active === 'a' ? lRTData.a : lRTData.b;

    compositeMaterial.current.uniforms.uBase.value   = tbRT.texture;   // transparent
    compositeMaterial.current.uniforms.uStroke.value = layerRT.texture;
    compositeMaterial.current.uniforms.uOpacity.value = _eff.opacity;
    compositeMaterial.current.uniforms.uMode.value   = 0;
    compositeMaterial.current.uniforms.uRotInv.value =
      _scratchMatrix3.setFromMatrix4(_eff.matrix).transpose();
    compositeMaterial.current.uniforms.uTranslateY.value = _eff.translateY;
    compositeMaterial.current.uniforms.uBakeMode.value   = 0;

    gl.setRenderTarget(sldRT);
    gl.render(compositeScene.current, orthoCamera.current);
    gl.setRenderTarget(null);
    return sldRT;
  }, []);


  /** Duplicate the selected layer: copy metadata, keyframes, and GPU texture content. */
  const duplicateSelectedLayer = () => {
    const store = useAppStore.getState();
    const srcId = store.selectedLayerId;
    if (!srcId) return;
    const src = store.layers.find((l) => l.id === srcId);
    if (!src) return;

    pushSnapshot();

    // 1 – Create the new layer metadata (insert directly above the source).
    const newId = crypto.randomUUID();
    const newLayer: Layer = {
      id: newId,
      name: src.name + ' copy',
      visible: src.visible,
      opacity: src.opacity,
      translate: { ...src.translate },
      rotate:    { ...src.rotate },
    };

    // 2 – Deep-clone the source keyframes under the new id.
    const srcKfs = store.layerKeyframes[srcId];
    const newKfs: LayerKeyframes = {} as LayerKeyframes;
    if (srcKfs) {
      for (const p of Object.keys(srcKfs) as Array<keyof LayerKeyframes>) {
        (newKfs as any)[p] = { ...(srcKfs[p] as any) };
      }
    }

    // 3 – Add the new layer to the store (inserts at top of stack).
    //     Then manually insert it right above the source layer instead.
    store.addLayer(newLayer);
    // Inject cloned keyframes (addLayer adds an empty one by default).
    useAppStore.setState((s) => ({
      layerKeyframes: { ...s.layerKeyframes, [newId]: newKfs },
      // Re-order: move newLayer to be directly above srcId.
      layers: (() => {
        const arr = s.layers.filter((l) => l.id !== newId);
        const srcIdx = arr.findIndex((l) => l.id === srcId);
        const insertAt = srcIdx >= 0 ? srcIdx : 0;
        return [...arr.slice(0, insertAt), newLayer, ...arr.slice(insertAt)];
      })(),
      selectedLayerId: newId,
    }));

    // 4 – Ensure RT pair exists for the new layer, then copy pixels directly
    //     GPU-to-GPU via the copy shader. This avoids a CPU read-back + data URL
    //     round-trip which would double-encode the sRGB values and produce lighter colors.
    ensureLayerRTs(useAppStore.getState().layers);
    const srcPair = layerRTs.current.get(srcId);
    const dstPair = layerRTs.current.get(newId);
    const gl = glRef.current;
    if (srcPair && dstPair && gl && copyMaterial.current && copyScene.current && orthoCamera.current) {
      const activeSrcRT = srcPair.active === 'a' ? srcPair.a : srcPair.b;
      copyMaterial.current.uniforms.uTex.value = activeSrcRT.texture;
      gl.setRenderTarget(dstPair.a);
      gl.render(copyScene.current, orthoCamera.current);
      gl.setRenderTarget(null);
    }

    flattenLayers();
    scheduleBump();
    generateLayerThumbnail(newId);
    isDirtyRef.current = true;
    autosaveNotifyDirty();
  };

  const duplicateLayerRef = useRef<() => void>(duplicateSelectedLayer);
  duplicateLayerRef.current = duplicateSelectedLayer;

  /**
   * Renders the given layer's active RT into the 128×64 thumbnail RT,
   * reads it back, and pushes a data-URL to the Zustand store.
   */
  const generateLayerThumbnail = useCallback((layerId: string) => {
    const gl = glRef.current;
    const tRT = thumbnailRT.current;
    if (!gl || !tRT || !copyScene.current || !copyMaterial.current || !orthoCamera.current) return;

    // Read directly from the raw layer RT — no rotation, translation, or opacity applied.
    const lRTData = layerRTs.current.get(layerId);
    if (!lRTData) return;
    const rawRT = lRTData.active === 'a' ? lRTData.a : lRTData.b;

    // Scale the raw layer content down to the 128×64 thumbnail RT.
    copyMaterial.current.uniforms.uTex.value = rawRT.texture;
    gl.setRenderTarget(tRT);
    gl.render(copyScene.current, orthoCamera.current);
    gl.setRenderTarget(null);

    // Read back the 128×64 pixels.
    const tw = 128, th = 64;
    const thumbBuf = new Uint8Array(tw * th * 4);
    try {
      gl.readRenderTargetPixels(tRT, 0, 0, tw, th, thumbBuf);
    } catch { return; }

    const worker = imageWorkerRef.current;
    if (!worker) return;
    const id = crypto.randomUUID();
    workerCallbacksRef.current.set(id, (msg) => {
      if (msg.error || !msg.blob) return;
      const newUrl = URL.createObjectURL(msg.blob as Blob);
      const prevUrl = thumbnailBlobUrlsRef.current.get(layerId);
      if (prevUrl) URL.revokeObjectURL(prevUrl);
      thumbnailBlobUrlsRef.current.set(layerId, newUrl);
      setLayerThumbnail(layerId, newUrl);
    });
    worker.postMessage({ id, kind: 'thumbnail', layerId, buffer: thumbBuf.buffer, width: tw, height: th }, [thumbBuf.buffer]);
  }, []);

  const generateAllLayerThumbnails = () => {
    layersRef.current.forEach((layer) => generateLayerThumbnail(layer.id));
  };

  const initializeBrushPipeline = () => {
    const gl = glRef.current;
    if (!gl) return;

    const size = { width: texResRef.current.w, height: texResRef.current.h };
    const makeRT = () => {
      const rt = new THREE.WebGLRenderTarget(size.width, size.height, {
        type: THREE.UnsignedByteType,
        minFilter: THREE.LinearFilter,
        magFilter: THREE.LinearFilter,
        wrapS: THREE.RepeatWrapping,
        wrapT: THREE.RepeatWrapping,
      });
      rt.texture.colorSpace = THREE.SRGBColorSpace;
      return rt;
    };

    renderTargetA.current = makeRT();
    renderTargetB.current = makeRT();
    strokeRenderTargetA.current = makeRT();
    strokeRenderTargetB.current = makeRT();
    activeStrokeRenderTarget.current = strokeRenderTargetA.current;
    lastStrokeRenderTarget.current = makeRT();

    activeRenderTarget.current = renderTargetA.current;

    // Initialize render targets to fully transparent — the white base is
    // now provided by the separate background sphere in the scene.
    gl.setRenderTarget(renderTargetA.current);
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.setRenderTarget(renderTargetB.current);
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);

    // Clear the current stroke buffer so it only shows the current stroke.
    const activeStrokeRT = activeStrokeRenderTarget.current;
    if (activeStrokeRT) {
      gl.setRenderTarget(activeStrokeRT);
      gl.clearColor(0, 0, 0, 0);
      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.setRenderTarget(null);
    }

    // Assign the cleared stroke RT texture immediately so the stroke sphere
    // starts transparent instead of solid (material default color) before
    // the first pointer-down event.
    if (strokeMaterialRef.current && activeStrokeRenderTarget.current) {
      strokeMaterialRef.current.map = activeStrokeRenderTarget.current.texture;
      strokeMaterialRef.current.needsUpdate = true;
    }

    // Also clear the "last stroke" buffer.
    gl.setRenderTarget(lastStrokeRenderTarget.current);
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.setRenderTarget(null);

    orthoCamera.current = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
    quadScene.current = new THREE.Scene();

    brushMaterial.current = new THREE.ShaderMaterial({
      glslVersion: THREE.GLSL3,
      transparent: false,
      blending: THREE.NoBlending,
      depthWrite: false,
      uniforms: {
        uPrev:          { value: null },
        uBrushCenter:   { value: new THREE.Vector3() },
        uBrushRadius:   { value: brushRadius.current },
        uBrushColor:    { value: new THREE.Color(0, 0, 0) },
        // Projection-mode uniforms (updated per dup copy in projection mode)
        uProjMode:      { value: 0 },
        uCamViewProj:   { value: new THREE.Matrix4() },
        uCamPos:        { value: new THREE.Vector3() },
        uBrushCenterNDC: { value: new THREE.Vector2() },
        uViewAspect:    { value: 1.0 },
        uSphereCenterNDC:     { value: new THREE.Vector2(0, 0) },
        uInverseWarpK:        { value: 1.5 },
        uInverseWarpStrength: { value: 0.85 },
      },
      vertexShader: brushVertexShader,
      fragmentShader: brushFragmentShader,
    });

    const quad = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), brushMaterial.current);
    quadScene.current.add(quad);

    // ── Envelope fill: batched capsule shader (ENVELOPE_BATCH segments per draw call) ─
    const ENVELOPE_BATCH = 64;
    envelopeMaterialRef.current = new THREE.ShaderMaterial({
      glslVersion: THREE.GLSL3,
      transparent: false,
      blending: THREE.NoBlending,
      depthWrite: false,
      depthTest: false,
      uniforms: {
        uPrev:        { value: null },
        uBrushColor:  { value: new THREE.Color() },
        uProjMode:    { value: 0 },
        uCamViewProj: { value: new THREE.Matrix4() },
        uCamPos:      { value: new THREE.Vector3() },
        uViewAspect:  { value: 1.0 },
        uSphereCenterNDC:     { value: new THREE.Vector2(0, 0) },
        uInverseWarpK:        { value: 1.5 },
        uInverseWarpStrength: { value: 0.85 },
        uCount:       { value: 0 },
        // xy = NDC or world.xy, zw = worldZ & radius  (layout depends on uProjMode)
        // proj:  xyz = ndc.x, ndc.y, ndcRadius  (w unused)
        // wrap:  xyz = world pos normalised,  w = world-space radius
        uSegA: { value: Array.from({ length: ENVELOPE_BATCH }, () => new THREE.Vector4()) },
        uSegB: { value: Array.from({ length: ENVELOPE_BATCH }, () => new THREE.Vector4()) },
      },
      ...makeEnvelopeShaders(ENVELOPE_BATCH),
    });
    envelopeSceneRef.current = new THREE.Scene();
    envelopeSceneRef.current.add(new THREE.Mesh(new THREE.PlaneGeometry(2, 2), envelopeMaterialRef.current));

    // Initialize a composite shader that takes the completed stroke buffer and
    // blends it onto the current sphere texture using a uniform opacity.
    compositeMaterial.current = new THREE.ShaderMaterial({
      transparent: false,
      blending: THREE.NoBlending,
      depthWrite: false,
      uniforms: {
        uBase: { value: null },
        uStroke: { value: null },
        uOpacity: { value: 1.0 },
        uMode: { value: 0 }, // 0 = draw, 1 = erase
        uRotInv: { value: new THREE.Matrix3() }, // combined rotation (tx-longitude ⊕ user-rotate) applied to UV sampling
        uTranslateY: { value: 0.0 },             // translateY applied as a UV v-shift
        uBakeMode:   { value: 0 },               // 0 = display (R⁻¹ then −ty), 1 = bake (+ty then R)
      },
      vertexShader: compositeVertexShader,
      fragmentShader: compositeFragmentShader,
    });

    // Create a dedicated scene/mesh for compositing so we don’t allocate each stroke.
    compositeScene.current = new THREE.Scene();
    compositeMesh.current = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), compositeMaterial.current);
    compositeScene.current.add(compositeMesh.current);

    if (textureRef.current) {
        copyMaterial.current = new THREE.ShaderMaterial({
          transparent: true,
          blending: THREE.NoBlending,
          depthWrite: false,
          uniforms: { uTex: { value: textureRef.current } },
          vertexShader: copyVertexShader,
          fragmentShader: copyFragmentShader,
        });
        copyScene.current = new THREE.Scene();
        copyScene.current.add(new THREE.Mesh(new THREE.PlaneGeometry(2, 2), copyMaterial.current));

        // Ensure every existing layer (e.g. the default background layer) has
        // its own RT pair, then seed the bottom layer with any canvas content.
        ensureLayerRTs(layersRef.current);
        const bottomLayerId =
          layersRef.current.length > 0
            ? layersRef.current[layersRef.current.length - 1].id
            : null;
        if (bottomLayerId) {
          const lRT = layerRTs.current.get(bottomLayerId);
          if (lRT) {
            gl.setRenderTarget(lRT.a);
            gl.render(copyScene.current, orthoCamera.current);
            gl.setRenderTarget(lRT.b);
            gl.render(copyScene.current, orthoCamera.current);
            gl.setRenderTarget(null);
          }
        }
    }

    // Produce the initial display texture by flattening all layers.
    // ── Stamp shader ──────────────────────────────────────────────────────────
    stampMaterial.current = new THREE.ShaderMaterial({
      transparent: false,
      blending: THREE.NoBlending,
      depthWrite: false,
      uniforms: {
        uBase:              { value: null },
        uStamp:             { value: null },
        uStampCenter:       { value: new THREE.Vector3(0, 0, 1) },
        uStampRight:        { value: new THREE.Vector3(1, 0, 0) },
        uStampUp:           { value: new THREE.Vector3(0, 1, 0) },
        uStampScale:        { value: new THREE.Vector2(0.4, 0.4) },
        uStampOpacity:      { value: 1.0 },
        uStampColor:        { value: new THREE.Color(1, 1, 1) },
        uRotFwd:            { value: new THREE.Matrix3() },
        uProjMode:          { value: 0 },           // 0 = wrap, 1 = camera-project
        uCamViewProj:       { value: new THREE.Matrix4() },
        uStampCenterNDC:    { value: new THREE.Vector2() },
        uStampHalfSizeNDC:  { value: new THREE.Vector2(0.1, 0.1) },
        uCamPos:            { value: new THREE.Vector3() },
        uStampRotation:     { value: 0.0 },
        uStampAspect:       { value: 1.0 },
        uStampFlipX:        { value: 0 },
        uStampFlipY:        { value: 0 },
        uStampCenterUV:     { value: new THREE.Vector2(0.5, 0.5) },
        uSphereCenterNDC:   { value: new THREE.Vector2(0, 0) },
        uInverseWarpK:      { value: 1.5 },
        uInverseWarpStrength: { value: 0.85 },
      },
      vertexShader: stampVertexShader,
      fragmentShader: stampFragmentShader,
    });
    stampScene.current = new THREE.Scene();
    stampScene.current.add(new THREE.Mesh(new THREE.PlaneGeometry(2, 2), stampMaterial.current));

    // Initialize the blur shader for the blur tool (two-pass separable Gaussian).
    const texW = size.width, texH = size.height;
    blurMaterial.current = new THREE.ShaderMaterial({
      transparent: false,
      blending: THREE.NoBlending,
      depthWrite: false,
      uniforms: {
        uBase:       { value: null },
        uOriginal:   { value: null },
        uStroke:     { value: null },
        uOpacity:    { value: 1.0 },
        uBlurRadius: { value: 4.0 },
        uTexelSize:  { value: new THREE.Vector2(1 / texW, 1 / texH) },
        uRotInv:     { value: new THREE.Matrix3() },
        uTranslateY: { value: 0.0 },
        uPassMode:   { value: 0 },
      },
      vertexShader: blurVertexShader,
      fragmentShader: blurFragmentShader,
    });
    blurScene.current = new THREE.Scene();
    blurScene.current.add(new THREE.Mesh(new THREE.PlaneGeometry(2, 2), blurMaterial.current));

    flattenLayers();
    if (materialRef.current && activeRenderTarget.current) {
      materialRef.current.map = activeRenderTarget.current.texture;
      materialRef.current.needsUpdate = true;
    }
    // Wire the stroke preview sphere to the (empty) stroke render target.
    if (strokeMaterialRef.current && activeStrokeRenderTarget.current) {
      strokeMaterialRef.current.map = activeStrokeRenderTarget.current.texture;
      strokeMaterialRef.current.needsUpdate = true;
    }

    // Full-res RT for single-layer previews (rotation + opacity applied).
    singleLayerDisplayRT.current = makeRT();

    // A permanently-transparent 1×1 RT — used as the "empty base" for single-layer
    // compositing without needing to reuse any of the main pipeline RTs.
    transparentBaseRT.current = new THREE.WebGLRenderTarget(1, 1, {
      type: THREE.UnsignedByteType,
      minFilter: THREE.LinearFilter,
      magFilter: THREE.LinearFilter,
    });
    gl.setRenderTarget(transparentBaseRT.current);
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.setRenderTarget(null);

    // Small render target used to produce layer thumbnails without reading
    // back the full-resolution texture.
    thumbnailRT.current = new THREE.WebGLRenderTarget(128, 64, {
      type: THREE.UnsignedByteType,
      minFilter: THREE.LinearFilter,
      magFilter: THREE.LinearFilter,
    });
    // Must match the layer RTs (SRGBColorSpace / SRGB8_ALPHA8) so that the copy
    // shader's linear output is re-encoded to sRGB on write.  readRenderTargetPixels
    // then returns proper sRGB bytes that putImageData/toDataURL can use directly.
    thumbnailRT.current.texture.colorSpace = THREE.SRGBColorSpace;
  };


  const paintSphereBrush = useCallback((worldPoint: THREE.Vector3, pressure = 1.0) => {
    brushRadius.current = brushSizeRef.current;

    const gl = glRef.current;
    const activeStrokeRT = activeStrokeRenderTarget.current;
    const otherStrokeRT =
      activeStrokeRT === strokeRenderTargetA.current
        ? strokeRenderTargetB.current
        : strokeRenderTargetA.current;

    if (!gl || !activeStrokeRT || !otherStrokeRT || !brushMaterial.current || !quadScene.current || !orthoCamera.current) return;

    // The stroke is always painted at the raw world-space hit point. No rotation
    // is applied here — the mark appears exactly under the mouse on the preview
    // sphere. The rotation correction happens at commit time (updateSpherePreview)
    // when the stroke is baked into the layer texture.
    const brushCenter = worldPoint.clone().normalize();

    const centers = getDuplicationCenters(brushCenter, dupModeRef.current);
    // When Ctrl is held and gradient is active, use the last gradient stop color;
    // otherwise use first stop / solid color (set into brushColorRef by App.tsx).
    let actualBrushColor: string = Array.isArray(brushColorRef.current)
      ? (brushColorRef.current as any)[0].color
      : brushColorRef.current;
    if (ctrlHeldRef.current) {
      const pv = pickerValueRef.current;
      if (pv && typeof pv.getColors === 'function') {
        const stops: Array<{ color: any; percent: number }> = pv.getColors();
        if (stops && stops.length > 0) {
          const last = stops[stops.length - 1];
          if (last.color && typeof last.color.toHexString === 'function') {
            const hex8 = last.color.toHexString();
            actualBrushColor = hex8.length >= 9 ? hex8.slice(0, 7) : hex8;
          }
        }
      }
    }
    // In erase/blur mode use white as the preview color — the composite/blur shader only
    // reads stroke.a for the mask, so the RGB value has no effect on the actual result.
    const paintColor = (toolModeRef.current === 'erase' || toolModeRef.current === 'blur') ? '#ffffff' : actualBrushColor;
    // setStyle parses the CSS sRGB hex and converts to linear, matching what the
    // SRGB8_ALPHA8 render targets expect (GPU re-encodes linear→sRGB on write).
    brushMaterial.current.uniforms.uBrushColor.value.setStyle(paintColor);

    const isProjMode = drawProjectionModeRef.current === 'project';
    const isInverseDrawMode = drawProjectionModeRef.current === 'inverse';
    brushMaterial.current.uniforms.uProjMode.value = isProjMode ? 1 : isInverseDrawMode ? 2 : 0;

    // For projection mode: compute the base NDC center (invariant across copies)
    // and viewport aspect ratio once.  Per-copy: rotate viewProj and camPos.
    const cam = cameraRef.current!;
    let baseCenterNDC = new THREE.Vector2();
    let viewAspect = 1.0;
    if (isProjMode || isInverseDrawMode) {
      // Convert sphere chord distance to NDC units so the brush appears the same
      // physical size in both wrap and projection modes.
      // A chord of length c on the unit sphere has arc angle theta = 2*asin(c/2).
      // A point at that arc from the brush center sits at camera-space x = sin(theta),
      // z = -(D - cos(theta)) where D is camera distance from the sphere origin.
      // This yields the vertical-NDC radius, which we compare against a distance
      // metric that scales x by the viewport aspect back into square pixel units.
      const D = cam.position.length();
      const effectiveSize = usePressureRef.current ? brushSizeRef.current * Math.max(0.05, pressure) : brushSizeRef.current;
      const theta = 2 * Math.asin(Math.min(1, effectiveSize / 2));
      brushMaterial.current.uniforms.uBrushRadius.value =
        Math.sin(theta) / camFrustumHalfH(cam, D - Math.cos(theta));
    } else {
      const effectiveSize = usePressureRef.current ? brushSizeRef.current * Math.max(0.05, pressure) : brushSizeRef.current;
      brushMaterial.current.uniforms.uBrushRadius.value = effectiveSize;
    }

    if (isProjMode) {
      const ndc = worldPoint.clone().project(cam);
      baseCenterNDC.set(ndc.x, ndc.y);
      const el = glRef.current?.domElement;
      viewAspect = el ? el.clientWidth / el.clientHeight : 1.0;
      brushMaterial.current.uniforms.uViewAspect.value = viewAspect;
      brushMaterial.current.uniforms.uBrushCenterNDC.value = baseCenterNDC;
    }
    if (isInverseDrawMode) {
      // Back-sphere exit of the cursor ray provides the anchor NDC for the inverse brush.
      // Apply the same radial warp as the shader so the brush center is in warped space.
      const camPos = cam.position;
      const D2 = camPos.dot(camPos);
      const rayFrag = worldPoint.clone().sub(camPos);
      const tBack = (D2 - 1.0) / rayFrag.dot(rayFrag);
      const pBack = camPos.clone().addScaledVector(rayFrag, tBack);
      const ndcBack = pBack.clone().project(cam);
      // Sphere center NDC (origin projected onto screen).
      const sphereOriginProj = new THREE.Vector3(0, 0, 0).project(cam);
      const sphereCenterNDC = new THREE.Vector2(sphereOriginProj.x, sphereOriginProj.y);
      // CPU-side warp matching the GLSL applyInverseWarp function.
      const warpedCenter = cpuInverseWarp(
        new THREE.Vector2(ndcBack.x, ndcBack.y),
        sphereCenterNDC,
        inverseWarpKRef.current,
        inverseWarpStrengthRef.current,
      );
      baseCenterNDC.copy(warpedCenter);
      const el = glRef.current?.domElement;
      viewAspect = el ? el.clientWidth / el.clientHeight : 1.0;
      brushMaterial.current.uniforms.uBrushCenterNDC.value = baseCenterNDC;
      brushMaterial.current.uniforms.uViewAspect.value = viewAspect;
      brushMaterial.current.uniforms.uCamViewProj.value.multiplyMatrices(cam.projectionMatrix, cam.matrixWorldInverse);
      brushMaterial.current.uniforms.uCamPos.value.copy(camPos);
      brushMaterial.current.uniforms.uSphereCenterNDC.value.copy(sphereCenterNDC);
      brushMaterial.current.uniforms.uInverseWarpK.value = inverseWarpKRef.current;
      brushMaterial.current.uniforms.uInverseWarpStrength.value = inverseWarpStrengthRef.current;
    }

    // The stroke buffer is rendered fully opaque; we apply the desired opacity
    // later when compositing it into the main texture.

    // Paint into the current stroke render target by ping-ponging.
    let prevRT = activeStrokeRT;
    let nextRT = otherStrokeRT;

    for (const { center, quaternion } of centers) {
      brushMaterial.current.uniforms.uBrushCenter.value.copy(center);

      if (isProjMode || isInverseDrawMode) {
        // Rotate the view matrix and camera position by the dup quaternion
        // so each copy is painted from the equivalent rotated viewpoint.
        // Uses module-level scratch objects to avoid per-dot allocations.
        _scratchQ.copy(quaternion).invert();
        _scratchM4a.makeRotationFromQuaternion(_scratchQ);
        _scratchM4b.copy(cam.matrixWorldInverse).multiply(_scratchM4a);
        brushMaterial.current.uniforms.uCamViewProj.value.multiplyMatrices(cam.projectionMatrix, _scratchM4b);
        brushMaterial.current.uniforms.uCamPos.value.copy(cam.position).applyQuaternion(quaternion);
        // NDC center is invariant (rotating camera + target by same Q leaves NDC unchanged).
      }

      brushMaterial.current.uniforms.uPrev.value = prevRT.texture;
      gl.setRenderTarget(nextRT);
      gl.render(quadScene.current, orthoCamera.current);
      gl.setRenderTarget(null);
      // Swap for the next pass, so each copy layers on top of the previous.
      [prevRT, nextRT] = [nextRT, prevRT];
    }

    activeStrokeRenderTarget.current = prevRT;

    // Push the updated stroke buffer to the preview sphere in real-time.
    if (strokeMaterialRef.current) {
      strokeMaterialRef.current.blendSrc = THREE.OneFactor;
      strokeMaterialRef.current.map = activeStrokeRenderTarget.current.texture;
      // Erase preview: white at half the brush opacity so the shape is always visible,
      // and dialling opacity down lets you preview a partial erase proportionally.
      // Draw preview: match the actual brush opacity so what you see is what gets committed.
      strokeMaterialRef.current.opacity = toolModeRef.current === 'blur'
        ? 0
        : toolModeRef.current === 'erase' ? brushOpacityRef.current * 0.5 : brushOpacityRef.current;
      strokeMaterialRef.current.needsUpdate = true;
    }
    // In demand mode, schedule a scene redraw to show the new stroke dot.
    invalidateRef.current();
  }, []);


  const paintSphereLine = useCallback((start: THREE.Vector3, end: THREE.Vector3, pressure = 1.0) => {
    const dist = start.distanceTo(end);
    if (dist < 1e-6) { paintSphereBrush(start, pressure); return; }

    // Step along the segment to fill gaps as the pointer moves.
    // Larger brushes require fewer steps.
    // 0.75× gives a gap-free preview (dots slightly overlap) with fewer GPU passes
    // than 0.5×. commitEnvelopeFill produces the pixel-perfect result at stroke end.
    const step = brushRadius.current * 0.75;
    const steps = Math.min(24, Math.max(1, Math.ceil(dist / step)));
    const tmp = new THREE.Vector3();

    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      tmp.copy(start).lerp(end, t);
      paintSphereBrush(tmp, pressure);
    }
  }, []);

  const [isDrawing, setIsDrawing] = useState(false);
  const [isRotating, setIsRotating] = useState(false);
  const [isTranslating, setIsTranslating] = useState(false);
  const isDrawingRef = useRef(false);
  const isRotatingRef = useRef(false);
  const isTranslatingRef = useRef(false);
  // True while the primary mouse button is held anywhere in the viewport,
  // even if the initial press was off the sphere mesh.
  const viewportMouseDownRef = useRef(false);
  const rotDragStart = useRef<{ x: number; y: number } | null>(null);
  const rotDragBase = useRef<{ rx: number; ry: number; rz: number } | null>(null);
  const transDragStart = useRef<{ x: number; y: number } | null>(null);
  const transDragBase = useRef<{ tx: number; ty: number } | null>(null);
  const [shiftHeld, setShiftHeld] = useState(false);
  const [overSphere, setOverSphere] = useState(false);
  // Opacity scrub: hold A (brush alpha) or L (layer opacity) + move mouse L/R.
  // Size scrub: hold B (brush size) + move mouse L/R.
  const opacityDragModeRef = useRef<null | 'brush' | 'layer' | 'size' | 'rotation' | 'blurRadius'>(null);
  const opacityDragStartXRef = useRef(0);
  const opacityDragStartValRef = useRef(0);
  const mouseScreenRef = useRef({ x: 0, y: 0 });
  const flattenLayersRef = useRef<() => void>(() => {});
  const ensureLayerRTsRef = useRef<(layers: Layer[]) => void>(() => {});
  const scheduleBumpRef = useRef<(after?: () => void) => void>(() => {});
  const generateAllLayerThumbnailsRef = useRef<() => void>(() => {});
  const generateLayerThumbnailRef = useRef<(id: string) => void>(() => {});
  const markLayersDirtyRef = useRef<(...ids: string[]) => void>(() => {});
  const resetHeroStateRef = useRef<() => void>(() => {});
  const brushSizeRef = useRef(0.075);
  const adjustBrushSizeRef = useRef<(factor: number) => void>(() => {});
  const renderTextPreviewRef = useRef<(hitPoint: THREE.Vector3) => void>(() => {});
  const renderStampPreviewRef = useRef<(hitPoint: THREE.Vector3) => void>(() => {});
  const scrubDxRef = useRef(0);               // accumulated movementX during pointer-locked scrub
  const lockedReticlePosRef = useRef({ x: 0, y: 0 }); // screen position where scrub key was pressed
  const [opacityReticle, setOpacityReticle] = useState<{ x: number; y: number; value: number; label: string; ringPx?: number; isSquare?: boolean } | null>(null);
  /** DOM ref for the brush cursor ring — updated directly to avoid React re-renders on every pointer move. */
  const brushCursorElemRef = useRef<HTMLDivElement>(null);
  /** DOM ref for the dup-mode dot row above the cursor — updated directly like brushCursorElemRef. */
  const dupModeCursorRef = useRef<HTMLDivElement>(null);
  /** Inner ring for blur mode showing the blur kernel radius as a fraction of brush size. */
  const blurInnerCursorElemRef = useRef<HTMLDivElement>(null);
  /** SVG line overlay for fill-mode gradient drag direction preview. */
  const fillDragSvgRef = useRef<SVGSVGElement>(null);
  /** Screen-space click-start for fill drag line. */
  const fillDragStartScreenRef = useRef<{ x: number; y: number } | null>(null);
  /** Whether alt was held at fill drag start — uses color 2 as the primary fill. */
  const fillDragAltRef = useRef(false);


  useEffect(() => {
    const onKey = (e: KeyboardEvent) => setShiftHeld(e.shiftKey);
    window.addEventListener('keydown', onKey);
    window.addEventListener('keyup', onKey);
    return () => { window.removeEventListener('keydown', onKey); window.removeEventListener('keyup', onKey); };
  }, []);

  const smoothness = useAppStore((s) => s.smoothness);
  const dupMode = useAppStore((s) => s.dupMode);
  const viewportBgMode = useAppStore((s) => s.viewportBgMode);
  const {
    camPos,
    selectedHeroId,
    heroBgColor,
    heroBgAlpha,
    applyCamPos,
    animateCameraTo,
    syncCamRef,
    resetHeroState,
    cameraTrackEnabled,
    setCameraTrackEnabled,
  } = useCameraControl({ cameraRef, controlsRef, invalidateRef });
  const showGrid = useAppStore((s) => s.showGrid);
  const showManipulator = useAppStore((s) => s.showManipulator);
  const uvViewportMain  = useAppStore((s) => s.uvViewportMain);
  const showFps = useAppStore((s) => s.showFps);
  const overlayArchitecture = useAppStore((s) => s.overlayArchitecture);
  const overlaySafety = useAppStore((s) => s.overlaySafety);
  const cameraFov = useAppStore((s) => s.cameraFov);
  const setCameraFov = useAppStore((s) => s.setCameraFov);
  const fpsReadout = useFpsCounter(showFps);
  const [isOrtho, setIsOrtho] = useState(false);

  // \u2500\u2500 Camera panel scroll/drag scrubbing \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500
  const camXScrub = useScrollDrag<HTMLInputElement>({ value: camPos.x, step: 0.1, onChange: (v) => applyCamPos('x', v) });
  const camYScrub = useScrollDrag<HTMLInputElement>({ value: camPos.y, step: 0.1, onChange: (v) => applyCamPos('y', v) });
  const camZScrub = useScrollDrag<HTMLInputElement>({ value: camPos.z, step: 0.1, onChange: (v) => applyCamPos('z', v) });
  const camFovScrub = useScrollDrag<HTMLInputElement>({ value: cameraFov, min: 5, max: 150, step: 1, onChange: (v) => setCameraFov(v) });
  const camScrubs = { x: camXScrub, y: camYScrub, z: camZScrub };
  const renderSequenceRef = useRef<(
    s: RenderSettings,
    onProgress: (done: number, total: number, status?: 'rendering' | 'converting') => void,
    signal?: AbortSignal,
  ) => Promise<void>>(async () => {});
  const isDirtyRef = useRef(false);
  // Suppress dirty notifications during new-project / load-project resets.
  const isResettingRef = useRef(false);

  // ── Layer history (undo/redo layer operations) ────────────────────────────
  const {
    captureLayerSnapshot,
    restoreLayerEntry,
    isLayerOpInProgress,
  } = useLayerHistory({
    glRef,
    copyMaterial,
    copyScene,
    orthoCamera,
    layerRTs,
    ensureLayerRTsRef,
    flattenLayersRef,
    scheduleBumpRef,
    generateAllLayerThumbnailsRef,
  });

  // ── Project save / load ──────────────────────────────────────────────────
  const { buildProjectBlob, buildProjectJson, saveProject, loadProject, newProject, saveCurrentProject: _saveCurrentProject } = useProjectIO({
    glRef,
    readRTPixelBufferRef,
    copyMaterial,
    copyScene,
    orthoCamera,
    layerRTs,
    clearHistoryRef,
    savedFileHandleRef,
    isDirtyRef,
    ensureLayerRTsRef,
    flattenLayersRef,
    resetHeroStateRef,
    generateAllLayerThumbnailsRef,
    scheduleBumpRef,
    layerTextureCacheRef,
  });

  // ── Autosave ─────────────────────────────────────────────────────────────
  const buildJsonRef = useRef<(() => string) | null>(null);
  buildJsonRef.current = () => buildProjectJson(useAppStore.getState().currentProjectName);
  const { notifyDirty: autosaveNotifyDirty, resetToClean: autosaveResetToClean, cancelPending: autosaveCancelPending } = useAutosave({
    buildJsonRef,
    onStatusChange: (s) => useAppStore.getState().setAutosaveStatus(s),
  });

  // Checkerboard texture for the transparent inner-sphere option.
  const checkerTex = useMemo(() => {
    const size = 128;
    const cell = 16;
    const c = document.createElement('canvas');
    c.width = size; c.height = size;
    const ctx = c.getContext('2d')!;
    for (let r = 0; r < size / cell; r++) {
      for (let col = 0; col < size / cell; col++) {
        ctx.fillStyle = (r + col) % 2 === 0 ? '#888888' : '#bbbbbb';
        ctx.fillRect(col * cell, r * cell, cell, cell);
      }
    }
    const tex = new THREE.CanvasTexture(c);
    tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
    tex.repeat.set(8, 4);
    return tex;
  }, []);

  // ── Toolbar state from store ─────────────────────────────────────────────
  type TexRes = typeof TEX_RESOLUTIONS[number];
  const texRes = useAppStore((s) => s.texRes);
  const texResRef = useRef<TexRes>(TEX_RESOLUTIONS[1]);

  const brushSize = useAppStore((s) => s.brushSize);
  const setBrushSize = useAppStore((s) => s.setBrushSize);
  const brushColor = useAppStore((s) => s.brushColor);
  const pickerValue = useAppStore((s) => s.pickerValue);
  const brushOpacity = useAppStore((s) => s.brushOpacity);
  const setBrushOpacity = useAppStore((s) => s.setBrushOpacity);
  const blurRadius = useAppStore((s) => s.blurRadius);
  const fillFloodMode = useAppStore((s) => s.fillFloodMode);
  const fillTolerance = useAppStore((s) => s.fillTolerance);
  const toolMode = useAppStore((s) => s.toolMode);
  const projectModalOpen = useAppStore((s) => s.projectModalOpen);
  const setProjectModalOpen = useAppStore((s) => s.setProjectModalOpen);
  const renderModalOpen = useAppStore((s) => s.renderModalOpen);
  const setRenderModalOpen = useAppStore((s) => s.setRenderModalOpen);
  const currentProjectName = useAppStore((s) => s.currentProjectName);
  const setCurrentProjectName = useAppStore((s) => s.setCurrentProjectName);
  const stampSize = useAppStore((s) => s.stampSize);
  const setStampSize = useAppStore((s) => s.setStampSize);
  const stampSizeRef = useRef(0.08);
  const stampRotation = useAppStore((s) => s.stampRotation);
  const setStampRotation = useAppStore((s) => s.setStampRotation);
  const stampRotationRef = useRef(0);
  const stampFlipX = useAppStore((s) => s.stampFlipX);
  const stampFlipY = useAppStore((s) => s.stampFlipY);
  const stampIsPreset  = useAppStore((s) => s.stampIsPreset);
  const stampFlipXRef = useRef(false);
  const stampFlipYRef = useRef(false);
  const stampIsPresetRef  = useRef(false);

  // ── Inverse-warp tuning values (from store) ─────────────────────────────
  const inverseWarpK = useAppStore((s) => s.inverseWarpK);
  const inverseWarpStrength = useAppStore((s) => s.inverseWarpStrength);
  const inverseWarpKRef = useRef(1.5);
  const inverseWarpStrengthRef = useRef(0.85);
  useEffect(() => {
    if (stampMaterial.current) {
      stampMaterial.current.uniforms.uInverseWarpK.value = inverseWarpK;
      stampMaterial.current.uniforms.uInverseWarpStrength.value = inverseWarpStrength;
    }
    if (brushMaterial.current) {
      brushMaterial.current.uniforms.uInverseWarpK.value = inverseWarpK;
      brushMaterial.current.uniforms.uInverseWarpStrength.value = inverseWarpStrength;
    }
    if (envelopeMaterialRef.current) {
      envelopeMaterialRef.current.uniforms.uInverseWarpK.value = inverseWarpK;
      envelopeMaterialRef.current.uniforms.uInverseWarpStrength.value = inverseWarpStrength;
    }
  }, [inverseWarpK, inverseWarpStrength]);

  // ── Opacity scrub (A = brush alpha, L = layer opacity) ────────────────────
  useOpacityScrub({
    opacityDragModeRef,
    opacityDragStartValRef,
    scrubDxRef,
    lockedReticlePosRef,
    mouseScreenRef,
    brushOpacityRef,
    toolModeRef,
    stampSizeRef,
    stampRotationRef,
    brushSizeRef,
    blurRadiusRef,
    cameraRef,
    glRef,
    layersRef,
    selectedLayerIdRef,
    lastStampHitPoint,
    flattenLayersRef,
    renderTextPreviewRef,
    renderStampPreviewRef,
    setOpacityReticle,
    fillToleranceRef,
    fillPreviewUpdateRef,
  });
  const stampProjectionMode = useAppStore((s) => s.stampProjectionMode);
  const stampProjectionModeRef = useRef<'wrap' | 'project' | 'inverse'>('wrap');
  const drawProjectionMode = useAppStore((s) => s.drawProjectionMode);
  const drawProjectionModeRef = useRef<'wrap' | 'project' | 'inverse'>('wrap');
  const usePressure = useAppStore((s) => s.usePressure);
  const simPressure = useAppStore((s) => s.simPressure);
  const simPressureRandomness = useAppStore((s) => s.simPressureRandomness);

  // ── Text tool state from store ───────────────────────────────────────────
  const textContent = useAppStore((s) => s.textContent);
  const textFont = useAppStore((s) => s.textFont);
  const textFontSize = useAppStore((s) => s.textFontSize);
  const textBold = useAppStore((s) => s.textBold);
  const textItalic = useAppStore((s) => s.textItalic);
  const textJustify = useAppStore((s) => s.textJustify);
  const textLetterSpacing = useAppStore((s) => s.textLetterSpacing);
  const textLineHeight = useAppStore((s) => s.textLineHeight);
  // Timeline / render settings
  const fps = useAppStore((s) => s.fps);
  const totalFrames = useAppStore((s) => s.totalFrames);
  const renderSettings = useAppStore((s) => s.renderSettings);
  const setRenderSettings = useAppStore((s) => s.setRenderSettings);
  const textContentRef = useRef('Hello');
  const textFontRef = useRef('Arial');
  const textFontSizeRef = useRef(120);
  const textBoldRef = useRef(false);
  const textItalicRef = useRef(false);
  const textJustifyRef = useRef<'left' | 'center' | 'right'>('center');
  const textLetterSpacingRef = useRef(0);
  const textLineHeightRef = useRef(1.4);
  const textStampTextureRef = useRef<THREE.CanvasTexture | null>(null);
  /** Cached canvas from the last buildTextCanvas call — reused while params are unchanged. */
  const textCanvasCacheRef = useRef<HTMLCanvasElement | null>(null);
  const textCanvasCacheKeyRef = useRef<string>('');

  // Keep refs in sync so event-handler closures always see the current values.
  texResRef.current = texRes;
  usePressureRef.current = usePressure;
  simPressureRef.current = simPressure;
  simPressureRandomnessRef.current = simPressureRandomness;
  brushOpacityRef.current = brushOpacity;
  blurRadiusRef.current = blurRadius;
  smoothnessRef.current = smoothness;
  brushColorRef.current = brushColor;
  pickerValueRef.current = pickerValue;
  brushSizeRef.current = brushSize;
  toolModeRef.current = toolMode;
  dupModeRef.current = dupMode;
  stampSizeRef.current = stampSize;
  stampRotationRef.current = stampRotation;
  stampProjectionModeRef.current = stampProjectionMode;
  drawProjectionModeRef.current = drawProjectionMode;
  stampFlipXRef.current = stampFlipX;
  stampFlipYRef.current = stampFlipY;
  stampIsPresetRef.current  = stampIsPreset;
  inverseWarpKRef.current = inverseWarpK;
  inverseWarpStrengthRef.current = inverseWarpStrength;
  textContentRef.current = textContent;
  textFontRef.current = textFont;
  textFontSizeRef.current = textFontSize;
  textBoldRef.current = textBold;
  textItalicRef.current = textItalic;
  textJustifyRef.current = textJustify;
  textLetterSpacingRef.current = textLetterSpacing;
  textLineHeightRef.current = textLineHeight;
  fillFloodModeRef.current = fillFloodMode;
  fillToleranceRef.current = fillTolerance;
  layersRef.current = layers;
  selectedLayerIdRef.current = selectedLayerId;

  // Apply FOV to the perspective camera whenever the store value changes.
  // isOrtho disables the perspective camera so skip then; OrthoCamera component handles that mode.
  useEffect(() => {
    const cam = cameraRef.current;
    if (!cam || isOrtho) return;
    const pc = cam as THREE.PerspectiveCamera;
    pc.fov = cameraFov;
    pc.updateProjectionMatrix();
    invalidateRef.current();
  }, [cameraFov, isOrtho]);
  useEffect(() => {
    if (selectedLayerId) {
      bumpTexture();
    }
  }, [selectedLayerId, bumpTexture]);

  // When text parameters change, immediately refresh the ghost preview.
  useEffect(() => {
    if (toolModeRef.current === 'text' && lastStampHitPoint.current) {
      renderTextPreview(lastStampHitPoint.current);
    }
  // renderTextPreview reads text settings from refs; stable exclusion.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [textContent, textFont, textFontSize, textBold, textItalic, textJustify, textLetterSpacing, textLineHeight]);

  // Sync strokeMaterial opacity when brushOpacity changes from store.
  useEffect(() => {
    if (strokeMaterialRef.current) {
      // Blur mode: hide the stroke preview (the brush marks would show as white
      // dots which are misleading — the actual effect only bakes at pointer-up).
      if (toolModeRef.current === 'blur') {
        strokeMaterialRef.current.opacity = 0;
      } else {
        strokeMaterialRef.current.opacity = toolModeRef.current === 'erase' ? brushOpacity * 0.5 : brushOpacity;
      }
      strokeMaterialRef.current.needsUpdate = true;
    }
  }, [brushOpacity]);

  // When the stamp image changes (e.g. switching from a pasted image to a preset),
  // dispose the old CanvasTexture so it is recreated fresh with the correct
  // dimensions next time it is used.  Reusing a CanvasTexture across image-dimension
  // changes can leave stale GPU data visible.
  useEffect(() => {
    if (stampTexture.current) {
      stampTexture.current.dispose();
      stampTexture.current = null;
    }
  }, [stampImage]);


  const colorParseContext = useRef<CanvasRenderingContext2D | null>(null);
  const getRgbFromCssColor = (css: string) => {
    // Use a tiny canvas to parse any valid CSS color string.
    if (!colorParseContext.current) {
      const canvas = document.createElement('canvas');
      canvas.width = 1;
      canvas.height = 1;
      colorParseContext.current = canvas.getContext('2d');
    }
    const ctx = colorParseContext.current;
    if (!ctx) return { r: 0, g: 0, b: 0, a: 1 };
    ctx.clearRect(0, 0, 1, 1);
    ctx.fillStyle = css;
    ctx.fillRect(0, 0, 1, 1);
    const data = ctx.getImageData(0, 0, 1, 1).data;
    const r = data[0];
    const g = data[1];
    const b = data[2];
    const a = data[3];
    return { r, g, b, a: a / 255 };
  };

  const fillSphereGradient = () => {
    pushSnapshot();
    const canvas = textureCanvas.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;

    const w = canvas.width;
    const h = canvas.height;
    let fillStyle: string | CanvasGradient = '#000000';

    // AntD ColorPicker gradient mode: color object has getColors() returning
    // an array of { color: AntDColor, percent: number } sorted 0..100.
    if (pickerValue && typeof pickerValue.getColors === 'function') {
      const stops: Array<{ color: any; percent: number }> = pickerValue.getColors();
      if (stops.length >= 2) {
        const grad = ctx.createLinearGradient(0, 0, 0, h);
        for (const s of stops) {
          const t = Math.min(1, Math.max(0, (s.percent ?? 0) / 100));
          const hex = typeof s.color.toHexString === 'function'
            ? s.color.toHexString()
            : '#000000';
          grad.addColorStop(t, hex);
        }
        fillStyle = grad;
      } else if (stops.length === 1) {
        fillStyle = typeof stops[0].color.toHexString === 'function'
          ? stops[0].color.toHexString()
          : '#000000';
      }
    } else if (typeof pickerValue === 'string' && !pickerValue.startsWith('linear-gradient') && !pickerValue.startsWith('radial-gradient')) {
      fillStyle = pickerValue;
    } else if (pickerValue && typeof pickerValue.toHexString === 'function') {
      fillStyle = pickerValue.toHexString();
    } else {
      fillStyle = brushColor;
    }

    ctx.clearRect(0, 0, w, h);
    ctx.fillStyle = fillStyle;
    ctx.fillRect(0, 0, w, h);

    // Push the fill into the active layer's RT only (not a global overwrite).
    const gl = glRef.current;
    const selectedId = selectedLayerIdRef.current;
    if (gl && selectedId && copyScene.current && copyMaterial.current && orthoCamera.current && textureRef.current) {
      textureRef.current.needsUpdate = true;
      copyMaterial.current.uniforms.uTex.value = textureRef.current;
      const lRT = layerRTs.current.get(selectedId);
      if (lRT) {
        gl.setRenderTarget(lRT.a);
        gl.render(copyScene.current, orthoCamera.current);
        gl.setRenderTarget(lRT.b);
        gl.render(copyScene.current, orthoCamera.current);
        gl.setRenderTarget(null);
        lRT.active = 'a';
      }
      flattenLayers();
    }
    isDirtyRef.current = true;
    autosaveNotifyDirty();
    if (selectedId) updateLayer(selectedId, { lastDupMode: dupModeRef.current });
    if (selectedId) markLayersDirty(selectedId);
    scheduleBump(() => {
      updateCanvasFromRenderTarget();
      const activeId = selectedLayerIdRef.current;
      if (activeId) generateLayerThumbnail(activeId);
    });
  };

  const floodFillAtPoint = (worldPoint: THREE.Vector3) => {
    const gl = glRef.current;
    const selectedId = selectedLayerIdRef.current;
    if (!gl || !selectedId || !copyScene.current || !copyMaterial.current || !orthoCamera.current || !textureRef.current) return;
    const canvas = textureCanvas.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;

    const w = canvas.width;
    const h = canvas.height;

    // Read the current layer's pixels from the GPU.
    const lRT = layerRTs.current.get(selectedId);
    if (!lRT) return;
    const activeRT = lRT[lRT.active];
    const bufLen = w * h * 4;
    if (!readPixelBufferRef.current || readPixelBufferRef.current.length < bufLen) {
      readPixelBufferRef.current = new Uint8Array(bufLen);
    }
    const buf = readPixelBufferRef.current;
    gl.readRenderTargetPixels(activeRT, 0, 0, w, h, buf);

    // WebGL returns pixels bottom-up; flip to top-down for the fill algorithm.
    flipBufferY(buf, w, h);

    // Map the world point to texture pixel coordinates.
    const uv = worldPointToUV(worldPoint);
    const seedX = Math.floor(uv.x * w);
    // worldPointToUV returns v=0 at the north pole (top); the flipped buffer is also top-down,
    // so the row index is v * h (no inversion).
    const seedY = Math.floor(uv.y * h);

    // Resolve fill color from the picker (first stop of gradient, or solid color).
    let fillHex = '#000000';
    const pv = pickerValueRef.current;
    if (pv && typeof pv.getColors === 'function') {
      const stops: Array<{ color: any; percent: number }> = pv.getColors();
      if (stops.length > 0) {
        fillHex = typeof stops[0].color.toHexString === 'function'
          ? stops[0].color.toHexString()
          : '#000000';
      }
    } else if (typeof pv === 'string' && /^#/.test(pv)) {
      fillHex = pv;
    } else if (pv && typeof pv.toHexString === 'function') {
      fillHex = pv.toHexString();
    } else {
      fillHex = brushColorRef.current;
    }
    const r = parseInt(fillHex.slice(1, 3), 16);
    const g = parseInt(fillHex.slice(3, 5), 16);
    const b = parseInt(fillHex.slice(5, 7), 16);

    floodFill(
      buf, w, h,
      seedX, seedY,
      r, g, b, 255,
      fillToleranceRef.current,
      fillFloodModeRef.current,
    );

    // buf is still top-down (same orientation as the canvas) — do NOT flip again.
    // Three.js CanvasTexture uploads with flipY=true, which converts top-down → WebGL bottom-up.
    ctx.putImageData(new ImageData(new Uint8ClampedArray(buf), w, h), 0, 0);
    textureRef.current.needsUpdate = true;
    copyMaterial.current.uniforms.uTex.value = textureRef.current;
    gl.setRenderTarget(lRT.a);
    gl.render(copyScene.current, orthoCamera.current);
    gl.setRenderTarget(lRT.b);
    gl.render(copyScene.current, orthoCamera.current);
    gl.setRenderTarget(null);
    lRT.active = 'a';
    flattenLayers();

    isDirtyRef.current = true;
    autosaveNotifyDirty();
    if (selectedId) updateLayer(selectedId, { lastDupMode: dupModeRef.current });
    if (selectedId) markLayersDirty(selectedId);
    scheduleBump(() => {
      updateCanvasFromRenderTarget();
      const activeId = selectedLayerIdRef.current;
      if (activeId) generateLayerThumbnail(activeId);
    });
  };

  const clearLayerGradient = () => {
    pushSnapshot();
    const gl = glRef.current;
    const selectedId = selectedLayerIdRef.current;
    if (!gl || !selectedId || !copyScene.current || !copyMaterial.current || !orthoCamera.current || !textureRef.current) return;
    const canvas = textureCanvas.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    textureRef.current.needsUpdate = true;
    copyMaterial.current.uniforms.uTex.value = textureRef.current;
    const lRT = layerRTs.current.get(selectedId);
    if (lRT) {
      gl.setRenderTarget(lRT.a);
      gl.render(copyScene.current, orthoCamera.current);
      gl.setRenderTarget(lRT.b);
      gl.render(copyScene.current, orthoCamera.current);
      gl.setRenderTarget(null);
      lRT.active = 'a';
    }
    isDirtyRef.current = true;
    autosaveNotifyDirty();
    if (selectedId) updateLayer(selectedId, { lastDupMode: dupModeRef.current });
    if (selectedId) markLayersDirty(selectedId);
    flattenLayers();
    scheduleBump(() => {
      updateCanvasFromRenderTarget();
      if (selectedId) generateLayerThumbnail(selectedId);
    });
  };

  const adjustBrushSize = (factor: number) => {
    const next = Math.min(BRUSH_MAX, Math.max(BRUSH_MIN, brushSize * factor));
    setBrushSize(next);
  };
  adjustBrushSizeRef.current = adjustBrushSize;

  // Keep a stable ref so the [] useEffect below can call the latest version
  const autosaveNotifyDirtyRef = useRef(autosaveNotifyDirty);
  autosaveNotifyDirtyRef.current = autosaveNotifyDirty;
  const autosaveCancelPendingRef = useRef(autosaveCancelPending);
  autosaveCancelPendingRef.current = autosaveCancelPending;

  // ── Stamp pipeline ────────────────────────────────────────────────────────
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stampCtx = useMemo(() => ({
    glRef, cameraRef, orthoCamera, stampMaterial, stampScene, stampTexture,
    strokeRenderTargetA, strokeRenderTargetB, transparentBaseRT, strokeMaterialRef,
    invalidateRef, stampSizeRef, stampProjectionModeRef, stampIsPresetRef, stampRotationRef,
    stampFlipXRef, stampFlipYRef, brushColorRef, ctrlHeldRef, pickerValueRef, brushOpacityRef,
    textStampTextureRef, textContentRef, textFontRef, textFontSizeRef, textBoldRef, textItalicRef,
    textJustifyRef, textLetterSpacingRef, textLineHeightRef, textCanvasCacheRef, textCanvasCacheKeyRef,
    lastStampHitPoint, selectedLayerIdRef, layerRTs, layersRef, dupModeRef,
    isDirtyRef, autosaveNotifyDirtyRef, flattenLayersRef, scheduleBumpRef,
    generateLayerThumbnailRef, markLayersDirtyRef,
  }), []); // all entries are stable refs — no deps needed
  const {
    buildTextCanvas, setStampUniforms, renderStampPreview, clearStampPreview,
    renderTextPreview, applyTextStamp, applyStamp,
  } = useStampPipeline(stampCtx);
  renderStampPreviewRef.current = renderStampPreview;
  renderTextPreviewRef.current  = renderTextPreview;

  // ── Unsaved-changes guard ─────────────────────────────────────────────────
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirtyRef.current) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    const unsubStore = useAppStore.subscribe((state, prev) => {
      if (isResettingRef.current) return;
      if (
        state.layers !== prev.layers ||
        state.layerKeyframes !== prev.layerKeyframes ||
        state.fps !== prev.fps ||
        state.totalFrames !== prev.totalFrames
      ) {
        isDirtyRef.current = true;
        autosaveNotifyDirtyRef.current();
      }
    });
    // Spin up the image-encoder worker. All Y-flip + WebP work runs off the main thread.
    const worker = new Worker(new URL('../workers/imageEncoder.worker.ts', import.meta.url));
    worker.onmessage = (evt: MessageEvent) => {
      const { id } = evt.data as { id: string };
      const cb = workerCallbacksRef.current.get(id);
      if (cb) {
        workerCallbacksRef.current.delete(id);
        cb(evt.data);
      }
    };
    imageWorkerRef.current = worker;

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      unsubStore();
      // Revoke all thumbnail blob URLs on unmount to avoid memory leaks.
      thumbnailBlobUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
      thumbnailBlobUrlsRef.current.clear();
      // Terminate the encoder worker.
      worker.terminate();
      imageWorkerRef.current = null;
    };
  }, []);

  // Sync layer render-targets with the Zustand layers array:
  // create RTs for new layers, dispose RTs for removed layers, and
  // re-flatten whenever opacity / visibility / order changes.
  useEffect(() => {
    if (!glRef.current || !compositeMaterial.current) return;
    ensureLayerRTs(layers);
    // Dispose RTs for layers that have been removed.
    const currentIds = new Set(layers.map((l) => l.id));
    layerRTs.current.forEach((lRT, id) => {
      if (!currentIds.has(id)) {
        lRT.a.dispose();
        lRT.b.dispose();
        layerRTs.current.delete(id);
        // Revoke the thumbnail blob URL for this layer so the browser can free memory.
        const blobUrl = thumbnailBlobUrlsRef.current.get(id);
        if (blobUrl) { URL.revokeObjectURL(blobUrl); thumbnailBlobUrlsRef.current.delete(id); }
      }
    });
    flattenLayers();
    layers.forEach(l => layerCacheDirtyRef.current.add(l.id));
    scheduleEncoder();
    scheduleBump(() => {
      updateCanvasFromRenderTarget();
    });
  // ensureLayerRTs and scheduleBump are plain fns reading only refs; stable exclusion.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layers, flattenLayers]);

  // Clear fill preview when switching away from fill mode.
  useEffect(() => {
    if (toolMode !== 'fill') {
      lastFillHoverPointRef.current = null;
      if (fillPreviewCanvasRef.current && fillPreviewTextureRef.current) {
        const ctx = fillPreviewCanvasRef.current.getContext('2d');
        if (ctx) ctx.clearRect(0, 0, fillPreviewCanvasRef.current.width, fillPreviewCanvasRef.current.height);
        fillPreviewTextureRef.current.needsUpdate = true;
        invalidateRef.current();
      }
    }
  }, [toolMode]);

  // Re-flatten when the current frame changes to reflect layer timing-bar
  // visibility (frameStart / frameEnd). Layers with no keyframes never change
  // their `layers` reference, so the effect above would not fire on frame change.
  useEffect(() => {
    flattenLayers();
  // flattenLayers is stable (empty useCallback deps); eslint disabled for clarity.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentFrame]);

  // When a merge-down request arrives: GPU-composite the top layer's content
  // (with its transforms and opacity) onto the bottom layer's RT, then remove
  // the top layer from the store.
  useEffect(() => {
    if (!pendingMergeDown) return;
    pushSnapshot();
    const { topId, bottomId } = pendingMergeDown;
    const gl  = glRef.current;
    if (!gl || !compositeMaterial.current || !compositeScene.current ||
        !orthoCamera.current || !singleLayerDisplayRT.current) {
      clearMergeRequest();
      return;
    }
    const bottomRTData = layerRTs.current.get(bottomId);
    if (!bottomRTData) { clearMergeRequest(); return; }

    // Pass 1: render the top layer in world UV into singleLayerDisplayRT
    // (honours top layer transform + opacity).  Returns null if top RT missing.
    const worldRT = buildSingleLayerDisplay(topId);
    if (!worldRT) { clearMergeRequest(); return; }

    // Pass 2: bake the world-UV result onto the bottom layer's local-UV RT.
    // uBakeMode=2 = bake-forward transform + straight-alpha source.
    const bottomLayer = layersRef.current.find((l) => l.id === bottomId);
    const srcRT    = bottomRTData.active === 'a' ? bottomRTData.a : bottomRTData.b;
    const dstRT    = bottomRTData.active === 'a' ? bottomRTData.b : bottomRTData.a;

    compositeMaterial.current.uniforms.uBase.value       = srcRT.texture;
    compositeMaterial.current.uniforms.uStroke.value     = worldRT.texture;
    compositeMaterial.current.uniforms.uOpacity.value    = 1.0;  // already applied in pass 1
    compositeMaterial.current.uniforms.uMode.value       = 0;
    compositeMaterial.current.uniforms.uBakeMode.value   = 2;    // bake + straight source
    compositeMaterial.current.uniforms.uRotInv.value     = bottomLayer
      ? _scratchMatrix3.setFromMatrix4(buildEffectiveLayerTransform(bottomLayer, layersRef.current))
      : _identityMatrix3;
    compositeMaterial.current.uniforms.uTranslateY.value = bottomLayer ? getEffectiveTranslateY(bottomLayer, layersRef.current) : 0;

    gl.setRenderTarget(dstRT);
    gl.render(compositeScene.current, orthoCamera.current);
    gl.setRenderTarget(null);

    bottomRTData.active = bottomRTData.active === 'a' ? 'b' : 'a';

    // Update store: remove top layer; keep selection on the bottom layer.
    const wasSelected = selectedLayerIdRef.current === topId;
    removeLayer(topId);
    if (wasSelected) selectLayer(bottomId);
    clearMergeRequest();

    generateLayerThumbnail(bottomId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingMergeDown, buildSingleLayerDisplay, generateLayerThumbnail, removeLayer, selectLayer, clearMergeRequest]);

  const pointBuffer = useRef<THREE.Vector3[]>([]);
  const lastSplinePoint = useRef<THREE.Vector3 | null>(null);
  const strokeSpineRef = useRef<{ point: THREE.Vector3; pressure: number }[]>([]);

  // Reusable analytical unit sphere for hit testing.
  // Using ray.intersectSphere instead of mesh triangle tests avoids silhouette-edge
  // misses in ortho mode where rays arrive nearly tangent to the surface.
  const _hitSphere = new THREE.Sphere(new THREE.Vector3(0, 0, 0), 1);
  const _hitPoint  = new THREE.Vector3();

  const getHitData = (
    evt: { clientX: number; clientY: number }
  ): { uv: { x: number; y: number }; point: THREE.Vector3 } | null => {
    if (!cameraRef.current || !glRef.current) return null;
    const rect = glRef.current.domElement.getBoundingClientRect();
    const mouse = new THREE.Vector2(
      ((evt.clientX - rect.left) / rect.width) * 2 - 1,
      -((evt.clientY - rect.top) / rect.height) * 2 + 1
    );
    raycaster.setFromCamera(mouse, cameraRef.current);
    if (!raycaster.ray.intersectSphere(_hitSphere, _hitPoint)) return null;
    // Derive equirectangular UV matching THREE.SphereGeometry's layout:
    //   x = -cos(phi)*sin(theta),  y = cos(theta),  z = sin(phi)*sin(theta)
    //   phi  = atan2(z, -x)  →  u ∈ [0, 1)
    //   theta = acos(y)       →  v ∈ [0, 1]
    const { x, y, z } = _hitPoint;
    const phi = Math.atan2(z, -x);
    const u   = phi < 0 ? 1 + phi / (2 * Math.PI) : phi / (2 * Math.PI);
    const v   = Math.acos(THREE.MathUtils.clamp(y, -1, 1)) / Math.PI;
    const w = texResRef.current.w;
    const h = texResRef.current.h;
    return {
      uv: { x: u * w, y: (1 - v) * h },
      point: _hitPoint.clone(),
    };
  };

  const updateCanvasFromRenderTarget = () => {
    // Only do full-res readbacks when the UV panel is actually visible.
    if (!useAppStore.getState().rightPanelOpen) return;

    const gl = glRef.current;
    const rt = activeRenderTarget.current;
    const canvas = textureCanvas.current;
    if (!gl || !rt || !canvas) return;

    const w = canvas.width;
    const h = canvas.height;
    const needed = w * h * 4;
    if (!readPixelBufferRef.current || readPixelBufferRef.current.length !== needed)
      readPixelBufferRef.current = new Uint8Array(needed);
    const buffer = readPixelBufferRef.current;

    try {
      gl.readRenderTargetPixels(rt, 0, 0, w, h, buffer);
    } catch {
      // Some environments don't allow readback; ignore.
      return;
    }

    const worker = imageWorkerRef.current;
    if (!worker) return;

    // Transfer a copy of the pixel data to the worker. The shared ref is safe to
    // reuse immediately since the ArrayBuffer copy is what gets transferred.
    const fullBuf = new Uint8ClampedArray(buffer).buffer;
    const fullId = crypto.randomUUID();
    workerCallbacksRef.current.set(fullId, (msg) => {
      if (msg.error || !msg.bitmap) return;
      const ctx = canvas.getContext('2d');
      if (!ctx) { (msg.bitmap as ImageBitmap).close(); return; }
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(msg.bitmap as ImageBitmap, 0, 0);
      (msg.bitmap as ImageBitmap).close();
      setUvFullCanvas(canvas);
      useAppStore.getState().bumpUvCanvas();
    });
    worker.postMessage({ id: fullId, kind: 'uv', subtype: 'full', buffer: fullBuf, width: w, height: h }, [fullBuf]);
  };

  const copyCanvasToRenderTargets = () => {
    const gl = glRef.current;
    if (!gl || !copyScene.current || !copyMaterial.current || !orthoCamera.current) return;

    if (textureRef.current) {
      textureRef.current.needsUpdate = true;
      copyMaterial.current.uniforms.uTex.value = textureRef.current;
    }

    // History snapshots capture the full flatten.  Restore into the bottom
    // (oldest) layer and clear all others so re-flattening reproduces the
    // snapshot exactly without double-compositing other layers.
    const currentLayers = layersRef.current;
    const bottomLayerId =
      currentLayers.length > 0 ? currentLayers[currentLayers.length - 1].id : null;

    layerRTs.current.forEach((lRT, id) => {
      if (id === bottomLayerId) {
        gl.setRenderTarget(lRT.a);
        gl.render(copyScene.current!, orthoCamera.current!);
        gl.setRenderTarget(lRT.b);
        gl.render(copyScene.current!, orthoCamera.current!);
        gl.setRenderTarget(null);
        lRT.active = 'a';
      } else {
        gl.setRenderTarget(lRT.a);
        gl.clearColor(0, 0, 0, 0);
        gl.clear(gl.COLOR_BUFFER_BIT);
        gl.setRenderTarget(lRT.b);
        gl.clearColor(0, 0, 0, 0);
        gl.clear(gl.COLOR_BUFFER_BIT);
        gl.setRenderTarget(null);
        lRT.active = 'a';
      }
    });

    flattenLayers();
  };

  const pushHistory = () => {
    // Legacy alias removed — push-before semantics used everywhere.
    // This stub exists only for the call at the bottom of initializeTexture
    // which is separately cleaned up.
  };

  const applyHistory = (_index: number) => {
    // Removed — unified undo uses undoStack/redoStack directly.
  };

  // ── Unified undo / redo ────────────────────────────────────────────────────
  /**
   * Push-before snapshot: call BEFORE any mutation to save the current world
   * state.  Clears the redo stack so the new branch replaces the old one.
   */
  const pushSnapshot = () => {
    const entry = captureLayerSnapshot();
    // Dispose entries evicted from the front of the undo stack.
    const overflow = undoStack.current.slice(0, Math.max(0, undoStack.current.length - (MAX_HISTORY - 1)));
    overflow.forEach(disposeLayerHistoryEntry);
    undoStack.current = [...undoStack.current.slice(-(MAX_HISTORY - 1)), entry];
    // Invalidate (and free GPU snapshot RTs for) the now-stale redo stack.
    redoStack.current.forEach(disposeLayerHistoryEntry);
    redoStack.current = [];
  };

  clearHistoryRef.current = () => {
    undoStack.current.forEach(disposeLayerHistoryEntry);
    redoStack.current.forEach(disposeLayerHistoryEntry);
    undoStack.current = [];
    redoStack.current = [];
  };

  const undoRef = useRef<() => void>(() => {});
  const redoRef = useRef<() => void>(() => {});

  const undo = () => {
    if (isLayerOpInProgress.current) return;
    const target = undoStack.current[undoStack.current.length - 1];
    if (!target) return;
    const current = captureLayerSnapshot();
    undoStack.current = undoStack.current.slice(0, -1);
    // Dispose redo entries that fall off the stack.
    const redoOverflow = redoStack.current.slice(0, Math.max(0, redoStack.current.length - (MAX_HISTORY - 1)));
    redoOverflow.forEach(disposeLayerHistoryEntry);
    redoStack.current = [...redoStack.current.slice(-(MAX_HISTORY - 1)), current];
    isLayerOpInProgress.current = true;
    restoreLayerEntry(target).finally(() => {
      disposeLayerHistoryEntry(target);
      isLayerOpInProgress.current = false;
    });
  };

  const redo = () => {
    if (isLayerOpInProgress.current) return;
    const target = redoStack.current[redoStack.current.length - 1];
    if (!target) return;
    const current = captureLayerSnapshot();
    redoStack.current = redoStack.current.slice(0, -1);
    // Dispose undo entries that fall off the stack.
    const undoOverflow = undoStack.current.slice(0, Math.max(0, undoStack.current.length - (MAX_HISTORY - 1)));
    undoOverflow.forEach(disposeLayerHistoryEntry);
    undoStack.current = [...undoStack.current.slice(-(MAX_HISTORY - 1)), current];
    isLayerOpInProgress.current = true;
    restoreLayerEntry(target).finally(() => {
      disposeLayerHistoryEntry(target);
      isLayerOpInProgress.current = false;
    });
  };

  // When switching away from stamp/text mode, clear any lingering ghost preview.
  // When switching away from draw/erase mode, hide the brush cursor.
  useEffect(() => {
    if (toolMode !== 'stamp' && toolMode !== 'text') {
      clearStampPreview();
    }
    if (toolMode !== 'draw' && toolMode !== 'erase') {
      if (brushCursorElemRef.current) brushCursorElemRef.current.style.display = 'none';
      if (blurInnerCursorElemRef.current) blurInnerCursorElemRef.current.style.display = 'none';
    }
    if (toolMode !== 'draw' && toolMode !== 'erase' && toolMode !== 'stamp' && toolMode !== 'text') {
      if (dupModeCursorRef.current) dupModeCursorRef.current.style.display = 'none';
    }
  // clearStampPreview reads only refs; stable exclusion.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toolMode]);

  undoRef.current = undo;
  redoRef.current = redo;
  viewportActions.undo.current = undo;
  viewportActions.redo.current = redo;
  viewportActions.fill.current = fillSphereGradient;
  viewportActions.clearLayer.current = clearLayerGradient;
  viewportActions.newProject.current = () => {
    isResettingRef.current = true;
    newProject();
    autosaveResetToClean();
    setTimeout(() => { isResettingRef.current = false; }, 0);
  };
  viewportActions.pushSnapshot.current = pushSnapshot;
  viewportActions.pushLayerSnapshot.current = pushSnapshot;

  // ── UV-viewport drawing actions ───────────────────────────────────────────
  // These are called from UVPanel when it is the main viewport. They translate
  // UV [0,1]² mouse coordinates into sphere world-points and feed the same
  // WebGL brush pipeline that the 3-D viewport uses.
  viewportActions.uvPaintStart.current = (u: number, v: number, rawPressure: number) => {
    autosaveCancelPendingRef.current();
    cancelEncoder();
    const gl = glRef.current;
    const currentStrokeRT = activeStrokeRenderTarget.current;
    if (!gl || !currentStrokeRT) return;
    pushSnapshot();
    setIsDrawing(true); isDrawingRef.current = true;
    setIsDrawingActive(true);
    pressureRef.current = usePressureRef.current
      ? simPressureRef.current
        ? 0.5 + 0.5 * Math.sin(Date.now() / (2000 - simPressureRandomnessRef.current * 1950))
        : rawPressure
      : 1.0;
    // Clear the stroke RT so only the new stroke is shown.
    gl.setRenderTarget(currentStrokeRT);
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.setRenderTarget(null);
    const worldPt = uvToWorldPoint(u, v);
    prevHitPoint.current = worldPt;
    pointBuffer.current = [worldPt.clone()];
    lastSplinePoint.current = worldPt.clone();
    strokeSpineRef.current = [{ point: worldPt.clone(), pressure: pressureRef.current }];
    paintSphereBrush(worldPt, pressureRef.current);
    // Immediately seed the live canvas so UVPanel preview shows the first dot.
    if (!strokeLiveRafPendingRef.current) {
      strokeLiveRafPendingRef.current = true;
      requestAnimationFrame(() => {
        strokeLiveRafPendingRef.current = false;
        const glInner = glRef.current;
        const rtInner = activeStrokeRenderTarget.current;
        if (!glInner || !rtInner) return;
        const { w, h } = texResRef.current;
        const needed = w * h * 4;
        if (!strokeLivePixelBufRef.current || strokeLivePixelBufRef.current.length !== needed)
          strokeLivePixelBufRef.current = new Uint8Array(needed);
        try { glInner.readRenderTargetPixels(rtInner, 0, 0, w, h, strokeLivePixelBufRef.current); } catch { return; }
        if (!strokeLiveCanvasRef.current) {
          const sc = document.createElement('canvas');
          sc.width = w; sc.height = h;
          strokeLiveCanvasRef.current = sc;
        }
        // Always re-sync — UVPanel’s useEffect can null this after a store update.
        viewportActions.strokeLiveCanvas.current = strokeLiveCanvasRef.current;
        const ctx = strokeLiveCanvasRef.current.getContext('2d');
        if (!ctx) return;
        const img = ctx.createImageData(w, h);
        const buf = strokeLivePixelBufRef.current;
        for (let y = 0; y < h; y++) {
          const srcRow = h - 1 - y;
          img.data.set(buf.subarray(srcRow * w * 4, (srcRow + 1) * w * 4), y * w * 4);
        }
        ctx.putImageData(img, 0, 0);
        viewportActions.notifyUvStrokeLive.current();
      });
    }
  };

  viewportActions.uvPaintMove.current = (u: number, v: number, rawPressure: number) => {
    if (!isDrawingRef.current || !prevHitPoint.current) return;
    if (usePressureRef.current && !simPressureRef.current && rawPressure > 0)
      pressureRef.current = rawPressure;
    const worldPt = uvToWorldPoint(u, v);
    pointBuffer.current.push(worldPt.clone());
    if (pointBuffer.current.length > 4) pointBuffer.current.shift();
    strokeSpineRef.current.push({ point: worldPt.clone(), pressure: pressureRef.current });
    paintSphereLine(prevHitPoint.current, worldPt, pressureRef.current);
    prevHitPoint.current = worldPt;
    lastSplinePoint.current = worldPt.clone();

    // Throttled live stroke canvas update so UVPanel can show in-progress stroke.
    if (!strokeLiveRafPendingRef.current) {
      strokeLiveRafPendingRef.current = true;
      requestAnimationFrame(() => {
        strokeLiveRafPendingRef.current = false;
        const gl = glRef.current;
        const rt = activeStrokeRenderTarget.current;
        if (!gl || !rt) return;
        const { w, h } = texResRef.current;
        const needed = w * h * 4;
        if (!strokeLivePixelBufRef.current || strokeLivePixelBufRef.current.length !== needed)
          strokeLivePixelBufRef.current = new Uint8Array(needed);
        try { gl.readRenderTargetPixels(rt, 0, 0, w, h, strokeLivePixelBufRef.current); } catch { return; }
        if (!strokeLiveCanvasRef.current) {
          const sc = document.createElement('canvas');
          sc.width = w; sc.height = h;
          strokeLiveCanvasRef.current = sc;
        }
        // Always re-sync — UVPanel’s useEffect can null this after a store update.
        viewportActions.strokeLiveCanvas.current = strokeLiveCanvasRef.current;
        const ctx = strokeLiveCanvasRef.current.getContext('2d');
        if (!ctx) return;
        const img = ctx.createImageData(w, h);
        const buf = strokeLivePixelBufRef.current;
        for (let y = 0; y < h; y++) {
          const srcRow = h - 1 - y;
          img.data.set(buf.subarray(srcRow * w * 4, (srcRow + 1) * w * 4), y * w * 4);
        }
        ctx.putImageData(img, 0, 0);
        viewportActions.notifyUvStrokeLive.current();
      });
    }
  };

  viewportActions.uvPaintEnd.current = () => {
    if (!isDrawingRef.current) return;
    setIsDrawing(false); isDrawingRef.current = false;
    setIsDrawingActive(false);
    const gl = glRef.current;
    const currentStrokeRT = activeStrokeRenderTarget.current;
    const lastStrokeRT = lastStrokeRenderTarget.current;
    // Copy stroke into the "last stroke" buffer for the UV panel's stroke-mode display.
    if (gl && currentStrokeRT && lastStrokeRT && copyScene.current && copyMaterial.current) {
      copyMaterial.current.uniforms.uTex.value = currentStrokeRT.texture;
      gl.setRenderTarget(lastStrokeRT);
      gl.render(copyScene.current, orthoCamera.current);
      gl.setRenderTarget(null);
    }
    // Commit: replace live dots with a clean SDF envelope, then bake into layer.
    commitEnvelopeFill(strokeSpineRef.current);
    if (toolModeRef.current === 'blur') {
      applyBlurStroke(brushOpacityRef.current);
    } else {
      updateSpherePreview(brushOpacityRef.current);
    }
    // Clear both stroke RTs.
    const envelopeRT = activeStrokeRenderTarget.current;
    for (const rtToClear of [currentStrokeRT, envelopeRT !== currentStrokeRT ? envelopeRT : null]) {
      if (gl && rtToClear) {
        gl.setRenderTarget(rtToClear);
        gl.clearColor(0, 0, 0, 0);
        gl.clear(gl.COLOR_BUFFER_BIT);
        gl.setRenderTarget(null);
      }
    }
    if (strokeMaterialRef.current) {
      strokeMaterialRef.current.map = currentStrokeRT ? currentStrokeRT.texture : null;
      strokeMaterialRef.current.needsUpdate = true;
    }
    isDirtyRef.current = true;
    autosaveNotifyDirty();
    const activeId = selectedLayerIdRef.current;
    if (activeId) markLayersDirty(activeId);
    scheduleBump(() => {
      updateCanvasFromRenderTarget();
      if (activeId) generateLayerThumbnail(activeId);
    });
  };

  // Synchronously read strokeRenderTargetA (preview RT) into strokeLiveCanvas
  // so UVPanel can display stamp/text hover previews without a rAF delay.
  const uvReadPreviewRT = () => {
    const gl = glRef.current;
    const rt = strokeRenderTargetA.current;
    if (!gl || !rt) return;
    const { w, h } = texResRef.current;
    const needed = w * h * 4;
    if (!strokeLivePixelBufRef.current || strokeLivePixelBufRef.current.length !== needed)
      strokeLivePixelBufRef.current = new Uint8Array(needed);
    try { gl.readRenderTargetPixels(rt, 0, 0, w, h, strokeLivePixelBufRef.current); } catch { return; }
    if (!strokeLiveCanvasRef.current) {
      const sc = document.createElement('canvas');
      sc.width = w; sc.height = h;
      strokeLiveCanvasRef.current = sc;
    }
    viewportActions.strokeLiveCanvas.current = strokeLiveCanvasRef.current;
    const ctx = strokeLiveCanvasRef.current.getContext('2d');
    if (!ctx) return;
    const img = ctx.createImageData(w, h);
    const buf = strokeLivePixelBufRef.current;
    for (let y = 0; y < h; y++) {
      const srcRow = h - 1 - y;
      img.data.set(buf.subarray(srcRow * w * 4, (srcRow + 1) * w * 4), y * w * 4);
    }
    ctx.putImageData(img, 0, 0);
  };

  viewportActions.uvStampPreviewMove.current = (u: number, v: number) => {
    const worldPt = uvToWorldPoint(u, v);
    if (useAppStore.getState().stampImage) {
      renderStampPreview(worldPt);
    } else {
      clearStampPreview();
    }
    uvReadPreviewRT();
    // Suppress the preview on the 3D sphere — only the UV canvas should show it.
    clearStampPreview();
  };

  viewportActions.uvStampApply.current = (u: number, v: number) => {
    const worldPt = uvToWorldPoint(u, v);
    pushSnapshot();
    applyStamp(worldPt);
    clearStampPreview();
    const activeId = selectedLayerIdRef.current;
    if (activeId) markLayersDirty(activeId);
    scheduleBump(() => {
      updateCanvasFromRenderTarget();
      if (activeId) generateLayerThumbnail(activeId);
    });
  };

  viewportActions.uvTextApply.current = (u: number, v: number) => {
    const worldPt = uvToWorldPoint(u, v);
    pushSnapshot();
    applyTextStamp(worldPt);
    clearStampPreview();
    const activeId = selectedLayerIdRef.current;
    if (activeId) markLayersDirty(activeId);
    scheduleBump(() => {
      updateCanvasFromRenderTarget();
      if (activeId) generateLayerThumbnail(activeId);
    });
  };

  viewportActions.uvTextPreviewMove.current = (u: number, v: number) => {
    const worldPt = uvToWorldPoint(u, v);
    renderTextPreview(worldPt);
    uvReadPreviewRT();
    // Suppress the preview on the 3D sphere — only the UV canvas should show it.
    clearStampPreview();
  };

  viewportActions.uvClearStampPreview.current = () => {
    clearStampPreview();
    // Clear strokeLiveCanvas so UVPanel stops showing the hover preview.
    if (strokeLiveCanvasRef.current) {
      const ctx = strokeLiveCanvasRef.current.getContext('2d');
      if (ctx) ctx.clearRect(0, 0, strokeLiveCanvasRef.current.width, strokeLiveCanvasRef.current.height);
    }
    viewportActions.strokeLiveCanvas.current = null;
    viewportActions.notifyUvStrokeLive.current();
  };

  // ── UV rotate / translate drag actions (used when UV is the primary viewport) ──
  viewportActions.uvRotateStart.current = (clientX: number, clientY: number) => {
    const selId = selectedLayerIdRef.current;
    const selLayer = layersRef.current.find((l) => l.id === selId);
    if (!selLayer) return;
    pushSnapshot();
    setIsRotating(true); isRotatingRef.current = true;
    rotDragStart.current = { x: clientX, y: clientY };
    rotDragBase.current = { rx: selLayer.rotate.x, ry: selLayer.rotate.y, rz: selLayer.rotate.z };
  };

  viewportActions.uvRotateMove.current = (clientX: number, clientY: number) => {
    if (!isRotatingRef.current) return;
    const selId = selectedLayerIdRef.current;
    if (!selId || !rotDragStart.current || !rotDragBase.current) return;
    const dx = clientX - rotDragStart.current.x;
    const dy = clientY - rotDragStart.current.y;
    const DEG_PER_PX = 0.5;
    const newRy = rotDragBase.current.ry + dx * DEG_PER_PX;
    const newRx = rotDragBase.current.rx + dy * DEG_PER_PX;
    updateLayer(selId, { rotate: { x: newRx, y: newRy, z: rotDragBase.current.rz } });
    const layer = layersRef.current.find((l) => l.id === selId);
    if (layer) { layer.rotate = { x: newRx, y: newRy, z: rotDragBase.current.rz }; }
    flattenLayers();
    scheduleBump();
  };

  viewportActions.uvRotateEnd.current = () => {
    if (!isRotatingRef.current) return;
    setIsRotating(false); isRotatingRef.current = false;
    const base = rotDragBase.current;
    rotDragStart.current = null;
    rotDragBase.current = null;
    const selId = selectedLayerIdRef.current;
    if (selId) {
      if (autoKey !== 0 && base) {
        const finalLayer = layersRef.current.find((l) => l.id === selId);
        if (finalLayer) {
          maybeAutoKeyBatch({
            layerId: selId,
            props: (['rotateX', 'rotateY', 'rotateZ'] as const).map((prop) => ({
              prop,
              value: finalLayer.rotate[prop.replace('rotate', '').toLowerCase() as 'x'|'y'|'z'],
            })),
            autoKey,
            currentFrame,
            layerKeyframes,
            setKeyframe,
          });
        }
      }
      markLayersDirty(selId);
      scheduleBump(() => {
        updateCanvasFromRenderTarget();
      });
    }
  };

  viewportActions.uvTranslateStart.current = (clientX: number, clientY: number) => {
    const selId = selectedLayerIdRef.current;
    const selLayer = layersRef.current.find((l) => l.id === selId);
    if (!selLayer) return;
    pushSnapshot();
    setIsTranslating(true); isTranslatingRef.current = true;
    transDragStart.current = { x: clientX, y: clientY };
    transDragBase.current = { tx: selLayer.translate.x, ty: selLayer.translate.y };
  };

  viewportActions.uvTranslateMove.current = (clientX: number, clientY: number, refWidth: number) => {
    if (!isTranslatingRef.current) return;
    const selId = selectedLayerIdRef.current;
    if (!selId || !transDragStart.current || !transDragBase.current) return;
    // refWidth is the canvas element's display width in CSS pixels (full width = 1.0 UV unit).
    const newTx = transDragBase.current.tx + (clientX - transDragStart.current.x) / refWidth;
    const newTy = transDragBase.current.ty - (clientY - transDragStart.current.y) / (refWidth * 0.5);
    updateLayer(selId, { translate: { x: newTx, y: newTy } });
    const layer = layersRef.current.find((l) => l.id === selId);
    if (layer) { layer.translate = { x: newTx, y: newTy }; }
    flattenLayers();
    scheduleBump();
  };

  viewportActions.uvTranslateEnd.current = () => {
    if (!isTranslatingRef.current) return;
    setIsTranslating(false); isTranslatingRef.current = false;
    const base = transDragBase.current;
    transDragStart.current = null;
    transDragBase.current = null;
    const selId = selectedLayerIdRef.current;
    if (selId) {
      if (autoKey !== 0 && base) {
        const finalLayer = layersRef.current.find((l) => l.id === selId);
        if (finalLayer) {
          maybeAutoKeyBatch({
            layerId: selId,
            props: (['translateX', 'translateY'] as const).map((prop) => ({
              prop,
              value: finalLayer.translate[prop === 'translateX' ? 'x' : 'y'],
            })),
            autoKey,
            currentFrame,
            layerKeyframes,
            setKeyframe,
          });
        }
      }
      markLayersDirty(selId);
      scheduleBump(() => {
        updateCanvasFromRenderTarget();
      });
    }
  };

  // ── Clipboard paste actions ───────────────────────────────────────────────
  viewportActions.pasteClipboard.current = async () => {
    try {
      const items = await navigator.clipboard.read();
      for (const item of items) {
        const imageType = item.types.find((t: string) => t.startsWith('image/'));
        if (!imageType) continue;
        const blob = await item.getType(imageType);
        const url = URL.createObjectURL(blob);
        const img = await new Promise<HTMLImageElement>((resolve, reject) => {
          const el = new Image();
          el.onload  = () => resolve(el);
          el.onerror = reject;
          el.src = url;
        });
        URL.revokeObjectURL(url);

        const c = document.createElement('canvas');
        c.width  = img.naturalWidth;
        c.height = img.naturalHeight;
        c.getContext('2d')!.drawImage(img, 0, 0);

        const { w, h } = texResRef.current;
        const fitScale = Math.min(w / img.naturalWidth, h / img.naturalHeight);

        const st = useAppStore.getState();
        st.setClipboardPasteCanvas(c);
        st.setClipboardPasteFitScale(fitScale);
        st.setClipboardPasteScale(1.0);
        clipboardPasteNewLayerRef.current = true;

        // 2:1 equirectangular image — skip the sizing overlay and commit immediately.
        if (c.width === 2 * c.height) {
          viewportActions.commitClipboardPaste.current();
        }
        return;
      }
      notification.warning({ title: 'No image in clipboard', placement: 'bottomRight', duration: 3 });
    } catch (err: any) {
      if (err?.name === 'NotAllowedError') {
        notification.error({ title: 'Clipboard access denied', description: 'Allow clipboard access in browser settings.', placement: 'bottomRight' });
      } else {
        notification.error({ title: 'Clipboard read failed', description: String(err), placement: 'bottomRight' });
      }
    }
  };

  viewportActions.commitClipboardPaste.current = () => {
    const gl = glRef.current;
    if (!gl || !compositeMaterial.current || !compositeScene.current || !orthoCamera.current) return;
    const { clipboardPasteCanvas, clipboardPasteScale, clipboardPasteFitScale } = useAppStore.getState();
    if (!clipboardPasteCanvas) return;

    // Snapshot is always taken before any mutation so undo restores the full pre-paste state.
    pushSnapshot();

    // If requested, create a new layer and target it instead of the selected one.
    let selId: string | null = selectedLayerIdRef.current;
    if (clipboardPasteNewLayerRef.current) {
      clipboardPasteNewLayerRef.current = false;
      const newId = `layer-${Date.now()}`;
      useAppStore.getState().addLayer({
        id: newId,
        name: 'Pasted Image',
        visible: true,
        opacity: 1,
        translate: { x: 0, y: 0 },
        rotate: { x: 0, y: 0, z: 0 },
      });
      useAppStore.getState().selectLayer(newId);
      ensureLayerRTs(useAppStore.getState().layers);
      selId = newId;
    }

    if (!selId) return;
    const lRTData = layerRTs.current.get(selId);
    if (!lRTData) return;

    const { w, h } = texResRef.current;

    // Build a full-resolution canvas with the image centered + scaled.
    const pasteCanvas = document.createElement('canvas');
    pasteCanvas.width  = w;
    pasteCanvas.height = h;
    const ctx = pasteCanvas.getContext('2d')!;
    const scaledW = clipboardPasteCanvas.width  * clipboardPasteFitScale * clipboardPasteScale;
    const scaledH = clipboardPasteCanvas.height * clipboardPasteFitScale * clipboardPasteScale;
    ctx.drawImage(clipboardPasteCanvas, (w - scaledW) / 2, (h - scaledH) / 2, scaledW, scaledH);

    // Create / update the THREE CanvasTexture.
    if (!clipboardPasteTextureRef.current) {
      clipboardPasteTextureRef.current = new THREE.CanvasTexture(pasteCanvas);
      clipboardPasteTextureRef.current.colorSpace = THREE.SRGBColorSpace;
    } else {
      clipboardPasteTextureRef.current.image = pasteCanvas;
      clipboardPasteTextureRef.current.needsUpdate = true;
    }

    const srcLayerRT = lRTData.active === 'a' ? lRTData.a : lRTData.b;
    const dstLayerRT = lRTData.active === 'a' ? lRTData.b : lRTData.a;

    // Porter-Duff over in UV space — bakeMode=0 with identity rotation and no translateY.
    compositeMaterial.current.uniforms.uBase.value       = srcLayerRT.texture;
    compositeMaterial.current.uniforms.uStroke.value     = clipboardPasteTextureRef.current;
    compositeMaterial.current.uniforms.uOpacity.value    = 1.0;
    compositeMaterial.current.uniforms.uMode.value       = 0;
    compositeMaterial.current.uniforms.uRotInv.value     = new THREE.Matrix3();
    compositeMaterial.current.uniforms.uTranslateY.value = 0;
    compositeMaterial.current.uniforms.uBakeMode.value   = 0;

    gl.setRenderTarget(dstLayerRT);
    gl.render(compositeScene.current, orthoCamera.current);
    gl.setRenderTarget(null);
    lRTData.active = lRTData.active === 'a' ? 'b' : 'a';

    // Clear paste state.
    useAppStore.getState().setClipboardPasteCanvas(null);
    useAppStore.getState().setClipboardPasteScale(1.0);

    flattenLayers();
    isDirtyRef.current = true;
    autosaveNotifyDirtyRef.current();
    markLayersDirty(selId);
    scheduleBump(() => {
      updateCanvasFromRenderTarget();
      generateLayerThumbnail(selId);
    });
  };

  viewportActions.cancelClipboardPaste.current = () => {
    clipboardPasteNewLayerRef.current = false;
    useAppStore.getState().setClipboardPasteCanvas(null);
    useAppStore.getState().setClipboardPasteScale(1.0);
  };

  viewportActions.resetCamera.current = () => {
    if (controlsRef.current) {
      controlsRef.current.target.set(0, 0, 0);
    }
    animateCameraTo(new THREE.Vector3(HERO_POSITIONS[0].position.x, HERO_POSITIONS[0].position.y, HERO_POSITIONS[0].position.z));
    useAppStore.getState().setCameraFov(HERO_POSITIONS[0].fov);
  };
  const saveCurrentWithClean = async () => { await _saveCurrentProject(); autosaveResetToClean(); };
  saveCurrentProjectRef.current = saveCurrentWithClean;
  viewportActions.save.current = saveCurrentWithClean;
  viewportActions.loadFile.current = async (file: File, handle?: any) => {
    isResettingRef.current = true;
    await loadProject(file, handle);
    autosaveResetToClean();
    setTimeout(() => { isResettingRef.current = false; }, 0);
  };
  openProjectRef.current = async () => {    if (typeof (window as any).showOpenFilePicker === 'function') {
      try {
        const lastHandle = await getLastHandle();
        const [fileHandle] = await (window as any).showOpenFilePicker({
          types: [{ description: 'StoryBall Project', accept: { 'application/json': ['.balls'] } }],
          multiple: false,
          startIn: lastHandle ?? undefined,
        });
        storeLastHandle(fileHandle);
        const file: File = await fileHandle.getFile();
        await viewportActions.loadFileWithAutosaveCheck.current(file, fileHandle);
      } catch (err: any) {
        if (err?.name !== 'AbortError') throw err;
      }
    } else {
      setProjectModalOpen(true);
    }
  };
  viewportActions.open.current = openProjectRef.current as any;
  viewportActions.open.current = openProjectRef.current as any;

  flattenLayersRef.current = flattenLayers;
  floodFillAtPointRef.current = floodFillAtPoint;

  // ── Fill hover preview ────────────────────────────────────────────────────
  const clearFillPreview = () => {
    lastFillHoverPointRef.current = null;
    if (fillPreviewMaterialRef.current) {
      fillPreviewMaterialRef.current.opacity = 0;
      fillPreviewMaterialRef.current.needsUpdate = true;
    }
    if (!fillPreviewCanvasRef.current || !fillPreviewTextureRef.current) {
      invalidateRef.current();
      return;
    }
    const ctx = fillPreviewCanvasRef.current.getContext('2d');
    if (ctx) ctx.clearRect(0, 0, fillPreviewCanvasRef.current.width, fillPreviewCanvasRef.current.height);
    fillPreviewTextureRef.current.needsUpdate = true;
    invalidateRef.current();
  };

  const doFillPreview = () => {
    const hit = lastFillHoverPointRef.current;
    if (!hit) return;
    const now = Date.now();
    if (now - fillPreviewThrottleRef.current < 50) return;
    fillPreviewThrottleRef.current = now;

    const gl = glRef.current;
    const selectedId = selectedLayerIdRef.current;
    if (!gl || !selectedId) return;
    const lRT = layerRTs.current.get(selectedId);
    if (!lRT) return;
    const texCanvas = textureCanvas.current;
    if (!texCanvas) return;
    const w = texCanvas.width;
    const h = texCanvas.height;

    // Lazy-init preview canvas at full texture resolution.
    if (!fillPreviewCanvasRef.current || fillPreviewCanvasRef.current.width !== w || fillPreviewCanvasRef.current.height !== h) {
      const c = document.createElement('canvas');
      c.width = w; c.height = h;
      fillPreviewCanvasRef.current = c;
      if (fillPreviewTextureRef.current) fillPreviewTextureRef.current.dispose();
      const tex = new THREE.CanvasTexture(c);
      fillPreviewTextureRef.current = tex;
      if (fillPreviewMaterialRef.current) {
        fillPreviewMaterialRef.current.map = tex;
        fillPreviewMaterialRef.current.needsUpdate = true;
      }
    }

    const activeRT = lRT[lRT.active];
    const bufLen = w * h * 4;
    if (!fillPreviewPixelBufRef.current || fillPreviewPixelBufRef.current.length < bufLen) {
      fillPreviewPixelBufRef.current = new Uint8Array(bufLen);
    }
    const buf = fillPreviewPixelBufRef.current;
    gl.readRenderTargetPixels(activeRT, 0, 0, w, h, buf);
    flipBufferY(buf, w, h);

    const uv = worldPointToUV(hit);
    const seedX = Math.floor(uv.x * w);
    const seedY = Math.floor(uv.y * h);
    const mask = buildFillMask(buf, w, h, seedX, seedY, fillToleranceRef.current, fillFloodModeRef.current);

    const ctx = fillPreviewCanvasRef.current.getContext('2d')!;
    const imgData = ctx.createImageData(w, h);
    const d = imgData.data;
    for (let i = 0; i < mask.length; i++) {
      if (mask[i]) {
        d[i * 4]     = 255;
        d[i * 4 + 1] = 215;
        d[i * 4 + 2] = 0;
        d[i * 4 + 3] = 90;
      }
    }
    ctx.putImageData(imgData, 0, 0);
    fillPreviewTextureRef.current!.needsUpdate = true;
    if (fillPreviewMaterialRef.current) {
      fillPreviewMaterialRef.current.opacity = 1;
      fillPreviewMaterialRef.current.needsUpdate = true;
    }
    invalidateRef.current();
  };
  fillPreviewUpdateRef.current = doFillPreview;
  ensureLayerRTsRef.current = ensureLayerRTs;
  scheduleBumpRef.current = scheduleBump;
  generateAllLayerThumbnailsRef.current = generateAllLayerThumbnails;
  generateLayerThumbnailRef.current = generateLayerThumbnail;
  markLayersDirtyRef.current = markLayersDirty;
  resetHeroStateRef.current = resetHeroState;

  useViewportKeyboard({
    controlsRef,
    isDrawingRef,
    cameraRef,
    syncCamRef,
    adjustBrushSizeRef,
    undoRef,
    redoRef,
    saveCurrentProjectRef,
    openProjectRef,
    duplicateLayerRef,
  });

  // ── Texture resolution change — scale all layer RTs to the new size ──────
  const resolutionInitRef = useRef(false);
  useEffect(() => {
    // Skip the initial mount — RTs are created at the right size by initializeBrushPipeline.
    if (!resolutionInitRef.current) { resolutionInitRef.current = true; return; }
    const gl = glRef.current;
    if (!gl || !copyScene.current || !copyMaterial.current || !orthoCamera.current) return;
    const { w, h } = texRes;

    const makeRT = () => {
      const rt = new THREE.WebGLRenderTarget(w, h, {
        type: THREE.UnsignedByteType,
        minFilter: THREE.LinearFilter,
        magFilter: THREE.LinearFilter,
        wrapS: THREE.RepeatWrapping,
        wrapT: THREE.RepeatWrapping,
      });
      rt.texture.colorSpace = THREE.SRGBColorSpace;
      return rt;
    };

    // Helper: blit srcRT into a new RT of the new size using the copy shader.
    const blitScaled = (srcRT: THREE.WebGLRenderTarget): THREE.WebGLRenderTarget => {
      const dst = makeRT();
      copyMaterial.current!.uniforms.uTex.value = srcRT.texture;
      gl.setRenderTarget(dst);
      gl.render(copyScene.current!, orthoCamera.current!);
      return dst;
    };

    // Scale each layer's active RT into a new RT pair; dispose old pair.
    layerRTs.current.forEach((pair, id) => {
      const activeSrc = pair.active === 'a' ? pair.a : pair.b;
      const newActive = blitScaled(activeSrc);
      // Inactive side can be blank — it's overwritten before use.
      const newInactive = makeRT();
      gl.setRenderTarget(newInactive);
      gl.clearColor(0, 0, 0, 0);
      gl.clear(gl.COLOR_BUFFER_BIT);
      pair.a.dispose();
      pair.b.dispose();
      layerRTs.current.set(id, { a: newActive, b: newInactive, active: 'a' });
    });

    // Resize the transient pipeline RTs (content recomposed by flattenLayers).
    const resizeTransient = (rt: THREE.WebGLRenderTarget | null) => {
      if (!rt) return;
      rt.setSize(w, h);
      gl.setRenderTarget(rt);
      gl.clearColor(0, 0, 0, 0);
      gl.clear(gl.COLOR_BUFFER_BIT);
    };
    resizeTransient(renderTargetA.current);
    resizeTransient(renderTargetB.current);
    resizeTransient(strokeRenderTargetA.current);
    resizeTransient(strokeRenderTargetB.current);
    resizeTransient(lastStrokeRenderTarget.current);
    resizeTransient(singleLayerDisplayRT.current);
    gl.setRenderTarget(null);
    // Invalidate the blur temp RT — it will be recreated at the new size on next use.
    blurTempRT.current?.dispose();
    blurTempRT.current = null;

    // Resize CPU canvases.
    const canvas = textureCanvas.current;
    if (canvas) { canvas.width = w; canvas.height = h; }
    if (textureRef.current) textureRef.current.needsUpdate = true;

    flattenLayersRef.current();
    scheduleBump();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [texRes]);

  // ── Gizmo orbit callback (used by AxisGizmo overlay) ──────────────────────
  // Uses refs throughout so it never stale-closes over component state.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleGizmoOrbitDelta = useCallback((dx: number, dy: number) => {
    const cam = cameraRef.current;
    if (!cam) return;
    const sensitivity = 0.008;

    // Horizontal: rotate around world Y
    cam.position.applyAxisAngle(new THREE.Vector3(0, 1, 0), -dx * sensitivity);

    // Vertical: rotate around the camera's right vector (Y × camNorm)
    const camNorm = cam.position.clone().normalize();
    const right   = new THREE.Vector3().crossVectors(new THREE.Vector3(0, 1, 0), camNorm).normalize();
    const nextPos = cam.position.clone().applyAxisAngle(right, dy * sensitivity);
    // Clamp so camera doesn't flip over the poles
    if (Math.abs(nextPos.clone().normalize().y) < 0.98) {
      cam.position.copy(nextPos);
    }

    cam.lookAt(0, 0, 0);
    controlsRef.current?.update();
    syncCamRef.current();
    // syncCamRef is a stable ref from useCameraControl — no stale closure risk.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Image-sequence render ──────────────────────────────────────────────────
  const renderSequence = async (
    settings: RenderSettings,
    onProgress: (done: number, total: number, status?: 'rendering' | 'converting') => void,
    signal?: AbortSignal,
  ): Promise<void> => {
    const gl = glRef.current;
    if (!gl) return;

    // 1. Persist the settings so they survive save/load
    setRenderSettings(settings);

    // 2. Ask the user to pick an output folder — PNG sequence only.
    // GIF and WebM produce a single file and use a direct download instead.
    let dirHandle: any = null;
    const { outputName, bgMode, startFrame, endFrame, stepFrames, outputFormat } = settings;
    if (outputFormat === 'png-sequence') {
      const hasDirPicker = typeof (window as any).showDirectoryPicker === 'function';
      if (hasDirPicker) {
        try {
          dirHandle = await (window as any).showDirectoryPicker({ mode: 'readwrite' });
        } catch (err: any) {
          if (err?.name !== 'AbortError') {
            notification.error({ message: 'Render failed', description: String(err), placement: 'bottomRight' });
          }
          return;
        }
      }
    }
    const safeName   = outputName.replace(/[/\\:*?"<>|]/g, '_') || 'output';

    const store      = useAppStore.getState();
    const allLayers  = store.layers;
    const allKfs     = store.layerKeyframes;
    const allEasings = store.keyframeEasings;
    const selectedId = selectedLayerIdRef.current ?? allLayers[0]?.id ?? '';

    // Build the list of frames to render
    const frames: number[] = [];
    for (let f = startFrame; f <= endFrame; f += Math.max(1, stepFrames)) {
      frames.push(Math.round(f));
    }
    const total = frames.length;
    if (total === 0) return;

    const W = texResRef.current.w;
    const H = texResRef.current.h;

    // Allocate scratch canvases once outside the loop
    const tmpCanvas = document.createElement('canvas');
    tmpCanvas.width = W; tmpCanvas.height = H;
    const tmpCtx = tmpCanvas.getContext('2d')!;
    const outCanvas = document.createElement('canvas');
    outCanvas.width = W; outCanvas.height = H;
    const outCtx = outCanvas.getContext('2d')!;
    const buf = new Uint8Array(W * H * 4);
    const imgData = tmpCtx.createImageData(W, H);

    // ── Format-specific setup ──────────────────────────────────────────────────
    // Video (WebM/VP9 via WebCodecs + webm-muxer): set up encoder and muxer.
    // Each frame gets an explicit microsecond timestamp (frameIndex * 1e6 / fps),
    // making the output exactly frame-deterministic regardless of encoding speed.
    let videoMuxer: any = null;
    let videoEncoder: VideoEncoder | null = null;
    let encoderError: Error | null = null;
    const videoChunksDone: Promise<void>[] = [];
    if (outputFormat === 'webm' || outputFormat === 'mp4') {
      try {
        // Check if VideoEncoder is available
        if (typeof VideoEncoder === 'undefined') {
          throw new Error('VideoEncoder not supported. Please use Chrome 94+, Edge 94+, or Firefox 91+.');
        }

        const { Muxer, ArrayBufferTarget } = await import('webm-muxer');
        videoMuxer = new Muxer({
          target: new ArrayBufferTarget(),
          video: { codec: 'V_VP9', width: W, height: H, frameRate: fps },
        });
        videoEncoder = new VideoEncoder({
          output: (chunk: EncodedVideoChunk, meta?: EncodedVideoChunkMetadata) =>
            videoMuxer.addVideoChunk(chunk, meta),
          error: (e: Error) => {
            encoderError = e;
            console.error('[VideoEncoder] Encoder error:', e);
            notification.error({ title: 'Video encode error', description: String(e), placement: 'bottomRight' });
          },
        });

        // Try VP9 codec first, with minimal config to increase compatibility
        const bitrate = Math.max(2_000_000, Math.min(50_000_000, W * H * fps * 0.05));
        let codecConfig: any = {
          codec: 'vp09.00.51.08',  // VP9 profile 0, level 5.1, 8-bit
          width: W,
          height: H,
          bitrate: bitrate,
          framerate: fps,
        };

        console.log('[VideoEncoder] Attempting VP9 codec config:', codecConfig);
        try {
          videoEncoder.configure(codecConfig);
          console.log('[VideoEncoder] VP9 configuration successful');
        } catch (vp9Err) {
          console.warn('[VideoEncoder] VP9 failed, trying H.264:', vp9Err);
          // Fallback to H.264 if VP9 fails
          codecConfig = {
            codec: 'avc1.42001f',  // H.264 baseline
            width: W,
            height: H,
            bitrate: bitrate,
            framerate: fps,
          };
          console.log('[VideoEncoder] Attempting H.264 codec config:', codecConfig);
          videoEncoder.configure(codecConfig);
          console.log('[VideoEncoder] H.264 configuration successful');
        }
      } catch (err) {
        const errMsg = err instanceof Error ? err.message : String(err);
        const stack = err instanceof Error ? err.stack : '';
        console.error('[VideoEncoder] Initialization failed:', errMsg, stack);
        notification.error({
          title: 'Video encoder initialization failed',
          description: `${errMsg}. Supported browsers: Chrome 94+, Edge 94+, or Firefox 91+.`,
          placement: 'bottomRight',
          duration: 0,
        });
        throw new Error(`Failed to initialize video encoder: ${errMsg}`);
      }
    }
    const savedLayers = layersRef.current;

    for (let i = 0; i < total; i++) {
      // Check for abort signal (user clicked cancel)
      if (signal?.aborted) {
        throw new DOMException('Render cancelled by user', 'AbortError');
      }

      // Stop if encoder encountered an error
      if (encoderError) {
        throw encoderError;
      }

      const frame = frames[i];
      onProgress(i, total);

      // Apply keyframe interpolation to produce layer snapshot at this frame
      const interpLayers = interpolateLayers(allLayers, frame, allKfs, allEasings);

      // Temporarily override layersRef so flattenLayers uses the interpolated values without touching React state
      layersRef.current = interpLayers;

      flattenLayers(frame);
      const srcRT = activeRenderTarget.current;

      // Restore immediately so no other code sees the temporary override
      layersRef.current = savedLayers;

      if (!srcRT) continue;

      // Read GPU pixels (Y-flip: GPU origin is bottom-left, canvas is top-left)
      try {
        gl.readRenderTargetPixels(srcRT, 0, 0, W, H, buf);
      } catch {
        continue;
      }
      for (let y = 0; y < H; y++) {
        const srcRow = H - 1 - y;
        imgData.data.set(buf.subarray(srcRow * W * 4, (srcRow + 1) * W * 4), y * W * 4);
      }
      tmpCtx.putImageData(imgData, 0, 0);

      // Composite onto output canvas with chosen background.
      // Video doesn't support alpha — force black when transparent is selected.
      const effectiveBgMode = (outputFormat === 'webm' || outputFormat === 'mp4') && bgMode === 'transparent'
        ? 'black'
        : bgMode;
      let exportCanvas: HTMLCanvasElement;
      if (effectiveBgMode === 'transparent') {
        exportCanvas = tmpCanvas;
      } else {
        outCtx.clearRect(0, 0, W, H);
        if (effectiveBgMode === 'black') {
          outCtx.fillStyle = '#000';
          outCtx.fillRect(0, 0, W, H);
        } else {
          outCtx.fillStyle = '#fff';
          outCtx.fillRect(0, 0, W, H);
        }
        outCtx.drawImage(tmpCanvas, 0, 0);
        exportCanvas = outCanvas;
      }

      if (outputFormat === 'png-sequence') {
        // Write one PNG per frame.
        const fname = `${safeName}.${String(frame + FRAME_OFFSET).padStart(8, '0')}.png`;
        const blob: Blob = await new Promise<Blob>((res) =>
          exportCanvas.toBlob((b) => res(b!), 'image/png'),
        );
        if (dirHandle) {
          try {
            const fh  = await dirHandle.getFileHandle(fname, { create: true });
            const wr  = await fh.createWritable();
            await wr.write(blob);
            await wr.close();
          } catch (err) {
            notification.error({ title: 'Write error', description: `Could not write ${fname}`, placement: 'bottomRight', duration: 4 });
          }
        } else {
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url; a.download = fname; a.click();
          URL.revokeObjectURL(url);
        }
        await new Promise<void>((res) => setTimeout(res, 0));

      } else if ((outputFormat === 'webm' || outputFormat === 'mp4')) {
        if (!videoEncoder) {
          throw new Error('Video encoder not initialized');
        }
        // Encode into VP9 WebM with an exact microsecond timestamp.
        // Use the actual frame number (not output index i) to preserve correct playback timing
        // when rendering with frame stepping (e.g., every 10th frame).
        const timestamp = frames[i] * (1_000_000 / fps);   // microseconds
        const vf = new VideoFrame(exportCanvas, { timestamp });
        try {
          // Apply light backpressure to avoid overwhelming the encoder queue
          let waitCount = 0;
          while (videoEncoder.encodeQueueSize > 8 && waitCount < 100) {
            await new Promise<void>((res) => setTimeout(res, 5));
            waitCount++;
          }
          if (waitCount >= 100) {
            throw new Error('Encoder queue not draining; possible encoder failure');
          }
          videoEncoder.encode(vf, { keyFrame: i % Math.max(1, Math.round(fps * 2)) === 0 });
        } catch (encodeErr) {
          vf.close();
          throw new Error(`Failed to encode frame ${i}: ${encodeErr instanceof Error ? encodeErr.message : String(encodeErr)}`);
        }
        vf.close();
      }
    }

    // Restore the sphere's texture to the current frame's composite
    layersRef.current = savedLayers;
    flattenLayers();

    // ── Format-specific finalization ──────────────────────────────────────────
    const downloadBlob = (blob: Blob, filename: string) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    };

    if ((outputFormat === 'webm' || outputFormat === 'mp4') && videoEncoder && videoMuxer) {
      if (encoderError) {
        throw new Error(`Cannot finalize video: encoder error occurred during rendering: ${String(encoderError)}`);
      }
      await videoEncoder.flush();
      videoMuxer.finalize();
      const webmBlob = new Blob([videoMuxer.target.buffer], { type: 'video/webm' });

      if (outputFormat === 'webm') {
        downloadBlob(webmBlob, `${safeName}.webm`);
      } else {
        // Convert WebM to MP4 using FFmpeg
        try {
          const mp4Blob = await convertWebMToMP4(webmBlob, safeName, (progress) => {
            // Map FFmpeg progress (0.0-1.0) to visual progress (0-total)
            const progressFrames = Math.round(progress * total);
            onProgress(progressFrames, total, 'converting');
          }, signal);
          downloadBlob(mp4Blob, `${safeName}.mp4`);
        } catch (error) {
          notification.error({
            title: 'MP4 conversion failed',
            description: `${error instanceof Error ? error.message : String(error)}`,
            placement: 'bottomRight',
            duration: 10,
          });
        }
      }
    }

    onProgress(total, total);
    notification.success({
      title: 'Render complete',
      description: `${total} frame${total !== 1 ? 's' : ''} exported to "${safeName}".`,
      placement: 'bottomRight',
      duration: 5,
    });
  };
  renderSequenceRef.current = renderSequence;


  const handlePointerDown = useCallback((evt: ThreeEvent<PointerEvent>) => {
    // Cancel any pending autosave idle callback immediately so it doesn't
    // freeze the main thread while the user is actively drawing.
    autosaveCancelPendingRef.current();
    cancelEncoder();

    // read tablet mode from store
    const { tabletMode: _tablet, tabletSphereMode: _tMode } = useAppStore.getState();

    // Alt+click (without Shift) is reserved for orbiting the camera (OrbitControls)
    // Right-click in fill mode is handled below to use color 2; all other right-clicks are blocked.
    if ((evt.altKey && !evt.shiftKey) || (evt.button !== 0 && !(evt.button === 2 && toolModeRef.current === 'fill'))) return;

    // Block all painting/transformation on locked layers (including ancestor-locked)
    const _selLayerForLock = layersRef.current.find((l) => l.id === selectedLayerIdRef.current);
    if (_selLayerForLock && isEffectivelyLocked(_selLayerForLock, layersRef.current)) return;

    // Prevent OrbitControls from handling drag while we are drawing.
    // stopPropagation() only stops R3F's synthetic event bubbling.
    // stopImmediatePropagation() on the native event also blocks OrbitControls'
    // DOM listener (registered on the same canvas element) — critical for
    // preventing the camera from moving when clicking fast on the sphere.
    evt.stopPropagation();
    evt.nativeEvent.stopImmediatePropagation();

    // Ensure we keep receiving pointermove/up even if the cursor leaves the mesh.
    // Skip pointer capture when pointer lock is active — they are mutually exclusive.
    const ct = evt.currentTarget as any;
    if (ct?.setPointerCapture && !document.pointerLockElement) {
      try { ct.setPointerCapture(evt.pointerId); } catch (_) { /* pointer no longer valid */ }
    }

    // Shift+Alt+drag OR tablet translateLayer mode: translate the selected layer in UV space.
    if ((evt.shiftKey && evt.altKey) || (_tablet && _tMode === 'translateLayer')) {
      const selId = selectedLayerIdRef.current;
      const selLayer = layersRef.current.find((l) => l.id === selId);
      if (selLayer) {
        pushSnapshot();
        setIsTranslating(true); isTranslatingRef.current = true;
        transDragStart.current = { x: evt.clientX, y: evt.clientY };
        transDragBase.current = { tx: selLayer.translate.x, ty: selLayer.translate.y };
      }
      return;
    }

    // Track Ctrl key state for gradient last-stop drawing.
    ctrlHeldRef.current = evt.ctrlKey || evt.metaKey;

    // Shift+drag OR tablet rotateLayer mode: rotate the selected layer instead of painting.
    if (evt.shiftKey || (_tablet && _tMode === 'rotateLayer')) {
      const selId = selectedLayerIdRef.current;
      const selLayer = layersRef.current.find((l) => l.id === selId);
      if (selLayer) {
        pushSnapshot();
        setIsRotating(true); isRotatingRef.current = true;
        rotDragStart.current = { x: evt.clientX, y: evt.clientY };
        rotDragBase.current = { rx: selLayer.rotate.x, ry: selLayer.rotate.y, rz: selLayer.rotate.z };
      }
      return;
    }

    // Stamp mode: single click applies stamp, no drag needed.
    if (toolModeRef.current === 'stamp') {
      const hit = getHitData(evt);
      if (hit) {
        pushSnapshot();
        applyStamp(hit.point);
        clearStampPreview();
      }
      return;
    }

    // Text mode: render current text settings and stamp onto sphere.
    if (toolModeRef.current === 'text') {
      const hit = getHitData(evt);
      if (hit) {
        pushSnapshot();
        applyTextStamp(hit.point);
        clearStampPreview();
      }
      return;
    }

    // Fill mode: record drag start; the actual fill fires on pointer-up.
    if (toolModeRef.current === 'fill') {
      const hit = getHitData(evt);
      if (hit) {
        fillDragStartRef.current = hit.point;
        fillDragAltRef.current = evt.button === 2;
        const canvasRect = glRef.current?.domElement.getBoundingClientRect();
        fillDragStartScreenRef.current = {
          x: canvasRect ? evt.clientX - canvasRect.left : evt.clientX,
          y: canvasRect ? evt.clientY - canvasRect.top  : evt.clientY,
        };
        // Mark drawing so handlePointerUp is reached (needed for pointer-capture release).
        isDrawingRef.current = true;
      }
      return;
    }

    const hit = getHitData(evt);
    if (!hit) return;

    pushSnapshot();
    setIsDrawing(true); isDrawingRef.current = true;
    setIsDrawingActive(true);
    pressureRef.current = usePressureRef.current
      ? simPressureRef.current
        ? 0.5 + 0.5 * Math.sin(Date.now() / (2000 - simPressureRandomnessRef.current * 1950))
        : (evt.pressure > 0 ? evt.pressure : 1.0)
      : 1.0;

    // Clear the current-stroke render target so it only shows this stroke.
    const gl = glRef.current;
    const currentStrokeRT = activeStrokeRenderTarget.current;
    if (gl && currentStrokeRT) {
      gl.setRenderTarget(currentStrokeRT);
      gl.clearColor(0, 0, 0, 0);
      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.setRenderTarget(null);
    }

    // Start a new stroke: clear spline state and paint the first point.
    prevHitPoint.current = hit.point;
    smoothedHitPoint.current = hit.point.clone();
    pointBuffer.current = [hit.point.clone()];
    lastSplinePoint.current = hit.point.clone();
    strokeSpineRef.current = [{ point: hit.point.clone(), pressure: pressureRef.current }];
    paintSphereBrush(hit.point, pressureRef.current);
    // Don't scheduleBump here — the preview sphere shows the stroke RT directly.
    // CPU readback (UV panel, thumbnails) happens at stroke end in handlePointerUp.
  }, []);

  /** Returns the screen-space half-height in pixels of the stamp/text preview square. */
  const computeStampCursorHalfHeight = (): number => {
    const cam = cameraRef.current;
    if (!cam) return 20;
    const camDist = cam.position.length();
    const halfY_NDC = (stampSizeRef.current * camDist) / camFrustumHalfH(cam, camDist);
    const h = glRef.current?.domElement.clientHeight ?? window.innerHeight;
    return halfY_NDC * h / 2;
  };

  const computeBrushCursorRadius = (): number => {
    const cam = cameraRef.current;
    if (!cam) return 18;
    const pressure = usePressureRef.current ? pressureRef.current : 1.0;
    const size = brushSizeRef.current * Math.max(0.05, pressure);
    const D = cam.position.length();
    const theta = 2 * Math.asin(Math.min(1, size / 2));
    const ndcR = Math.sin(theta) / camFrustumHalfH(cam, D - Math.cos(theta));
    const h = glRef.current?.domElement.clientHeight ?? window.innerHeight;
    return ndcR * h / 2;
  };

  const handlePointerMove = useCallback((evt: ThreeEvent<PointerEvent>) => {
    // Track Ctrl key for gradient last-stop drawing.
    ctrlHeldRef.current = evt.ctrlKey || evt.metaKey;
    // Update brush cursor position when in draw/erase/blur mode.
    // Directly mutates the DOM element — avoids React re-render on every pointer move.
    if (toolModeRef.current === 'draw' || toolModeRef.current === 'erase' || toolModeRef.current === 'blur') {
      if (usePressureRef.current && !simPressureRef.current && evt.pressure > 0) pressureRef.current = evt.pressure;
      if (usePressureRef.current && simPressureRef.current) pressureRef.current = 0.5 + 0.5 * Math.sin(Date.now() / (2000 - simPressureRandomnessRef.current * 1950));
      const cursorEl = brushCursorElemRef.current;
      if (cursorEl) {
        const r = computeBrushCursorRadius();
        // Cursor div is position:absolute inside the canvas container — subtract the
        // canvas bounding rect so the ring tracks the mouse precisely regardless of
        // any toolbar/panel offset above or to the left of the viewport.
        const canvasRect = glRef.current?.domElement.getBoundingClientRect();
        const cx = canvasRect ? evt.clientX - canvasRect.left : evt.clientX;
        const cy = canvasRect ? evt.clientY - canvasRect.top  : evt.clientY;
        cursorEl.style.left   = `${cx}px`;
        cursorEl.style.top    = `${cy}px`;
        cursorEl.style.width  = `${r * 2}px`;
        cursorEl.style.height = `${r * 2}px`;
        cursorEl.style.display = 'block';
        // Inner blur-radius ring.
        const innerEl = blurInnerCursorElemRef.current;
        if (innerEl) {
          if (toolModeRef.current === 'blur') {
            const ir = Math.max(2, blurRadiusRef.current * r);
            innerEl.style.left    = `${cx}px`;
            innerEl.style.top     = `${cy}px`;
            innerEl.style.width   = `${ir * 2}px`;
            innerEl.style.height  = `${ir * 2}px`;
            innerEl.style.display = 'block';
          } else {
            innerEl.style.display = 'none';
          }
        }
        // Dots: centred above the ring.
        const dotsEl = dupModeCursorRef.current;
        if (dotsEl) {
          dotsEl.style.left    = `${cx}px`;
          dotsEl.style.top     = `${cy - r - 8}px`;
          dotsEl.style.display = 'flex';
        }
      }
    }
    // ── Translation drag mode ────────────────────────────────────────────
    if (isTranslatingRef.current) {
      if ((evt.buttons & 1) === 0) { setIsTranslating(false); isTranslatingRef.current = false; return; }
      const selId = selectedLayerIdRef.current;
      if (!selId || !transDragStart.current || !transDragBase.current) return;
      const rect = glRef.current?.domElement.getBoundingClientRect();
      if (!rect) return;
      // 1 full viewport width = 1.0 UV unit for X; height is half that (2:1 equirect)
      const newTx = transDragBase.current.tx + (evt.clientX - transDragStart.current.x) / rect.width;
      const newTy = transDragBase.current.ty - (evt.clientY - transDragStart.current.y) / (rect.width * 0.5);
      updateLayer(selId, { translate: { x: newTx, y: newTy } });
      const layer = layersRef.current.find((l) => l.id === selId);
      if (layer) { layer.translate = { x: newTx, y: newTy }; }
      flattenLayers();
      scheduleBump();
      return;
    }

    // ── Rotation drag mode ──────────────────────────────────────────────────
    if (isRotatingRef.current) {
      if ((evt.buttons & 1) === 0) { setIsRotating(false); isRotatingRef.current = false; return; }
      const selId = selectedLayerIdRef.current;
      if (!selId || !rotDragStart.current || !rotDragBase.current) return;
      const dx = evt.clientX - rotDragStart.current.x;
      const dy = evt.clientY - rotDragStart.current.y;
      // 0.5 deg per pixel feels responsive without being too twitchy.
      const DEG_PER_PX = 0.5;
      const newRy = rotDragBase.current.ry + dx * DEG_PER_PX;
      const newRx = rotDragBase.current.rx + dy * DEG_PER_PX;
      updateLayer(selId, { rotate: { x: newRx, y: newRy, z: rotDragBase.current.rz } });
      // layersRef is updated next render; update immediately for flattenLayers.
      const layer = layersRef.current.find((l) => l.id === selId);
      if (layer) { layer.rotate = { x: newRx, y: newRy, z: rotDragBase.current.rz }; }
      flattenLayers();
      scheduleBump();
      return;
    }

    // ── Stamp mode hover preview ────────────────────────────────────────────
    if (toolModeRef.current === 'stamp') {
      const hit = getHitData(evt);
      if (hit && useAppStore.getState().stampImage) {
        renderStampPreview(hit.point);
      } else {
        clearStampPreview();
      }
      const dotsEl = dupModeCursorRef.current;
      if (dotsEl) {
        const canvasRect = glRef.current?.domElement.getBoundingClientRect();
        const halfH = computeStampCursorHalfHeight();
        dotsEl.style.left    = `${canvasRect ? evt.clientX - canvasRect.left : evt.clientX}px`;
        dotsEl.style.top     = `${(canvasRect ? evt.clientY - canvasRect.top : evt.clientY) - halfH - 8}px`;
        dotsEl.style.display = 'flex';
      }
      return;
    }

    if (toolModeRef.current === 'text') {
      const hit = getHitData(evt);
      if (hit) {
        renderTextPreview(hit.point);
      } else {
        clearStampPreview();
      }
      const dotsEl = dupModeCursorRef.current;
      if (dotsEl) {
        const canvasRect = glRef.current?.domElement.getBoundingClientRect();
        const halfH = computeStampCursorHalfHeight();
        dotsEl.style.left    = `${canvasRect ? evt.clientX - canvasRect.left : evt.clientX}px`;
        dotsEl.style.top     = `${(canvasRect ? evt.clientY - canvasRect.top : evt.clientY) - halfH - 8}px`;
        dotsEl.style.display = 'flex';
      }
      return;
    }

    // Fill mode: update hover preview when not dragging, or clear it when dragging starts.
    if (toolModeRef.current === 'fill') {
      const hit = getHitData(evt);
      if (hit && !isDrawingRef.current) {
        lastFillHoverPointRef.current = hit.point.clone();
        fillPreviewUpdateRef.current();
      } else if (isDrawingRef.current) {
        // Hide preview while dragging for gradient fill.
        if (lastFillHoverPointRef.current) {
          lastFillHoverPointRef.current = null;
          if (fillPreviewCanvasRef.current && fillPreviewTextureRef.current) {
            const ctx2 = fillPreviewCanvasRef.current.getContext('2d');
            if (ctx2) ctx2.clearRect(0, 0, fillPreviewCanvasRef.current.width, fillPreviewCanvasRef.current.height);
            fillPreviewTextureRef.current.needsUpdate = true;
          }
        }
      }
    }

    if (!isDrawingRef.current) return;

    // Fill mode drag: update the SVG line preview imperatively.
    if (toolModeRef.current === 'fill') {
      if (fillDragStartScreenRef.current) {
        const svgEl = fillDragSvgRef.current;
        if (svgEl) {
          const canvasRect = glRef.current?.domElement.getBoundingClientRect();
          const cx = canvasRect ? evt.clientX - canvasRect.left : evt.clientX;
          const cy = canvasRect ? evt.clientY - canvasRect.top  : evt.clientY;
          const { x: sx, y: sy } = fillDragStartScreenRef.current;
          const line = svgEl.querySelector('line')!;
          line.setAttribute('x1', String(sx));
          line.setAttribute('y1', String(sy));
          line.setAttribute('x2', String(cx));
          line.setAttribute('y2', String(cy));
          const dot = svgEl.querySelector('circle')!;
          dot.setAttribute('cx', String(sx));
          dot.setAttribute('cy', String(sy));
          svgEl.style.display = 'block';
        }
      }
      return;
    }

    // If the mouse button is no longer pressed, commit and stop (robust against missed pointerup).
    // Calling handlePointerUp ensures the stroke is baked into the layer rather than orphaned.
    if ((evt.buttons & 1) === 0) {
      handlePointerUp(evt);
      return;
    }

    // Use getCoalescedEvents() to recover ALL intermediate pointer samples that the
    // browser accumulated between animation frames.  Without this, fast strokes
    // skip because only one sample per rAF is delivered.  Falls back to the main
    // event on browsers that don't support getCoalescedEvents (Safari < 13.1).
    //
    // Cap to MAX_COALESCED events per frame to prevent a snowball effect:
    // if one frame is slow, it would accumulate more events, making the next frame
    // even slower.  We subsample evenly, always keeping the last event (most recent
    // pointer position) so the stroke stays accurate under high input rates.
    const MAX_COALESCED = 8;
    const nativeEvt = evt.nativeEvent as PointerEvent;
    const coalescedRaw: PointerEvent[] = nativeEvt.getCoalescedEvents?.() ?? [nativeEvt];
    const coalescedEvents: PointerEvent[] =
      coalescedRaw.length <= MAX_COALESCED
        ? coalescedRaw
        : (() => {
            const out: PointerEvent[] = [];
            const stride = (coalescedRaw.length - 1) / (MAX_COALESCED - 1);
            for (let _ci = 0; _ci < MAX_COALESCED - 1; _ci++)
              out.push(coalescedRaw[Math.round(_ci * stride)]);
            out.push(coalescedRaw[coalescedRaw.length - 1]); // always keep most-recent
            return out;
          })();

    for (const ce of coalescedEvents) {
      // Update real-hardware pressure per coalesced sample; sim-pressure is
      // time-based so it's already updated once at the top of handlePointerMove.
      if (usePressureRef.current && !simPressureRef.current && (ce as PointerEvent).pressure > 0)
        pressureRef.current = (ce as PointerEvent).pressure;

      const hit = getHitData(ce);
      if (!hit || !prevHitPoint.current) continue;

      // Apply EMA input smoothing to reduce digitizer jitter and hand tremor.
      // factor=1 (smoothness=0) means raw input; factor→0 (smoothness=1) means heavy averaging.
      let workPoint = hit.point;
      if (smoothnessRef.current > 0 && smoothedHitPoint.current) {
        const factor = 1 - smoothnessRef.current * 0.92;
        smoothedHitPoint.current.lerp(hit.point, factor);
        workPoint = smoothedHitPoint.current;
      } else {
        smoothedHitPoint.current = hit.point.clone();
      }

      pointBuffer.current.push(workPoint.clone());
      if (pointBuffer.current.length > 4) pointBuffer.current.shift();
      strokeSpineRef.current.push({ point: workPoint.clone(), pressure: pressureRef.current });
      if (strokeSpineRef.current.length > 2000) {
        // Thin the spine (keep every 2nd point + last) to bound memory.
        const thinned: { point: THREE.Vector3; pressure: number }[] = [];
        for (let _k = 0; _k < strokeSpineRef.current.length - 1; _k += 2) thinned.push(strokeSpineRef.current[_k]);
        thinned.push(strokeSpineRef.current[strokeSpineRef.current.length - 1]);
        strokeSpineRef.current = thinned;
      }

      if (pointBuffer.current.length >= 4) {
        const [p0, p1, p2, p3] = pointBuffer.current;
        const dist = p1.distanceTo(p2);
        const step = brushRadius.current * 0.5;
        const steps = Math.min(32, Math.max(1, Math.ceil(dist / step)));
        const splinePoints = getSplinePoints(p0, p1, p2, p3, steps, smoothnessRef.current);

        // Start from last painted spline point (if present) to avoid gaps.
        let startIdx = 0;
        if (lastSplinePoint.current) {
          while (
            startIdx < splinePoints.length - 1 &&
            splinePoints[startIdx].distanceToSquared(lastSplinePoint.current) < 1e-6
          ) {
            startIdx++;
          }
        }

        for (let i = Math.max(1, startIdx); i < splinePoints.length; i++) {
          paintSphereLine(splinePoints[i - 1], splinePoints[i], pressureRef.current);
        }

        lastSplinePoint.current = splinePoints[splinePoints.length - 1].clone();
      } else {
        paintSphereLine(prevHitPoint.current, workPoint, pressureRef.current);
        lastSplinePoint.current = workPoint.clone();
      }

      prevHitPoint.current = workPoint;
    }
  }, []);

  function updateSpherePreview(opacity: number): boolean {
    const gl = glRef.current;
    const strokeRT = activeStrokeRenderTarget.current;
    const selectedId = selectedLayerIdRef.current;
    if (!gl || !strokeRT || !selectedId || !compositeMaterial.current || !compositeScene.current || !orthoCamera.current) return false;

    const lRTData = layerRTs.current.get(selectedId);
    if (!lRTData) return false;

    const srcLayerRT = lRTData.active === 'a' ? lRTData.a : lRTData.b;
    const dstLayerRT = lRTData.active === 'a' ? lRTData.b : lRTData.a;

    // Composite the completed stroke into the active layer's ping-pong buffer.
    // The stroke was painted in world space. To bake it into the layer texture
    // The stroke was painted in world space. To bake it into the layer texture
    // (which is stored in layer-local space) we must sample the stroke at T(q)
    // for each layer-local position q, where T is the combined forward transform.
    const activeLayer = layersRef.current.find((l) => l.id === selectedId);
    if (activeLayer && compositeMaterial.current.uniforms.uRotInv) {
      // Forward rotation (no transpose) maps layer-local → world; use effective transform for parent inheritance.
      compositeMaterial.current.uniforms.uRotInv.value =
        new THREE.Matrix3().setFromMatrix4(buildEffectiveLayerTransform(activeLayer, layersRef.current));
      compositeMaterial.current.uniforms.uTranslateY.value = getEffectiveTranslateY(activeLayer, layersRef.current);
    } else if (compositeMaterial.current.uniforms.uRotInv) {
      compositeMaterial.current.uniforms.uRotInv.value = new THREE.Matrix3(); // identity
      compositeMaterial.current.uniforms.uTranslateY.value = 0;
    }
    compositeMaterial.current.uniforms.uBakeMode.value = 1;
    compositeMaterial.current.uniforms.uBase.value = srcLayerRT.texture;
    compositeMaterial.current.uniforms.uStroke.value = strokeRT.texture;
    compositeMaterial.current.uniforms.uOpacity.value = opacity;
    compositeMaterial.current.uniforms.uMode.value = toolModeRef.current === 'erase' ? 1 : 0;

    gl.setRenderTarget(dstLayerRT);
    gl.render(compositeScene.current, orthoCamera.current);
    gl.setRenderTarget(null);

    // Advance the active pointer for this layer's ping-pong.
    lRTData.active = lRTData.active === 'a' ? 'b' : 'a';

    // Re-flatten all layers into the display texture.
    flattenLayers();

    return true;
  }

  /** Applies a two-pass separable Gaussian blur to the active layer wherever
   *  the stroke was drawn.  Pass 0: horizontal blur → temp RT.  Pass 1:
   *  vertical blur → dst RT, mixed with original via stroke mask. */
  function applyBlurStroke(opacity: number): boolean {
    const gl = glRef.current;
    const strokeRT = activeStrokeRenderTarget.current;
    const selectedId = selectedLayerIdRef.current;
    if (!gl || !strokeRT || !selectedId || !orthoCamera.current) return false;

    // Lazy-initialize the blur material in case initializeBrushPipeline ran
    // before the blur shader was available, or the uniforms are out of date.
    if (!blurMaterial.current || !blurScene.current ||
        !blurMaterial.current.uniforms.uPassMode) {
      const { w, h } = texResRef.current;
      blurMaterial.current = new THREE.ShaderMaterial({
        transparent: false,
        blending: THREE.NoBlending,
        depthWrite: false,
        uniforms: {
          uBase:       { value: null },
          uOriginal:   { value: null },
          uStroke:     { value: null },
          uOpacity:    { value: 1.0 },
          uBlurRadius: { value: 4.0 },
          uTexelSize:  { value: new THREE.Vector2(1 / w, 1 / h) },
          uRotInv:     { value: new THREE.Matrix3() },
          uTranslateY: { value: 0.0 },
          uPassMode:   { value: 0 },
        },
        vertexShader: blurVertexShader,
        fragmentShader: blurFragmentShader,
      });
      blurScene.current = new THREE.Scene();
      blurScene.current.add(new THREE.Mesh(new THREE.PlaneGeometry(2, 2), blurMaterial.current));
    }

    // Lazy-initialize the horizontal-pass temp RT (invalidated on resolution change).
    if (!blurTempRT.current) {
      const { w, h } = texResRef.current;
      blurTempRT.current = new THREE.WebGLRenderTarget(w, h, {
        type: THREE.UnsignedByteType,
        minFilter: THREE.LinearFilter,
        magFilter: THREE.LinearFilter,
        wrapS: THREE.RepeatWrapping,
        wrapT: THREE.RepeatWrapping,
      });
      blurTempRT.current.texture.colorSpace = THREE.SRGBColorSpace;
    }

    const lRTData = layerRTs.current.get(selectedId);
    if (!lRTData) return false;

    const srcLayerRT = lRTData.active === 'a' ? lRTData.a : lRTData.b;
    const dstLayerRT = lRTData.active === 'a' ? lRTData.b : lRTData.a;
    const tempRT     = blurTempRT.current;

    // Set per-stroke uniforms shared by both passes.
    const activeLayer = layersRef.current.find((l) => l.id === selectedId);
    if (activeLayer) {
      blurMaterial.current.uniforms.uRotInv.value =
        new THREE.Matrix3().setFromMatrix4(buildEffectiveLayerTransform(activeLayer, layersRef.current));
      blurMaterial.current.uniforms.uTranslateY.value = getEffectiveTranslateY(activeLayer, layersRef.current);
    } else {
      blurMaterial.current.uniforms.uRotInv.value = new THREE.Matrix3();
      blurMaterial.current.uniforms.uTranslateY.value = 0;
    }
    blurMaterial.current.uniforms.uBlurRadius.value =
      1 + blurRadiusRef.current * 23; // [0,1] → [1,24] texels
    blurMaterial.current.uniforms.uOpacity.value  = opacity;
    blurMaterial.current.uniforms.uStroke.value   = strokeRT.texture;

    // Pass 0: horizontal blur — srcLayerRT → tempRT (no masking yet).
    blurMaterial.current.uniforms.uBase.value     = srcLayerRT.texture;
    blurMaterial.current.uniforms.uPassMode.value = 0;
    gl.setRenderTarget(tempRT);
    gl.render(blurScene.current, orthoCamera.current);
    gl.setRenderTarget(null);

    // Pass 1: vertical blur + stroke mask — tempRT → dstLayerRT.
    blurMaterial.current.uniforms.uBase.value     = tempRT.texture;
    blurMaterial.current.uniforms.uOriginal.value = srcLayerRT.texture;
    blurMaterial.current.uniforms.uPassMode.value = 1;
    gl.setRenderTarget(dstLayerRT);
    gl.render(blurScene.current, orthoCamera.current);
    gl.setRenderTarget(null);

    lRTData.active = lRTData.active === 'a' ? 'b' : 'a';
    flattenLayers();
    return true;
  }

  // ── Envelope fill helpers ────────────────────────────────────────────────────


  // Overwrite activeStrokeRenderTarget with a perfect SDF envelope of the
  // stroke spine, replacing the live dot-packed preview before committing.
  const commitEnvelopeFill = (
    spinePoints: { point: THREE.Vector3; pressure: number }[],
  ) => {
    if (spinePoints.length === 0) return;
    const gl  = glRef.current;
    const cam = cameraRef.current;
    if (!gl || !cam || !envelopeMaterialRef.current || !envelopeSceneRef.current ||
        !orthoCamera.current || !strokeRenderTargetA.current || !strokeRenderTargetB.current) return;

    const BATCH = 64;
    const isProjMode = drawProjectionModeRef.current === 'project';
    const isInverseMode = drawProjectionModeRef.current === 'inverse';
    const el         = gl.domElement;
    const viewAspect = el ? el.clientWidth / el.clientHeight : 1.0;
    const camD       = cam.position.length();

    // SDF capsules sharing endpoints are ALWAYS gap-free regardless of step size.
    // Step must match the preview dot spacing (brushRadius × 0.5) so the final
    // shape traces the same curve as the live preview.  Using a larger step (the
    // old 1.8×) produces too few segments for large brushes — a tight curve drawn
    // with a large brush would be approximated by only a handful of straight chords,
    // visibly deviating from the smooth preview.  resampleStrokeSpine caps the
    // result at 1024 points so GPU work at stroke commit remains bounded.
    let minRadius = brushSizeRef.current;
    if (usePressureRef.current) {
      for (const sp of spinePoints) {
        const r = brushSizeRef.current * Math.max(0.05, sp.pressure);
        if (r < minRadius) minRadius = r;
      }
    }
    // Single-point stroke: duplicate the point to form a degenerate capsule (A=B),
    // which the envelope shader evaluates as a circle — same sharp fwidth edge as
    // any dragged stroke.  This replaces the old behaviour of leaving the soft
    // brush-shader dot as the committed result.
    const resampled = spinePoints.length < 2
      ? [spinePoints[0], spinePoints[0]]
      : resampleStrokeSpine(spinePoints, Math.max(0.001, minRadius * 0.5));
    const N = resampled.length;
    if (N < 2) return;

    // color / common uniforms.
    // When Ctrl is held and gradient is active, use the last gradient stop color;
    // otherwise use first stop / solid color (set into brushColorRef by App.tsx).
    let actualColor: string = Array.isArray(brushColorRef.current)
      ? (brushColorRef.current as any)[0].color : brushColorRef.current;
    if (ctrlHeldRef.current) {
      const pv = pickerValueRef.current;
      if (pv && typeof pv.getColors === 'function') {
        const stops: Array<{ color: any; percent: number }> = pv.getColors();
        if (stops && stops.length > 0) {
          const last = stops[stops.length - 1];
          if (last.color && typeof last.color.toHexString === 'function') {
            const hex8 = last.color.toHexString();
            actualColor = hex8.length >= 9 ? hex8.slice(0, 7) : hex8;
          }
        }
      }
    }
    const paintColor = toolModeRef.current === 'erase' ? '#ffffff' : actualColor;
    const mat = envelopeMaterialRef.current;
    mat.uniforms.uBrushColor.value.setStyle(paintColor);
    mat.uniforms.uProjMode.value   = isInverseMode ? 2 : isProjMode ? 1 : 0;
    mat.uniforms.uViewAspect.value = viewAspect;
    if (isProjMode || isInverseMode) {
      mat.uniforms.uCamViewProj.value =
        new THREE.Matrix4().multiplyMatrices(cam.projectionMatrix, cam.matrixWorldInverse);
      mat.uniforms.uCamPos.value = cam.position.clone();
    }
    if (isInverseMode) {
      const sphereOriginProj = new THREE.Vector3(0, 0, 0).project(cam);
      mat.uniforms.uSphereCenterNDC.value.set(sphereOriginProj.x, sphereOriginProj.y);
      mat.uniforms.uInverseWarpK.value = inverseWarpKRef.current;
      mat.uniforms.uInverseWarpStrength.value = inverseWarpStrengthRef.current;
    }

    // Ping-pong: start with A cleared (blank prev), render one batch per draw call.
    let prevRT = strokeRenderTargetA.current;
    let nextRT = strokeRenderTargetB.current;
    gl.setRenderTarget(prevRT);
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.setRenderTarget(null);

    const numSegments = N - 1;
    for (let batchStart = 0; batchStart < numSegments; batchStart += BATCH) {
      const batchCount = Math.min(BATCH, numSegments - batchStart);

      for (let k = 0; k < batchCount; k++) {
        const i = batchStart + k;
        const { point: ptA, pressure: prA } = resampled[i];
        const { point: ptB, pressure: prB } = resampled[i + 1];
        const rA = usePressureRef.current ? brushSizeRef.current * Math.max(0.05, prA) : brushSizeRef.current;
        const rB = usePressureRef.current ? brushSizeRef.current * Math.max(0.05, prB) : brushSizeRef.current;

        if (isProjMode || isInverseMode) {
          const pAN = ptA.clone().normalize();
          const pBN = ptB.clone().normalize();
          let ndcA: THREE.Vector3;
          let ndcB: THREE.Vector3;
          if (isInverseMode) {
            const scNDC = mat.uniforms.uSphereCenterNDC.value as THREE.Vector2;
            ({ ndcA, ndcB } = computeInverseSegmentNdc(pAN, pBN, cam, scNDC, inverseWarpKRef.current, inverseWarpStrengthRef.current));
          } else {
            ndcA = pAN.project(cam);
            ndcB = pBN.project(cam);
          }
          const thetaA = 2 * Math.asin(Math.min(1, rA / 2));
          const thetaB = 2 * Math.asin(Math.min(1, rB / 2));
          const rNdcA = Math.sin(thetaA) / camFrustumHalfH(cam, camD - Math.cos(thetaA));
          const rNdcB = Math.sin(thetaB) / camFrustumHalfH(cam, camD - Math.cos(thetaB));
          mat.uniforms.uSegA.value[k].set(ndcA.x, ndcA.y, rNdcA, 0);
          mat.uniforms.uSegB.value[k].set(ndcB.x, ndcB.y, rNdcB, 0);
        } else {
          const pA = ptA.clone().normalize();
          const pB = ptB.clone().normalize();
          mat.uniforms.uSegA.value[k].set(pA.x, pA.y, pA.z, rA);
          mat.uniforms.uSegB.value[k].set(pB.x, pB.y, pB.z, rB);
        }
      }
      mat.uniforms.uCount.value = batchCount;

      mat.uniforms.uPrev.value = prevRT.texture;
      gl.setRenderTarget(nextRT);
      gl.render(envelopeSceneRef.current, orthoCamera.current);
      gl.setRenderTarget(null);
      [prevRT, nextRT] = [nextRT, prevRT];
    }

    // Render duplication copies (diptych / triptych / quadriptych) on top of the
    // primary envelope already accumulated in prevRT.
    const baseCenter = resampled[0].point.clone().normalize();
    const dupCopies = getDuplicationCenters(baseCenter, dupModeRef.current);
    for (const { quaternion } of dupCopies.slice(1)) {
      if (isProjMode || isInverseMode) {
        // Rotate camera for this copy so back-face culling targets the right hemisphere.
        // NDC coords of the capsule endpoints are invariant (rotating camera + points
        // by the same Q leaves NDC unchanged), so uSegA/uSegB are reused as-is.
        const rotMatInv = new THREE.Matrix4().makeRotationFromQuaternion(quaternion.clone().invert());
        const viewMatRotated = cam.matrixWorldInverse.clone().multiply(rotMatInv);
        mat.uniforms.uCamViewProj.value = new THREE.Matrix4().multiplyMatrices(cam.projectionMatrix, viewMatRotated);
        mat.uniforms.uCamPos.value = cam.position.clone().applyQuaternion(quaternion);
      }

      for (let batchStart = 0; batchStart < numSegments; batchStart += BATCH) {
        const batchCount = Math.min(BATCH, numSegments - batchStart);

        for (let k = 0; k < batchCount; k++) {
          const i = batchStart + k;
          const { point: ptA, pressure: prA } = resampled[i];
          const { point: ptB, pressure: prB } = resampled[i + 1];
          const rA = usePressureRef.current ? brushSizeRef.current * Math.max(0.05, prA) : brushSizeRef.current;
          const rB = usePressureRef.current ? brushSizeRef.current * Math.max(0.05, prB) : brushSizeRef.current;

          if (isProjMode || isInverseMode) {
            // NDC is invariant across dup copies for both proj and inverse modes.
            const pAN = ptA.clone().normalize();
            const pBN = ptB.clone().normalize();
            let ndcA: THREE.Vector3;
            let ndcB: THREE.Vector3;
            if (isInverseMode) {
              const scNDC = mat.uniforms.uSphereCenterNDC.value as THREE.Vector2;
              ({ ndcA, ndcB } = computeInverseSegmentNdc(pAN, pBN, cam, scNDC, inverseWarpKRef.current, inverseWarpStrengthRef.current));
            } else {
              ndcA = pAN.project(cam);
              ndcB = pBN.project(cam);
            }
            const thetaA = 2 * Math.asin(Math.min(1, rA / 2));
            const thetaB = 2 * Math.asin(Math.min(1, rB / 2));
            const rNdcA = Math.sin(thetaA) / camFrustumHalfH(cam, camD - Math.cos(thetaA));
            const rNdcB = Math.sin(thetaB) / camFrustumHalfH(cam, camD - Math.cos(thetaB));
            mat.uniforms.uSegA.value[k].set(ndcA.x, ndcA.y, rNdcA, 0);
            mat.uniforms.uSegB.value[k].set(ndcB.x, ndcB.y, rNdcB, 0);
          } else {
            // Wrap mode: rotate each spine point by the dup quaternion.
            const pA = ptA.clone().normalize().applyQuaternion(quaternion);
            const pB = ptB.clone().normalize().applyQuaternion(quaternion);
            mat.uniforms.uSegA.value[k].set(pA.x, pA.y, pA.z, rA);
            mat.uniforms.uSegB.value[k].set(pB.x, pB.y, pB.z, rB);
          }
        }
        mat.uniforms.uCount.value = batchCount;

        mat.uniforms.uPrev.value = prevRT.texture;
        gl.setRenderTarget(nextRT);
        gl.render(envelopeSceneRef.current, orthoCamera.current);
        gl.setRenderTarget(null);
        [prevRT, nextRT] = [nextRT, prevRT];
      }
    }

    // prevRT now holds the complete clean envelope — make it the active stroke RT.
    activeStrokeRenderTarget.current = prevRT;

    if (strokeMaterialRef.current) {
      strokeMaterialRef.current.blendSrc = THREE.OneFactor;
      strokeMaterialRef.current.map     = prevRT.texture;
      strokeMaterialRef.current.opacity = toolModeRef.current === 'blur'
        ? 0
        : toolModeRef.current === 'erase'
        ? brushOpacityRef.current * 0.5 : brushOpacityRef.current;
      strokeMaterialRef.current.needsUpdate = true;
    }
  };

  const handlePointerUp = useCallback((evt: ThreeEvent<PointerEvent>) => {
    const { autoKey, currentFrame, layerKeyframes } = useAppStore.getState();
    const ct = evt.currentTarget as any;
    if (ct?.releasePointerCapture) {
      ct.releasePointerCapture(evt.pointerId);
    }

    // ── Fill mode: execute flood fill / gradient fill on pointer-up ────────
    if (toolModeRef.current === 'fill' && fillDragStartRef.current) {
      isDrawingRef.current = false;
      const startPoint = fillDragStartRef.current;
      fillDragStartRef.current = null;
      fillDragStartScreenRef.current = null;
      const useColor2 = fillDragAltRef.current;
      fillDragAltRef.current = false;
      // Hide the drag preview line.
      if (fillDragSvgRef.current) fillDragSvgRef.current.style.display = 'none';
      const endHit  = getHitData(evt);
      const endPoint = endHit?.point ?? startPoint;

      // Detect if the picker is in gradient mode (2+ color stops).
      const pv = pickerValueRef.current;
      let isGradientMode = false;
      let gradientStops: GradientStop[] = [];
      let r2 = 0, g2 = 0, b2 = 0; // last-stop color kept for right-click flat fill
      if (pv && typeof pv.getColors === 'function') {
        const pickerStops: Array<{ color: any; percent: number }> = pv.getColors();
        if (pickerStops.length >= 2) {
          isGradientMode = true;
          const hexOf = (s: any): string =>
            typeof s.color.toHexString === 'function' ? s.color.toHexString() : '#000000';
          gradientStops = pickerStops.map((s) => {
            const hex = hexOf(s);
            return {
              r: parseInt(hex.slice(1, 3), 16),
              g: parseInt(hex.slice(3, 5), 16),
              b: parseInt(hex.slice(5, 7), 16),
              pos: Math.max(0, Math.min(1, (s.percent ?? 0) / 100)),
            };
          });
          gradientStops.sort((a, b) => a.pos - b.pos);
          // Keep last stop for right-click flat fill (before reversal).
          const lastStop = gradientStops[gradientStops.length - 1];
          r2 = lastStop.r; g2 = lastStop.g; b2 = lastStop.b;
          // Right-click: reverse gradient direction by mirroring stop positions.
          if (useColor2) {
            gradientStops = gradientStops
              .map((s) => ({ ...s, pos: 1 - s.pos }))
              .sort((a, b) => a.pos - b.pos);
          }
        }
      }

      pushSnapshot();
      if (isGradientMode) {
        const startUV = worldPointToUV(startPoint);
        const endUV   = worldPointToUV(endPoint);
        // Run the gradient fill directly here (it shares the same GPU readback pipeline
        // as floodFillAtPoint, so duplicate the logic inline for clarity).
        const gl = glRef.current;
        const selectedId = selectedLayerIdRef.current;
        const canvas = textureCanvas.current;
        const ctx = canvas?.getContext('2d');
        if (gl && selectedId && copyScene.current && copyMaterial.current && orthoCamera.current && textureRef.current && canvas && ctx) {
          const w = canvas.width;
          const h = canvas.height;
          const lRT = layerRTs.current.get(selectedId);
          if (lRT) {
            const activeRT = lRT[lRT.active];
            const bufLen = w * h * 4;
            if (!readPixelBufferRef.current || readPixelBufferRef.current.length < bufLen) {
              readPixelBufferRef.current = new Uint8Array(bufLen);
            }
            const buf = readPixelBufferRef.current;
            gl.readRenderTargetPixels(activeRT, 0, 0, w, h, buf);
            flipBufferY(buf, w, h);
            const seedX = Math.floor(startUV.x * w);
            const seedY = Math.floor(startUV.y * h);
            floodFillGradient(
              buf, w, h,
              seedX, seedY,
              startUV.x, startUV.y,
              endUV.x,   endUV.y,
              gradientStops,
              fillToleranceRef.current,
              fillFloodModeRef.current,
            );
            ctx.putImageData(new ImageData(new Uint8ClampedArray(buf), w, h), 0, 0);
            textureRef.current.needsUpdate = true;
            copyMaterial.current.uniforms.uTex.value = textureRef.current;
            gl.setRenderTarget(lRT.a);
            gl.render(copyScene.current, orthoCamera.current);
            gl.setRenderTarget(lRT.b);
            gl.render(copyScene.current, orthoCamera.current);
            gl.setRenderTarget(null);
            lRT.active = 'a';
            flattenLayers();
            isDirtyRef.current = true;
            autosaveNotifyDirty();
            updateLayer(selectedId, { lastDupMode: dupModeRef.current });
            markLayersDirty(selectedId);
            scheduleBump(() => {
              updateCanvasFromRenderTarget();
              const activeId = selectedLayerIdRef.current;
              if (activeId) generateLayerThumbnail(activeId);
            });
          }
        }
      } else {
        // Flat fill — for gradient pickers, right-click uses color 2 as the fill color.
        if (useColor2 && isGradientMode) {
          const gl = glRef.current;
          const selectedId = selectedLayerIdRef.current;
          const canvas = textureCanvas.current;
          const ctx = canvas?.getContext('2d');
          if (gl && selectedId && copyScene.current && copyMaterial.current && orthoCamera.current && textureRef.current && canvas && ctx) {
            const w = canvas.width;
            const h = canvas.height;
            const lRT = layerRTs.current.get(selectedId);
            if (lRT) {
              const activeRT = lRT[lRT.active];
              const bufLen = w * h * 4;
              if (!readPixelBufferRef.current || readPixelBufferRef.current.length < bufLen) {
                readPixelBufferRef.current = new Uint8Array(bufLen);
              }
              const buf = readPixelBufferRef.current;
              gl.readRenderTargetPixels(activeRT, 0, 0, w, h, buf);
              flipBufferY(buf, w, h);
              const startUV = worldPointToUV(startPoint);
              const seedX = Math.floor(startUV.x * w);
              const seedY = Math.floor(startUV.y * h);
              floodFill(buf, w, h, seedX, seedY, r2, g2, b2, 255, fillToleranceRef.current, fillFloodModeRef.current);
              ctx.putImageData(new ImageData(new Uint8ClampedArray(buf), w, h), 0, 0);
              textureRef.current.needsUpdate = true;
              copyMaterial.current.uniforms.uTex.value = textureRef.current;
              gl.setRenderTarget(lRT.a); gl.render(copyScene.current, orthoCamera.current);
              gl.setRenderTarget(lRT.b); gl.render(copyScene.current, orthoCamera.current);
              gl.setRenderTarget(null);
              lRT.active = 'a';
              flattenLayers();
              isDirtyRef.current = true;
              autosaveNotifyDirty();
              updateLayer(selectedId, { lastDupMode: dupModeRef.current });
              markLayersDirty(selectedId);
              scheduleBump(() => {
                updateCanvasFromRenderTarget();
                const activeId = selectedLayerIdRef.current;
                if (activeId) generateLayerThumbnail(activeId);
              });
            }
          }
        } else {
          floodFillAtPointRef.current(startPoint);
        }
      }
      clearFillPreview();
      return;
    }

    // ── End translation drag ────────────────────────────────────────────
    if (isTranslatingRef.current) {
      evt.stopPropagation();
      setIsTranslating(false); isTranslatingRef.current = false;
      transDragStart.current = null;
      const base = transDragBase.current;
      transDragBase.current = null;
      const selId = selectedLayerIdRef.current;
      if (selId) {
        if (autoKey !== 0 && base) {
          const finalLayer = layersRef.current.find((l) => l.id === selId);
          if (finalLayer) {
            maybeAutoKeyBatch({
              layerId: selId,
              props: (['translateX', 'translateY'] as const).map((prop) => ({
                prop,
                value: finalLayer.translate[prop === 'translateX' ? 'x' : 'y'],
              })),
              autoKey,
              currentFrame,
              layerKeyframes,
              setKeyframe,
            });
          }
        }
        if (selId) markLayersDirty(selId);
        scheduleBump(() => {
          updateCanvasFromRenderTarget();
        });
      }
      return;
    }

    // ── End rotation drag ───────────────────────────────────────────────────
    if (isRotatingRef.current) {
      evt.stopPropagation();
      setIsRotating(false); isRotatingRef.current = false;
      rotDragStart.current = null;
      const base = rotDragBase.current;
      rotDragBase.current = null;
      const selId = selectedLayerIdRef.current;
      if (selId) {
        // Auto-key rotation channels on drag-end.
        if (autoKey !== 0 && base) {
          const finalLayer = layersRef.current.find((l) => l.id === selId);
          if (finalLayer) {
            maybeAutoKeyBatch({
              layerId: selId,
              props: (['rotateX', 'rotateY', 'rotateZ'] as const).map((prop) => ({
                prop,
                value: finalLayer.rotate[prop.replace('rotate', '').toLowerCase() as 'x'|'y'|'z'],
              })),
              autoKey,
              currentFrame,
              layerKeyframes,
              setKeyframe,
            });
          }
        }
        if (selId) markLayersDirty(selId);
        scheduleBump(() => {
          updateCanvasFromRenderTarget();
        });
      }
      return;
    }

    if (!isDrawingRef.current) return;
    evt.stopPropagation();
    setIsDrawing(false); isDrawingRef.current = false;
    setIsDrawingActive(false);

    const gl = glRef.current;
    const currentStrokeRT = activeStrokeRenderTarget.current;
    const lastStrokeRT = lastStrokeRenderTarget.current;

    // At stroke end, copy the latest stroke into the "last stroke" buffer.
    if (gl && currentStrokeRT && lastStrokeRT && copyScene.current && copyMaterial.current) {
      copyMaterial.current.uniforms.uTex.value = currentStrokeRT.texture;
      gl.setRenderTarget(lastStrokeRT);
      gl.render(copyScene.current, orthoCamera.current);
      gl.setRenderTarget(null);
    }

    if (usePressureRef.current && evt.pressure > 0) pressureRef.current = evt.pressure;
    // Replace the live dot-packed preview with a clean SDF envelope outline.
    commitEnvelopeFill(strokeSpineRef.current);
    // Commit the stroke into the active layer, then re-flatten for display.
    if (toolModeRef.current === 'blur') {
      applyBlurStroke(brushOpacityRef.current);
    } else {
      updateSpherePreview(brushOpacityRef.current);
    }

    // Clear BOTH stroke RTs: the pre-envelope one (captured above) and the
    // envelope RT that commitEnvelopeFill left as activeStrokeRenderTarget.
    const envelopeRT = activeStrokeRenderTarget.current;
    for (const rtToClear of [currentStrokeRT, envelopeRT !== currentStrokeRT ? envelopeRT : null]) {
      if (gl && rtToClear) {
        gl.setRenderTarget(rtToClear);
        gl.clearColor(0, 0, 0, 0);
        gl.clear(gl.COLOR_BUFFER_BIT);
        gl.setRenderTarget(null);
      }
    }
    if (strokeMaterialRef.current) {
      strokeMaterialRef.current.map = currentStrokeRT ? currentStrokeRT.texture : null;
      strokeMaterialRef.current.needsUpdate = true;
    }

    // Sync the UV texture view, then record history.
    isDirtyRef.current = true;
    autosaveNotifyDirty();
    const activeId = selectedLayerIdRef.current;
    if (activeId) updateLayer(activeId, { lastDupMode: dupModeRef.current });
    if (activeId) markLayersDirty(activeId);
    // Defer the expensive GPU→CPU readbacks to the next animation frame so the
    // pointerup handler returns quickly and doesn't block input processing.
    scheduleBump(() => {
      updateCanvasFromRenderTarget();
      if (activeId) generateLayerThumbnail(activeId);
    });
    pointBuffer.current = [];
    lastSplinePoint.current = null;
    strokeSpineRef.current = [];
  }, []);

  const initializeTexture = () => {
    if (textureCanvas.current) return;

    const canvas = document.createElement('canvas');
    canvas.width = texResRef.current.w;
    canvas.height = texResRef.current.h;
    // Leave the canvas fully transparent — the white background sphere
    // behind the main sphere provides the white base layer.

    textureCanvas.current = canvas;

    const tex = new THREE.CanvasTexture(canvas);
    tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
    // ensure linear filtering so the texture displays smoothly
    tex.minFilter = THREE.LinearFilter;
    tex.magFilter = THREE.LinearFilter;
    // Mark as sRGB so the GPU decodes it correctly when copyCanvasToRenderTargets
    // renders it back into the sRGB layer RTs.  Without this, sRGB-encoded pixel
    // data read back via readRenderTargetPixels gets sRGB-encoded AGAIN on restore,
    // making all undo states look lighter/brighter than the original.
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.needsUpdate = true;


    textureRef.current = tex;
    setTextureCanvas(canvas);

    if (materialRef.current) {
      materialRef.current.map = tex;
      materialRef.current.needsUpdate = true;
    }

    initializeBrushPipeline();
    scheduleBump();
  };


  return (
    <>
    <div
      style={{ flex: 1, position: 'relative', overflow: 'hidden', touchAction: 'none', cursor: opacityDragModeRef.current ? 'ew-resize' : isTranslating ? 'move' : isRotating ? 'grabbing' : shiftHeld ? 'grab' : (overSphere && (toolMode === 'draw' || toolMode === 'erase')) ? 'none' : 'crosshair' }}
      onMouseLeave={() => {
        if (brushCursorElemRef.current) brushCursorElemRef.current.style.display = 'none';
        if (blurInnerCursorElemRef.current) blurInnerCursorElemRef.current.style.display = 'none';
        if (dupModeCursorRef.current) dupModeCursorRef.current.style.display = 'none';
      }}
    >
      <Canvas
        frameloop="demand"
        camera={{ position: [HERO_POSITIONS[0].position.x, HERO_POSITIONS[0].position.y, HERO_POSITIONS[0].position.z], fov: 30 }}
        style={{
          background: heroBgAlpha > 0.001
            ? `color-mix(in srgb, ${heroBgColor} ${Math.round(heroBgAlpha * 100)}%, #222)`
            : '#222',
        }}
        onCreated={({ camera, gl }) => {
          cameraRef.current = camera;
          glRef.current = gl;
          initializeTexture();
          setTimeout(() => syncCamRef.current(), 0);
        }}
      >
        <InvalidateCapture target={invalidateRef} />
        <gridHelper args={[10, 10, 'white', 'gray']} rotation={[0, 0, 0]} position={[0, -0.5, 0]} visible={showGrid} />
        <ambientLight intensity={0.5} />
        <directionalLight position={[5, 5, 5]} intensity={1} />

        {/* 1. Background sphere — solid black/white or checkerboard for transparent. */}
        <mesh renderOrder={0} visible={viewportBgMode == 'white'}>
          <sphereGeometry args={[0.999, 64, 64]} />
          {/* <meshBasicMaterial color={viewportBgMode === 'white' ? 'white' : 'black'} /> */}
          <meshBasicMaterial color={'white'} />
        </mesh>
        <mesh renderOrder={0} visible={viewportBgMode == 'black'}>
          <sphereGeometry args={[0.999, 64, 64]} />
          {/* <meshBasicMaterial color={viewportBgMode === 'white' ? 'white' : 'black'} /> */}
          <meshBasicMaterial color={'black'} />
        </mesh>
        <mesh renderOrder={0} visible={viewportBgMode === 'transparent'}>
          <sphereGeometry args={[0.999, 64, 64]} />
          <meshBasicMaterial map={checkerTex} />
        </mesh>

        {/* 2. Main sphere — accumulates committed strokes. */}
        <mesh
          ref={sphereRef}
          renderOrder={1}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerEnter={() => setOverSphere(true)}
          onPointerLeave={() => {
            setOverSphere(false);
            lastFillHoverPointRef.current = null;
            clearFillPreview();
            if (toolModeRef.current === 'stamp' || toolModeRef.current === 'text') clearStampPreview();
            if (brushCursorElemRef.current) brushCursorElemRef.current.style.display = 'none';
            if (blurInnerCursorElemRef.current) blurInnerCursorElemRef.current.style.display = 'none';
            if (dupModeCursorRef.current) dupModeCursorRef.current.style.display = 'none';
          }}
        >
          <sphereGeometry args={[1, 64, 64]} />
          <meshBasicMaterial ref={materialRef} transparent />
        </mesh>

        {/* 3. Stroke preview sphere — slightly larger, shows only the
            current in-progress stroke before it is committed.  Cleared
            to fully transparent as soon as the pointer is released. */}
        <mesh ref={strokeSphereRef} renderOrder={2}>
          <sphereGeometry args={[1.001, 64, 64]} />
          {/* Stroke RT stores premultiplied alpha — use ONE/ONE_MINUS_SRC_ALPHA
               so bilinear edge sampling doesn’t darken toward black. */}
          <meshBasicMaterial
            ref={strokeMaterialRef}
            transparent
            opacity={0}
            depthWrite={false}
            blending={THREE.CustomBlending}
            blendEquation={THREE.AddEquation}
            blendSrc={THREE.OneFactor}
            blendDst={THREE.OneMinusSrcAlphaFactor}
            blendEquationAlpha={THREE.AddEquation}
            blendSrcAlpha={THREE.OneFactor}
            blendDstAlpha={THREE.OneMinusSrcAlphaFactor}
          />
        </mesh>

        {/* 3b. Fill hover preview sphere — shows the region that would be
            filled at the current cursor position.  Only active in fill mode. */}
        <mesh renderOrder={3}>
          <sphereGeometry args={[1.002, 64, 64]} />
          <meshBasicMaterial
            ref={fillPreviewMaterialRef}
            transparent
            opacity={0}
            depthWrite={false}
            blending={THREE.NormalBlending}
          />
        </mesh>

        {/* 4. Architecture overlay — flat black cap on the bottom ~1/3 of the sphere.
            renderOrder=4, depthTest=false, DoubleSide: always draws on top of artwork.
            NOT part of the texture pipeline — invisible in renders. */}
        {overlayArchitecture && <ArchitectureCap />}

        {/* 5. Safety reticles — circles on the sphere surface, one per dup-mode sector.
            NOT part of the texture pipeline — invisible in renders. */}
        {overlaySafety && <SafetyReticles dupMode={dupMode} />}

        <OrthoCamera enabled={isOrtho} cameraRef={cameraRef} />
        <TabletOrbitSync controlsRef={controlsRef} />
        <OrbitControls
          ref={controlsRef}
          enabled={!isDrawing && !isRotating && !isTranslating && !shiftHeld}
          enableZoom
          enablePan
          enableRotate={false}
          enableDamping={false}
          dampingFactor={0}
          onChange={() => syncCamRef.current()}
        />
      </Canvas>



      {/* Hero name label — top center, visible only when snapped to a hero */}
      {selectedHeroId && (() => {
        const hero = HERO_POSITIONS.find((h) => h.id === selectedHeroId);
        return hero ? (
          <div
            style={{
              position: 'absolute',
              top: 10,
              left: '50%',
              transform: 'translateX(-50%)',
              color: 'rgba(255,255,255,0.75)',
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              pointerEvents: 'none',
              userSelect: 'none',
              textShadow: '0 1px 4px rgba(0,0,0,0.8)',
            }}
          >
            {hero.name}
          </div>
        ) : null;
      })()}

      {/* Axis orientation gizmo — bottom left */}
      {showManipulator && !uvViewportMain && (
        <AxisGizmo
          camPos={camPos}
          size={96}
          onSnap={animateCameraTo}
          onOrbitDelta={handleGizmoOrbitDelta}
        />
      )}

      {/* Dup-mode dot row — shown above cursor in draw/erase/stamp/text modes. */}
      {(() => {
        const selLayer = layers.find((l) => l.id === selectedLayerId);
        const layerDupMode = selLayer?.lastDupMode;
        const dotColor = !layerDupMode
          ? 'rgba(255,255,255,0.82)'
          : layerDupMode === dupMode
            ? 'rgba(80,220,80,0.9)'
            : 'rgba(255,80,80,0.9)';
        const dotCount = dupMode === 'hero' ? 1 : dupMode === 'diptych' ? 2 : dupMode === 'triptych' ? 3 : 4;
        return (
          <div
            ref={dupModeCursorRef}
            style={{
              display: 'none',
              position: 'absolute',
              flexDirection: 'row',
              gap: 4,
              alignItems: 'center',
              transform: 'translateX(-50%)',
              pointerEvents: 'none',
              userSelect: 'none',
              zIndex: 9999,
            }}
          >
            {Array.from({ length: dotCount }).map((_, i) => (
              <div key={i} style={{ width: 5, height: 5, borderRadius: '50%', background: dotColor, boxShadow: '0 0 0 1px rgba(0,0,0,0.55)' }} />
            ))}
          </div>
        );
      })()}

      {/* Fill-mode drag direction preview line — imperatively updated during drag. */}
      <svg
        ref={fillDragSvgRef}
        style={{
          display: 'none',
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
          userSelect: 'none',
          overflow: 'visible',
          zIndex: 9999,
        }}
      >
        <line stroke="rgba(255,255,255,0.75)" strokeWidth="1.5" strokeDasharray="6 3"
              style={{ filter: 'drop-shadow(0 0 1px rgba(0,0,0,0.8))' }}
              x1="0" y1="0" x2="0" y2="0" />
        <circle r="4" fill="rgba(255,255,255,0.9)" stroke="rgba(0,0,0,0.6)" strokeWidth="1"
                cx="0" cy="0" />
      </svg>

      {/* Brush cursor circle — draw/erase/blur mode. Position/size are updated via DOM ref to
          avoid triggering React re-renders on every pointermove event. */}
      <div
        ref={brushCursorElemRef}
        style={{
          display: 'none',
          position: 'absolute',
          borderRadius: '50%',
          border: toolMode === 'erase' ? '1.5px solid rgba(255,80,80,0.85)'
                : toolMode === 'blur'  ? `1.5px solid rgba(34,211,238,0.85)`
                : '1.5px solid rgba(255,255,255,0.85)',
          boxShadow: '0 0 0 1px rgba(0,0,0,0.5)',
          transform: 'translate(-50%, -50%)',
          pointerEvents: 'none',
          userSelect: 'none',
          zIndex: 9998,
        }}
      />

      {/* Blur kernel radius inner ring — same color as outer, half alpha. */}
      <div
        ref={blurInnerCursorElemRef}
        style={{
          display: 'none',
          position: 'absolute',
          borderRadius: '50%',
          border: '1.5px solid rgba(34,211,238,0.425)',
          transform: 'translate(-50%, -50%)',
          pointerEvents: 'none',
          userSelect: 'none',
          zIndex: 9997,
          boxSizing: 'border-box',
        }}
      />

      {/* Opacity scrub reticle — rendered via portal at fixed screen position */}
      {opacityReticle && (
        <div style={{ position: 'fixed', left: opacityReticle.x, top: opacityReticle.y, pointerEvents: 'none', userSelect: 'none', zIndex: 9999 }}>
          {opacityReticle.label === 'Layer' ? (
            /* 2:1 rounded rectangle — visually distinct from the brush circle */
            <div style={{
              position: 'absolute',
              width: 64, height: 32,
              borderRadius: 5,
              border: '1.5px solid rgba(255,255,255,0.85)',
              boxShadow: '0 0 0 1px rgba(0,0,0,0.5)',
              transform: 'translate(-50%, -50%)',
              overflow: 'hidden',
            }}>
              <div style={{ position: 'absolute', inset: 0, background: 'white', opacity: opacityReticle.value }} />
            </div>
          ) : (
            /* Circle/square — scales to footprint in size-scrub mode, filled in brush-alpha mode */
            <div style={{
              position: 'absolute',
              width: opacityReticle.ringPx != null ? opacityReticle.ringPx * 2 : 36,
              height: opacityReticle.ringPx != null ? opacityReticle.ringPx * 2 : 36,
              borderRadius: opacityReticle.isSquare ? 4 : '50%',
              border: '1.5px solid rgba(255,255,255,0.85)',
              boxShadow: '0 0 0 1px rgba(0,0,0,0.5)',
              transform: `translate(-50%, -50%) rotate(${opacityReticle.label === 'Rotation' ? (opacityReticle.value * 360 - 180) : 0}deg)`,
              overflow: 'hidden',
            }}>
              {(opacityReticle.label === 'Opacity' || opacityReticle.label === 'Strength') && (
                <div style={{
                  position: 'absolute', inset: 0,
                  background: Array.isArray(brushColor) ? (brushColor as any)[0].color : brushColor as string,
                  opacity: opacityReticle.value,
                }} />
              )}
            </div>
          )}
          {/* Info panel above cursor */}
          <div style={{
            position: 'absolute',
            transform: 'translate(-50%, calc(-100% - 26px))',
            background: 'rgba(0,0,0,0.82)',
            border: '1px solid rgba(255,255,255,0.15)',
            borderRadius: 6,
            padding: '5px 12px 7px',
            textAlign: 'center',
            minWidth: 88,
            whiteSpace: 'nowrap',
          }}>
            <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.55)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 1 }}>
              {opacityReticle.label}
            </div>
            <div style={{ fontSize: 22, fontWeight: 700, color: 'white', lineHeight: 1.1 }}>
              {opacityReticle.label === 'Rotation'
                ? `${Math.round((opacityReticle.value * 360) - 180)}°`
                : `${Math.round(opacityReticle.value * 100)}%`}
            </div>
            <div style={{ marginTop: 5, height: 2, background: 'rgba(255,255,255,0.18)', borderRadius: 1 }}>
              <div style={{ width: `${opacityReticle.value * 100}%`, height: '100%', background: 'white', borderRadius: 1 }} />
            </div>
          </div>
        </div>
      )}
      {/* Camera XYZ / key / ortho panel — hidden when 3D is the small panel */}
      {!uvViewportMain && (
      <div
        style={{
          position: 'absolute',
          right: 12,
          top: 12,
          background: 'rgba(0,0,0,0.6)',
          borderRadius: 8,
          padding: '6px 8px',
          color: 'white',
          fontSize: 11,
          userSelect: 'none',
          minWidth: 90,
        }}
      >
        {/* Row 1: Camera label */}
        <div style={{ marginBottom: 5, fontWeight: 600, letterSpacing: '0.04em', opacity: 0.7, textTransform: 'uppercase', fontSize: 10 }}>
          Camera
        </div>

        {/* Row 2: Key + Ortho buttons */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 5 }}>
          <Tooltip title={cameraTrackEnabled ? 'Remove camera track' : 'Add camera track to timeline'}>
            <button
              onClick={() => {
                pushSnapshot();
                setCameraTrackEnabled(!cameraTrackEnabled);
              }}
              style={{
                background: cameraTrackEnabled ? 'rgba(96,165,250,0.3)' : 'rgba(255,255,255,0.08)',
                border: `1px solid ${cameraTrackEnabled ? 'rgba(96,165,250,0.6)' : 'rgba(255,255,255,0.2)'}`,
                borderRadius: 3,
                cursor: 'pointer',
                color: cameraTrackEnabled ? '#60a5fa' : 'rgba(255,255,255,0.5)',
                padding: '1px 4px',
                fontSize: 15,
                lineHeight: 1,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              ⚿
            </button>
          </Tooltip>
          <Tooltip title={isOrtho ? 'Switch to perspective' : 'Switch to orthographic (isometric)'}>
            <button
              onClick={() => setIsOrtho((v) => !v)}
              style={{
                background: isOrtho ? 'rgba(167,139,250,0.3)' : 'rgba(255,255,255,0.08)',
                border: `1px solid ${isOrtho ? 'rgba(167,139,250,0.7)' : 'rgba(255,255,255,0.2)'}`,
                borderRadius: 3,
                cursor: 'pointer',
                color: isOrtho ? '#a78bfa' : 'rgba(255,255,255,0.5)',
                padding: '1px 5px',
                fontSize: 10,
                fontWeight: 600,
                lineHeight: 1.6,
                letterSpacing: '0.04em',
              }}
            >
              ORTHO
            </button>
          </Tooltip>
        </div>

        {/* Rows 3–5: X, Y, Z */}
        {(['x', 'y', 'z'] as const).map((ax) => (
          <div key={ax} style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 3 }}>
            <span style={{ width: 15, fontSize: 9, opacity: 0.5, textTransform: 'uppercase' }}>{ax}</span>
            <input
              type="number"
              ref={camScrubs[ax].ref}
              onPointerDown={camScrubs[ax].onPointerDown}
              onPointerMove={camScrubs[ax].onPointerMove}
              onPointerUp={camScrubs[ax].onPointerUp}
              value={camPos[ax]}
              step={0.1}
              className="no-spinner"
              onChange={(e) => {
                const n = parseFloat(e.target.value);
                if (!isNaN(n)) applyCamPos(ax, n);
              }}
              onKeyDown={(e) => e.stopPropagation()}
              style={{
                width: 60,
                background: 'rgba(255,255,255,0.1)',
                border: '1px solid rgba(255,255,255,0.18)',
                borderRadius: 4,
                color: 'white',
                fontSize: 12,
                padding: '1px 3px',
                outline: 'none',
                textAlign: 'right',
              }}
            />
          </div>
        ))}

        {/* Row 6: FOV (perspective only) */}
        {!isOrtho && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 3 }}>
            <Tooltip title={`Field of view: ${cameraFov}°`} placement="bottom">
              <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{ width: 15, fontSize: 9, opacity: 0.5, textTransform: 'uppercase' }}>FOV</span>
                <input
                  type="number"
                  ref={camFovScrub.ref}
                  onPointerDown={camFovScrub.onPointerDown}
                  onPointerMove={camFovScrub.onPointerMove}
                  onPointerUp={camFovScrub.onPointerUp}
                  min={5}
                  max={150}
                  step={1}
                  value={cameraFov}
                  className="no-spinner"
                  onChange={(e) => {
                    const n = Math.max(5, Math.min(150, parseInt(e.target.value, 10)));
                    if (!isNaN(n)) setCameraFov(n);
                  }}
                  onKeyDown={(e) => e.stopPropagation()}
                  style={{
                    width: 60,
                    background: 'rgba(255,255,255,0.1)',
                    border: '1px solid rgba(255,255,255,0.18)',
                    borderRadius: 4,
                    color: 'white',
                    fontSize: 12,
                    padding: '1px 3px',
                    outline: 'none',
                    textAlign: 'right',
                  }}
                />
              </label>
            </Tooltip>
          </div>
        )}

        {/* ── Camera mode row (tablet only) ───────────────────────────── */}
        <TabletCameraButtons />

      </div>
      )} {/* end !uvViewportMain camera panel */}

      {/* ── Tablet sphere interaction row (floating, separate) ───────────── */}
      <TabletSphereButtons />


    {showFps && (
      <div style={{
        position: 'absolute',
        top: 10,
        left: 10,
        fontFamily: 'monospace',
        fontSize: 13,
        fontWeight: 'bold',
        color: fpsReadout < 30 ? '#ff4444' : '#aaff88',
        background: 'rgba(0,0,0,0.55)',
        padding: '2px 8px',
        borderRadius: 4,
        pointerEvents: 'none',
        userSelect: 'none',
        zIndex: 10,
      }}>
        {fpsReadout} fps
      </div>
    )}
    </div>

    <ProjectModal
      open={projectModalOpen}
      onClose={() => setProjectModalOpen(false)}
      currentProjectName={currentProjectName}
      onSave={async (name) => { await saveProject(name); setCurrentProjectName(name); autosaveResetToClean(); }}
      onLoad={async (file, handle) => { await viewportActions.loadFileWithAutosaveCheck.current(file, handle); }}
      onNew={() => { isResettingRef.current = true; newProject(); autosaveResetToClean(); setTimeout(() => { isResettingRef.current = false; }, 0); }}
    />
    <RenderModal
      open={renderModalOpen}
      onClose={() => setRenderModalOpen(false)}
      defaultSettings={{
        ...renderSettings,
        outputName: renderSettings.outputName === DEFAULT_RENDER_SETTINGS.outputName
          ? currentProjectName
          : renderSettings.outputName,
        endFrame: Math.min(renderSettings.endFrame, totalFrames - 1),
      }}
      fps={fps}
      totalFrames={totalFrames}
      onRender={(settings, onProgress, signal) => renderSequenceRef.current(settings, onProgress, signal)}
    />
    </>
  );
};

export default Viewport3D;
